/* eslint-disable */
/**
 * Motor do mundo isométrico de Britain.
 *
 * Porte fiel do mock `britain-on-horseback.html`. Continua sendo um módulo só,
 * de propósito: o motor é um subsistema com estado mutável compartilhado
 * (contexto, câmera, zoom, mapa) e quebrá-lo em arquivos que empurram o mesmo
 * objeto de contexto de um lado para o outro deixaria o código pior, não melhor.
 * O acoplamento que importa — com o React — está fechado em `WorldOptions` e
 * `WorldHandle`.
 *
 * Nada aqui lê o DOM por id: o que entra são canvases e um mapa já carregado.
 * A pé: folha da skin. Montado: animal/horse embaixo + corpo no lombo.
 */

import type {
  AlvoVitais,
  Gait,
  JournalLine,
  JournalTone,
  NetEntered,
  NetMoved,
  NetFrame,
  NetStrike,
  TargetKind,
  WorldHandle,
  WorldOptions,
  CargaMundo,
  WorldStatus
} from './types';
import { t } from '../i18n/idioma';
import type { RetratoDoMembro } from '../realtime/cityHub';
import {
  CORES_PENTA, EFEITO_PORTAL_PENTA, MAPA_SANTUARIO, TODAS_PENTA, centroDoMapa, chaveDoLugar, corDoPortalPenta,
  desenharParticulasPortal, desenharPonta, desenharRitual, desenharSeloPortal, lugarPerto, pontasDoMapa, spriteDoPortalPenta
} from './pentagrama';
import { FADIGA_TETO, fadigaAgora } from './fadiga';
import { MAGIAS } from '../data/magias';
import { aquecerEm, aquecerSpawn, blocosVisiveis, invalidarMapa, Z_PX } from './blocos';
import { assentoEm, cliqueNaCasa, cliqueNaPlaca, placaNaTela, desenharDestaque, colocacaoAtiva, ferramentaBloqueiaEm, confirmarColocacao, desenharColocacao, desenharPecaDeCasa, fracDePrio, passoCasa, pecasDasCasas, peMaisPerto, pararColocacao, peValido, zDaCasa, type PecaPosta, casasNoMinimapa } from './casas';
import { brilharAgua, dentroDaPlanta, desenharChao, desenharEstatico, desenharForro, desenharProjetil, ehAnimado, estaticoNaFrente, forroDeAltos, assarMinimapa } from './desenho';
import { aplicarLuz } from './luz';
import { MAPAS_ESCUROS, NOITE, corAmbiente, minutoAgora, nivelDeLuz } from './relogio';
import { visaoNoturna } from './buffs';
import { avisarSobre, carregarDano, desenharDano, marcarCabeca, spawnDano, spawnTexto, tipoDoStrike } from './dano';
import { ancorarBalao, calarBalao, calarTodos, desenharBaloes, falarBalao, novoQuadroBaloes } from './balao';
import { desenharCoroa, ouroDaCoroa } from '../look/coroaVip';
import { desenharSelo } from '../look/seloFundador';
import { carregarMapa, esquecerMapas } from './mapa';
import {
  arteDa, carregarFolha, cicloIdle, desenharFolha, desenharQuadro, ehMontaria, folhaParada, folhasDaArma, folhasDaBarba, folhasDaCloth, folhasDaSkin, folhasDoCabelo,
  folhasDoEscudo, folhasDoInimigo, IDLE_HOLD_QUADRO, jsonAnimal, jsonArma, jsonBarba, jsonCabelo, jsonCloth, jsonInimigo, jsonPersonagem,
  msDaFolha, camadasDaCloth, capuzNoFio, fioDoVisual, capaAtras, ehCamadaCabeca, ehCapa, estiloDaFolha, materialDaPeca, pastaDoEscudo, prefetchFolhas, trajeDaCamada, PELE_CADAVER, SKIN_PADRAO, WALK_FRAMES_PER_TILE, type Folha, type Quadro
} from './spriteAtlas';
import { MONTARIA_PADRAO } from './montarias';
import { faixasSentado, poseSentada, type PoseSentada } from './sentar';
import { aquecerCabelo, estiloValido, tingirFolhaCabelo } from '../look/cabelo';
import { peleNoIntervalo, peleValida, tingirFolha, tingirFolhaMaterial } from '../look/pele';
import { aquecerTraje, quandoTrajeCarregar, tingirFolhaTraje } from '../look/traje';
import { materialDoItem } from '../look/materiais';
import { NICK, nickFill, nomeComMarca } from '../look/nickColor';
import { NPC_ID_BASE, NPC_ALCANCE_PADRAO, carregarNpcs, type NpcNoMapa } from './npcs';
import { carregarManequins, type ManequimNoMapa } from './manequins';
import { PORTAL_ALCANCE, carregarPortais, type PortalNoMapa } from './portais';
import { carregarViagens, type ViagemDestino } from './viagens';
import { iniciarAmbiente, pararAmbiente, playAnkh, playColeta, playDie, playFizzle, playHurt, prefetchSkills, prefetchUo, playSkill, tocarUm, tocarUo } from '../audio/mixer';
import {
  ATAQUE, DOR, DOR_F, DOR_M, ITEM, MANEQUIM, MOB_SONS, MORTE, MORTE_F, MORTE_M, OCIOSO, PASSO, PASSO_GALOPE, PASSO_GALOPE_MS,
  PASSO_MONTADO_MS, PASSO_MS, RAIVA, somDaArma, sonsDaMontaria
} from '../audio/sonsUo';
import { aoComecarMovimento, ligarAcao, pressionada, registrarMira } from '../input/atalhos';
import { lerBotaoAndar } from '../input/botaoAndar';
import { DURACAO_MARCA, PARADA_MARCA, desenharMarcaClique, lerMarcaClique, type MarcaClique } from './marcaClique';
import { versaoDoEtag } from '../data/combateTabela';
import { animDaArma, armaComSkin } from '../data/armas';
import { defEfeito, forEachProjetil, imgEfeito, peitoZ, prefetchEfeito, spawnProjetil } from './efeitos';
import { STRIKE_CAP } from './dps';
import { marcarQuadro } from '../realtime/desempenho';
import { cursorCombate, cursorForaDeCombate, cursorMira } from '../input/cursor';

type Ctx2D = CanvasRenderingContext2D;
/** `ms`: duração do passo de outro jogador, vinda do servidor (Moved.PassoMs). */
type Step = { x: number; y: number; ms?: number };
/** Uma conjuração no ar (techspec-magias-animacoes). */
type Conjuracao = { desde: number; area: boolean };
/** O gesto da UO se repete a cada 1,5 s enquanto a magia está no ar (o AnimTimer do RunUO). */
const CICLO_GESTO_MS = 1500;

/** Quantas linhas o diário guarda. */
const JOURNAL_MAX = 200;
const COMBAT_MAX = 80;
/** Teto de publicações de status por segundo. 60 fps não precisa virar 60 renders. */
const STATUS_INTERVAL_MS = 120;

function must2d(canvas: HTMLCanvasElement): Ctx2D {
  const c = canvas.getContext('2d');
  if (!c) throw new Error('Este navegador não expôs um contexto 2D de canvas.');
  return c;
}

/**
 * Monta o mundo em um canvas e devolve o controle dele.
 * Chamar `destroy()` é obrigatório: sem isso o laço e os listeners sobrevivem
 * à desmontagem do componente (e o StrictMode do React monta duas vezes).
 */
/** Ultimate Battle e a arena de duelo: sem menu de botão direito no jogador. */
export function mapaDeEvento(id: string): boolean {
  return id === 'ultimate-battle' || id === 'arena-duelo-ice-age';
}

let anotarDiarioImpl: (text: string) => void = () => {};
let flutuarPrataImpl: (preco: number) => void = () => {};

export function anotarDiario(text: string){
  anotarDiarioImpl(text);
}

export function flutuarPrata(preco: number){
  flutuarPrataImpl(preco);
}

let flutuarSobreMimImpl: (texto: string) => void = () => {};

/** Texto que sobe sobre o próprio personagem (a coleta: "+2 Oak Log"). */
/** O aviso vermelho parado sobre a sua cabeça (o "You are attacking X!" do UO). */
let avisoSobreMimImpl: (texto: string) => void = () => {};
export function avisoSobreMim(texto: string){
  avisoSobreMimImpl(texto);
}

export function flutuarSobreMim(texto: string){
  flutuarSobreMimImpl(texto);
}

export function createWorld(opts: WorldOptions): WorldHandle {
prefetchSkills();

const playerName = opts.playerName?.trim() || t('mundo.aventureiro');
let mapa = opts.mapa;

function emEvento(): boolean {
  return mapaDeEvento(mapa.id);
}
let bounds = mapa.cerca;
const boundsMessage = opts.boundsMessage ?? t('mundo.foraDaCerca');

const insideBounds = (x: number, y: number) =>
  x >= bounds.x0 && y >= bounds.y0 && x <= bounds.x1 && y <= bounds.y1;

/** Cada listener registrado devolve o seu próprio desfazer. */
const teardown: Array<() => void> = [];
function on<K extends string>(
  target: Window | Document | HTMLElement,
  type: K,
  handler: (ev: never) => void
){
  const h = handler as EventListener;
  target.addEventListener(type, h);
  teardown.push(() => target.removeEventListener(type, h));
}

/**
 * Publica o status para o React. Estrangulado no tempo e comparado por valor:
 * as barras regeneram continuamente, mas em inteiros só mudam poucas vezes
 * por segundo, então a maioria dos quadros não gera render nenhum.
 */
let lastPublish = 0;
let lastKey = '';

/**
 * Vigor dos outros jogadores (techspec-hud-v3 §19): só a barra do alvo usa, então fica fora do `remotes` (e da
 * assinatura do upsertRemote). O servidor manda no passo e quando muda de faixa de 5%.
 */
const stamRemota = new Map<number, { stam: number; stamMax: number }>();
function anotarStam(m: NetMoved){
  if (m.stamMax && m.stamMax > 0) stamRemota.set(m.id, { stam: m.stam ?? 0, stamMax: m.stamMax });
}
/** O alvo da caçada automática que a sala mandou no último frame (zero = nenhum). */
let huntTargetAtual = 0;

/**
 * Quem o jogador está atacando agora (techspec-hud-v3 §19): o War dele, ou o alvo da caçada automática. No mock
 * (sem sala) vale o alvo selecionado vivo. Fora de combate, null — a barra do alvo some.
 */
/** O adversário do duelo (duelo v2): enquanto ele dura, a placa do alvo fica presa nele. Null fora do duelo. */
let adversarioDuelo: string | null = null;

function idDoAdversario(): number {
  if (!adversarioDuelo) return 0;
  const alvo = adversarioDuelo.toLowerCase();
  for (const r of remotes.values()) if (r.kind === 'player' && r.name.toLowerCase() === alvo) return r.id;
  return 0;
}

function alvoEmCombate(): AlvoVitais | null {
  const id = idDoAdversario() || (net
    ? (warTargetId || (huntTargetAtual !== youId ? huntTargetAtual : 0))
    : (targetId ?? 0));
  if (!id) return null;
  const r = remotes.get(id);
  if (!r || r.dead || r.hits <= 0 || r.kind === 'corpse' || r.kind === 'npc') return null;
  const s = stamRemota.get(id);
  return {
    id, nome: r.name, kind: r.kind, notoriety: r.notoriety,
    hits: r.hits, hitsMax: r.hitsMax, mana: r.mana, manaMax: r.manaMax,
    stam: s?.stam ?? 0, stamMax: s?.stamMax ?? 0
  };
}
/** O nome do jogador marcado como alvo (o destaque amarelo da placa do grupo); null em mob ou sem alvo. */
function nomeDoAlvoSelecionado(): string | null {
  if (targetId == null) return null;
  const r = remotes.get(targetId);
  return r && r.kind === 'player' ? r.name : null;
}

function publish(now: number, place: string, gait: Gait, force = false){
  if (!opts.onStatus) return;
  if (!force && now - lastPublish < STATUS_INTERVAL_MS) return;
  lastPublish = now;
  const status: WorldStatus = {
    hp: Math.round(player.hp),     hpMax: player.hpMax,
    mana: Math.round(player.mana), manaMax: player.manaMax,
    stam: Math.round(player.stam), stamMax: player.stamMax,
    fadiga: Math.round(fadigaAgora(fadigaSnap, fadigaEm, fadigaFaixa, Date.now())),
    fadigaMax: FADIGA_TETO,
    fadigaFaixa,
    gait,
    place,
    coord: sextant(player.x, player.y),
    canal: salaCanal,
    protegida: salaProtegida,
    tileX: Math.round(player.x),
    tileY: Math.round(player.y),
    envenenado: player.envenenado,
    criminalLeft,
    paralizado,
    mundoVersao,
    mundoNome,
    alvo: alvoEmCombate(),
    alvoSelecionado: nomeDoAlvoSelecionado()
  };
  const a = status.alvo;
  const chaveAlvo = a ? `${a.id}:${a.hits}/${a.hitsMax}:${a.mana}/${a.manaMax}:${a.stam}/${a.stamMax}:${a.nome}` : '-';
  const key = `${status.hp}|${status.mana}|${status.stam}|${status.fadiga}|${gait}|${place}|${status.coord}|${status.canal}|${status.protegida ? 1 : 0}|${status.tileX}|${status.tileY}|${status.envenenado}|${criminalLeft}|${paralizado ? 1 : 0}|${mundoVersao}|${mundoNome}|${chaveAlvo}|${status.alvoSelecionado ?? ''}`;
  if (key === lastKey) return;
  lastKey = key;
  opts.onStatus(status);
}

/** A cerca avisa no máximo uma vez a cada 5 s — senão o diário vira ruído. */
let lastNudge = 0;
function nudgeFence(){
  const now = performance.now();
  if (now - lastNudge < 5000) return;
  lastNudge = now;
  log(boundsMessage, 'warn');
}


/* ══════════════════════════════════════════════════════════════
   Britain on Horseback — mock isométrico de um idle de Ultima Online
   Terreno e estáticos vêm do mapa. O personagem vem das folhas em
   /sprites/character/{skin} (a pé ou montado). Sem folha pronta, o boneco não é desenhado (techspec-loading).
   ══════════════════════════════════════════════════════════════ */

let TW = mapa.tileW, TH = mapa.tileH;

const REDUCED = typeof matchMedia === 'function'
  && matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- utilidades ---------- */
const clamp = (v: number, a: number, b: number) => v < a ? a : v > b ? b : v;
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/* projeção isométrica: tile -> pixel de mundo */
const wx = (tx: number, ty: number) => (tx - ty) * TW / 2;
const wy = (tx: number, ty: number) => (tx + ty) * TH / 2;
/** Pixels de um passo. A/D (eixo longo) é ~TW; W/S (eixo curto) é ~TH. */
const passoTela = (dx: number, dy: number) =>
  Math.hypot((dx - dy) * TW / 2, (dx + dy) * TH / 2);
const PASSO_REF = passoTela(1, 0);
/** 1 = A/D no mesmo px/s que S+D; 0 = mesmo tempo por tile (A/D mais rápido). */
const PASSO_MIX = 0.7;
function passoVisual(dx: number, dy: number){
  const full = Math.max(0.35, passoTela(dx, dy) / PASSO_REF);
  return 1 + (full - 1) * PASSO_MIX;
}
/** Piso: a diagonal (eixo curto) não pode mandar mais tiles/s do que A/D. */
function passoDeGrade(dx: number, dy: number){
  return Math.max(passoVisual(dx, dy), passoVisual(1, -1));
}

const inMap = (x: number, y: number) => mapa.dentro(x, y);
/** A grade do mapa e a ferramenta de ofício da cidade (bigorna, roca, alvo), que é móvel e bloqueia o passo. */
const walkable = (x: number, y: number) => mapa.andavel(x, y) && !ferramentaBloqueiaEm(mapa.id, x, y);
/** O pé padrão em (x, y): o mais baixo da casa erguida (techspec-casas §03) ou o terreno. Quem sabe o andar do corpo
 *  (o jogador, `player.tz`; os outros, `zRemoto`) usa o dele. A ordem de desenho é `x + y + z do terreno` + `fracDePrio`. */
const zPe = (x: number, y: number) => zDaCasa(mapa.id, x, y) ?? mapa.zEm(x, y);
/**
 * A régua de desenho de um ponto: diagonal + z do terreno + a fração da prio. No mapa com relevo (techspec-relevo) o z
 * vai de −46 a +64 e não pode virar diagonal: a ordem é a do cliente do UO, a diagonal e, dentro dela, a prio de z.
 * Nos outros mapas a régua é exatamente a de sempre.
 */
/**
 * Onde um corpo entra na régua de desenho: parado, onde está; andando, no mais à frente (diagonal maior) dos dois tiles
 * do passo. O pé passa pelos dois losangos: o piso, o tapete e a escada de ambos vão para baixo dele.
 */
function tileDeOrdem(fx: number, fy: number, tx: number, ty: number, x: number, y: number): [number, number] {
  if (fx === tx && fy === ty) return [x, y];
  return tx + ty >= fx + fy ? [tx, ty] : [fx, fy];
}

function regua(x: number, y: number, zTerreno: number, prio: number){
  return mapa.relevo ? x + y + fracDePrio(prio + 64) : x + y + zTerreno + fracDePrio(prio);
}
/** Altura do pé dos outros (o servidor manda no Moved/Entered); 0 = o padrão da casa. */
const zRemoto = new Map<number, number>();
const zDoRemoto = (id: number, p: { x: number; y: number }) => zRemoto.get(id) || zPe(Math.round(p.x), Math.round(p.y));

/* ══════════════════ 3. DIREÇÕES ══════════════════ */

/* 8 direções de tile -> vetor em coordenadas de tela */
const DIRS = [
  { dx: 1, dy: 0 },  // 0 E   (baixo-direita na tela)
  { dx: 1, dy: 1 },  // 1 SE  (para o observador)
  { dx: 0, dy: 1 },  // 2 S   (baixo-esquerda)
  { dx:-1, dy: 1 },  // 3 SW  (esquerda)
  { dx:-1, dy: 0 },  // 4 W   (cima-esquerda)
  { dx:-1, dy:-1 },  // 5 NW  (para o fundo)
  { dx: 0, dy:-1 },  // 6 N   (cima-direita)
  { dx: 1, dy:-1 }   // 7 NE  (direita)
];

/* O que fica no lugar do boneco enquanto a folha dele não chegou: nada (techspec-loading). Antes era um cavaleiro
   montado desenhado aqui, que aparecia no mapa e no retrato do topo durante o carregamento. A tela de carregamento
   cobre o mapa até a folha do personagem estar pronta; se uma folha falhar, cai no fantasma (SKIN_PADRAO). */
/** Altura da cabeça (px, a partir dos pés) usada antes da primeira folha, para o nome e o balão. */
const CABECA_PADRAO = 56;

/* ══════════════════ 4. JOGADOR, ENTRADA E CAMINHO ══════════════════ */

const player = {
  x: mapa.spawn.x + 0.5, y: mapa.spawn.y + 0.5,
  moveStamp: 0,
  tx: mapa.spawn.x, ty: mapa.spawn.y,
  nx: mapa.spawn.x, ny: mapa.spawn.y,
  /** Altura do pé na casa de agora e na do passo (techspec-casas §03.10). */
  tz: 0, nz: 0,
  t: 1,                 // progresso do passo (0..1)
  dir: 6,
  moving: false,
  gait: 0,              // fase da passada
  authoredRun: false,   // passo da IA: galope, sem mandar `c` ao servidor
  hp: 100, hpMax: 100,
  mana: 72, manaMax: 72,
  stam: 85, stamMax: 85,
  envenenado: false
};

/** Snap do tanque de aprendizado. O servidor manda valor+instante; a barra interpola. */
let fadigaSnap = FADIGA_TETO;
let fadigaEm = 0;
let fadigaFaixa = 0;
let criminalLeft = 0;
let paralizado = false;

let path: Step[] = [];
let serverPath: Step[] = [];
/** Ritmo dos passos que o servidor manda para o próprio avatar (IA da caçada): decide andar × correr. */
let selfPace = 0;
let selfPaceAt = 0;
let stepVis = 1;
let cam = {
  x: wx(player.x, player.y),
  y: wy(player.x, player.y) - zPe(player.tx, player.ty) * Z_PX
};
const zoom = 1;

/* Relativo à tela, não ao norte do mapa. No losango, +x do tile cai para
   baixo-direita: W=[0,-1] ia para cima-direita e D=[1,0] para baixo.
   W+D virava NE (eixo longo da tela) e o cavalo parecia galopar. */
const ANDAR = [
  ['andarNorte', -1, -1],
  ['andarSul', 1, 1],
  ['andarOeste', -1, 1],
  ['andarLeste', 1, -1]
] as const;

teardown.push(ligarAcao('ciclarAlvo', () => cycleTarget()));
teardown.push(ligarAcao('limparAlvo', () => { targetId = null; }));
// A manopla volta ao normal quando o mundo desmonta (troca de personagem, saída).
teardown.push(() => { cursorMira('mundo', false); cursorForaDeCombate(); });
teardown.push(registrarMira({
  ativa: () => tilePick != null,
  cancelar: () => cancelTilePick()
}));
teardown.push(aoComecarMovimento(() => {
  path = [];
  serverPath = [];
  player.authoredRun = false;
}));

function dirIndex(dx: number, dy: number){
  for (let i = 0; i < 8; i++) if (DIRS[i].dx === dx && DIRS[i].dy === dy) return i;
  return player.dir;
}

/** O pé do último `canStep` que passou (techspec-casas §03.10): onde o corpo fica depois do passo. */
let passoNz = 0;

/* Em UO a diagonal só passa se os dois lados estiverem livres. Dentro da planta de uma casa erguida, quem decide é a
   tabela de andares (escada, 1º andar), a partir do pé `fz`. */
function canStep(fx: number, fy: number, dx: number, dy: number, ignorarOcupacao = false, fz = zPe(fx, fy)){
  const nx = fx + dx, ny = fy + dy;
  if (!walkable(nx, ny)) return false;
  if (!insideBounds(nx, ny)) return false;
  const naCasa = passoCasa(mapa.id, fx, fy, fz, dirIndex(dx, dy));
  if (naCasa === false) return false;
  // Relevo (techspec-relevo): o passo 3D já pronto (subida, cabeça livre e os dois lados da diagonal).
  const rel = mapa.relevo;
  if (naCasa === null && rel && !rel.passo(fx, fy, dx, dy)) return false;
  if (naCasa === null && !rel && dx && dy && (!walkable(fx + dx, fy) || !walkable(fx, fy + dy))) return false;
  if (!ignorarOcupacao && ocupada(nx, ny)) return false;
  passoNz = naCasa ?? zPe(nx, ny);
  return true;
}

/** Um tile em direção ao alvo. Sem BFS: 3 testes de passo. */
function passoGuloso(fx: number, fy: number, tx: number, ty: number, ignorar = true): Step | null {
  const dx = clamp(tx - fx, -1, 1);
  const dy = clamp(ty - fy, -1, 1);
  if (!dx && !dy) return null;
  if (canStep(fx, fy, dx, dy, ignorar)) return { x: fx + dx, y: fy + dy };
  if (dx && canStep(fx, fy, dx, 0, ignorar)) return { x: fx + dx, y: fy };
  if (dy && canStep(fx, fy, 0, dy, ignorar)) return { x: fx, y: fy + dy };
  return null;
}

/** Encadeia no máximo `teto` casas adjacentes. O(teto), sem alocar grade. */
function encadearAte(fx: number, fy: number, tx: number, ty: number, teto = 8): Step[] {
  const out: Step[] = [];
  let cx = fx, cy = fy;
  for (let i = 0; i < teto; i++) {
    if (cx === tx && cy === ty) break;
    const n = passoGuloso(cx, cy, tx, ty);
    if (!n) break;
    out.push(n);
    cx = n.x;
    cy = n.y;
  }
  return out;
}

function findPath(sx: number, sy: number, gx: number, gy: number, ignorarOcupacao = false): Step[] {
  let tx = gx, ty = gy;
  if (!ignorarOcupacao && ocupada(gx, gy)) {
    let found = false;
    for (let i = 0; i < 8; i++) {
      const d = DIRS[i];
      const nx = gx + d.dx, ny = gy + d.dy;
      if (!walkable(nx, ny) || !insideBounds(nx, ny) || ocupada(nx, ny)) continue;
      tx = nx;
      ty = ny;
      found = true;
      break;
    }
    if (!found) return [];
  }
  if (!walkable(tx, ty)) return [];
  // A busca anda em (x, y, pé): dentro de uma casa erguida, a mesma casa do mapa tem o térreo e o 1º andar
  // (techspec-casas §03.10). O alvo é o pé que o losango mostra (o mais perto do andar do jogador).
  const MW = mapa.largura;
  const key = (x: number, y: number, z: number) => (y * MW + x) * 256 + ((z + 128) & 255);
  const zAlvo = peMaisPerto(mapa.id, tx, ty, player.tz);
  const z0 = sx === player.tx && sy === player.ty ? player.tz : zPe(sx, sy);
  // BFS em camadas: `dist` guarda a distância (em passos) de cada (casa, pé) alcançado.
  const dist = new Map<number, number>();
  // Os pés alcançados em cada casa (quase sempre um só): a volta do caminhoReto não tenta os 256.
  const pes = new Map<number, number[]>();
  const anotarPe = (x: number, y: number, z: number) => {
    const c = y * MW + x;
    const l = pes.get(c);
    if (l) l.push(z); else pes.set(c, [z]);
  };
  const inicio = key(sx, sy, z0);
  dist.set(inicio, 0);
  anotarPe(sx, sy, z0);
  const q: Array<[number, number, number]> = [[sx, sy, z0]];
  let head = 0, guard = 0;
  while (head < q.length && guard++ < 24000){
    const [cx, cy, cz] = q[head++];
    if (cx === tx && cy === ty && (zAlvo === null || cz === zAlvo))
      return caminhoReto(dist, pes, key(cx, cy, cz), inicio, sx, sy, tx, ty, ignorarOcupacao, MW, key);
    const dAqui = dist.get(key(cx, cy, cz))!;
    for (let i = 0; i < 8; i++){
      const d = DIRS[i];
      const nx = cx + d.dx, ny = cy + d.dy;
      if (!inMap(nx, ny)) continue;
      if (!canStep(cx, cy, d.dx, d.dy, ignorarOcupacao, cz)) continue;
      const k = key(nx, ny, passoNz);
      if (dist.has(k)) continue;
      dist.set(k, dAqui + 1);
      anotarPe(nx, ny, passoNz);
      q.push([nx, ny, passoNz]);
    }
  }
  return [];
}

/**
 * Entre os caminhos mais curtos, o mais reto (Renato, 02/11/2026). Na grade de 8 direções há muitos caminhos com o
 * mesmo número de passos; o BFS ficava com o primeiro da ordem das direções, que sai em diagonal e depois corrige (o
 * boneco ia "para um lado" e voltava). Aqui o caminho é montado de trás para frente: de cada casa, o passo anterior é,
 * entre os vizinhos a um passo a menos do início, o que fica mais perto da reta início → alvo. O desvio de obstáculo
 * continua igual (só entra casa que o BFS alcançou), e o número de passos não muda. Na tela isométrica a reta da grade
 * é a reta da tela.
 */
function caminhoReto(
  dist: Map<number, number>, pes: Map<number, number[]>, alvo: number, inicio: number,
  sx: number, sy: number, tx: number, ty: number, ignorarOcupacao: boolean,
  MW: number, key: (x: number, y: number, z: number) => number
): Step[] {
  const rx = tx - sx, ry = ty - sy;
  const norma = Math.hypot(rx, ry) || 1;
  // Distância da casa até a reta (o produto vetorial); empate: mais perto do início ao longo da reta não importa.
  const desvio = (x: number, y: number) => Math.abs(rx * (y - sy) - ry * (x - sx)) / norma;
  const out: Step[] = [];
  let cur = alvo;
  let guard = 0;
  while (cur !== inicio && guard++ < 4096){
    const casa = Math.floor(cur / 256);
    const cx = casa % MW, cy = (casa / MW) | 0;
    const cz = (cur % 256) - 128;
    out.push({ x: cx, y: cy });
    const dCur = dist.get(cur)!;
    let melhor = -1, melhorDesvio = Infinity, melhorDiag = true;
    for (let i = 0; i < 8; i++){
      const d = DIRS[i];
      const px = cx - d.dx, py = cy - d.dy;
      // O pé de quem vem de (px, py) pode ser qualquer um dos alcançados ali; o passo tem de chegar no pé `cz`.
      if (!inMap(px, py)) continue;
      for (const pz of pes.get(py * MW + px) ?? []){
        const k = key(px, py, pz);
        if (dist.get(k) !== dCur - 1) continue;
        if (!canStep(px, py, d.dx, d.dy, ignorarOcupacao, pz) || passoNz !== cz) continue;
        const dv = desvio(px, py);
        const diag = d.dx !== 0 && d.dy !== 0;
        // Mais perto da reta; empate, o passo reto (sem diagonal) no lugar da diagonal.
        if (dv < melhorDesvio - 1e-9 || (Math.abs(dv - melhorDesvio) <= 1e-9 && melhorDiag && !diag)){
          melhor = k; melhorDesvio = dv; melhorDiag = diag;
        }
      }
    }
    if (melhor < 0) return [];
    cur = melhor;
  }
  return out.reverse();
}

/* ══════════════════ 5. CANVAS, CÂMERA E CLIQUE ══════════════════ */

const cv = opts.canvas;
// 'let' porque a assadura desvia o desenho para um canvas fora da tela
let ctx = must2d(cv);
let VW = 0, VH = 0, dpr = 1;
let marFundo: CanvasGradient | null = null;
let marLuz: CanvasGradient | null = null;

function aquecerMar() {
  marFundo = ctx.createLinearGradient(0, 0, 0, VH);
  marFundo.addColorStop(0, '#1b3348');
  marFundo.addColorStop(1, '#0e1e2c');
  const raio = Math.max(8, VW * 0.3);
  marLuz = ctx.createRadialGradient(VW / 2, 0, 0, VW / 2, 0, raio);
  marLuz.addColorStop(0, 'rgba(60,96,128,.22)');
  marLuz.addColorStop(1, 'rgba(60,96,128,0)');
}

function resize(){
  dpr = Math.min(devicePixelRatio || 1, 2);
  VW = cv.clientWidth; VH = cv.clientHeight;
  cv.width = Math.round(VW * dpr);
  cv.height = Math.round(VH * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.imageSmoothingEnabled = false;
  aquecerMar();
}
function resizeSePreciso(){
  const nextDpr = Math.min(devicePixelRatio || 1, 2);
  if (cv.clientWidth === VW && cv.clientHeight === VH && nextDpr === dpr) return;
  resize();
}
on(window, 'resize', resize);
if (typeof ResizeObserver === 'function') {
  const ro = new ResizeObserver(() => resizeSePreciso());
  ro.observe(cv);
  teardown.push(() => ro.disconnect());
}

const w2sx = (px: number) => (px - cam.x) * zoom + VW / 2;
const w2sy = (py: number) => (py - cam.y) * zoom + VH / 2;

/** A altura de uma casa para o clique e o losango: na casa erguida, o pé mais perto do andar do jogador
 *  (no 1º andar o clique cai no piso de cima, não no térreo embaixo dele). Fora dela, o pé padrão. */
const zClique = (x: number, y: number) => peMaisPerto(mapa.id, x, y, player.tz) ?? zPe(x, y);

function centroTile(tx: number, ty: number){
  const z = mapa.dentro(tx, ty) ? zClique(tx, ty) : 0;
  return {
    gx: w2sx(wx(tx, ty)),
    gy: w2sy(wy(tx, ty) + TH / 2 - z * Z_PX)
  };
}

function pontoNoLosango(sx: number, sy: number, tx: number, ty: number){
  const { gx, gy } = centroTile(tx, ty);
  const nx = Math.abs(sx - gx) / (TW * 0.5 * zoom);
  const ny = Math.abs(sy - gy) / (TH * 0.5 * zoom);
  return nx + ny <= 1.02;
}

function screenToTile(sx: number, sy: number){
  const px = (sx - VW / 2) / zoom + cam.x;
  const py = (sy - VH / 2) / zoom + cam.y - TH / 2;
  const estX = Math.floor(py / TH + px / TW);
  const estY = Math.floor(py / TH - px / TW);
  const alcance = 10;
  let best: { x: number; y: number } | null = null;
  let bestKey = -Infinity;
  for (let ty = estY - alcance; ty <= estY + alcance; ty++){
    for (let tx = estX - alcance; tx <= estX + alcance; tx++){
      if (!inMap(tx, ty) || !pontoNoLosango(sx, sy, tx, ty)) continue;
      const key = (tx + ty) * 1024 + zClique(tx, ty);
      if (key > bestKey){
        bestKey = key;
        best = { x: tx, y: ty };
      }
    }
  }
  return best ?? { x: estX, y: estY };
}

/** O pé do jogador agora: no meio do passo, entre o z de onde sai e o de onde chega (a escada sobe macio). */
function zJogador(){
  return player.moving ? player.tz + (player.nz - player.tz) * Math.min(1, Math.max(0, player.t)) : player.tz;
}

function pésNaTela(x: number, y: number, zFixo?: number){
  const z = zFixo ?? zPe(Math.round(x), Math.round(y));
  return {
    rx: w2sx(wx(x, y)),
    ry: w2sy(wy(x, y) + TH / 2 - z * Z_PX)
  };
}

function hitAvatar(sx: number, sy: number, rx: number, ry: number, mount: string, monster = false){
  const horse = ehMontaria(mount);
  const hw = (monster ? 26 : horse ? 30 : 18) * zoom;
  const hh = (monster ? 56 : horse ? 52 : 38) * zoom;
  const dx = (sx - rx) / hw;
  const dy = (sy - (ry - hh * 0.55)) / hh;
  return dx * dx + dy * dy <= 1;
}

/** Clique no chão conta como o loot se o SQM estiver a até esta distância. */
const LOOT_CLIQUE = 4;

function ehSaque(remote: { kind: string; hits: number }){
  return remote.kind === 'corpse' || (remote.kind === 'monster' && remote.hits <= 0);
}

function tileDoRemote(remote: { toX: number; toY: number }){
  return { x: Math.round(remote.toX), y: Math.round(remote.toY) };
}

/** Todos os saques do SQM mais perto do clique, dentro de LOOT_CLIQUE. */
function saquesDoClique(sx: number, sy: number, jaAchado?: ReturnType<typeof hitRemote>){
  const tile = screenToTile(sx, sy);
  const hit = jaAchado === undefined ? hitRemote(sx, sy) : jaAchado;
  let ax = tile.x;
  let ay = tile.y;
  let melhor = Infinity;
  if (hit && ehSaque(hit)) {
    const t = tileDoRemote(hit);
    ax = t.x;
    ay = t.y;
    melhor = 0;
  }
  const lista: { id: number; x: number; y: number; d: number }[] = [];
  for (const remote of remotes.values()) {
    if (!ehSaque(remote)) continue;
    const t = tileDoRemote(remote);
    const d = chebyshev(tile.x, tile.y, t.x, t.y);
    lista.push({ id: remote.id, x: t.x, y: t.y, d });
    if (melhor > 0 && d <= LOOT_CLIQUE && d < melhor) {
      melhor = d;
      ax = t.x;
      ay = t.y;
    }
  }
  if (melhor > LOOT_CLIQUE) return [];
  return lista.filter(c => c.x === ax && c.y === ay).map(c => c.id);
}

function hitRemote(sx: number, sy: number){
  const now = performance.now();
  let bestId = -1;
  let bestKey = -Infinity;
  for (const remote of remotes.values()){
    const p = remotePos(remote, now);
    const { rx, ry } = pésNaTela(p.x, p.y, zDoRemoto(remote.id, p));
    if (!hitAvatar(sx, sy, rx, ry, remote.mount, remote.kind === 'monster' || remote.kind === 'dummy')) continue;
    const key = p.x + p.y;
    if (bestId < 0 || key > bestKey){
      bestKey = key;
      bestId = remote.id;
    }
  }
  return bestId < 0 ? null : remotes.get(bestId) ?? null;
}

let hoverTile: { x: number; y: number } | null = null;
let tilePick: ((x: number, y: number) => void) | null = null;
let tilePickCancel: (() => void) | null = null;

function cancelTilePick(){
  if (!tilePick && !tilePickCancel) return;
  tilePick = null;
  const c = tilePickCancel;
  tilePickCancel = null;
  cv.style.cursor = '';
  cursorMira('mundo', false);
  c?.();
}

function beginTilePick(onPick: (x: number, y: number) => void, onCancel?: () => void, hint?: string){
  cancelTilePick();
  tilePick = onPick;
  tilePickCancel = onCancel ?? null;
  cv.style.cursor = 'crosshair';
  if (hint) log(hint, 'note');
  else logKey('mundo.cliqueParaTeleportar', undefined, 'note');
}

let hoverPlayerName: string | null = null;

function notifyPlayerHover(hit: { name: string; x: number; y: number } | null){
  if (!hit){
    if (hoverPlayerName){
      hoverPlayerName = null;
      opts.onPlayerHover?.(null);
    }
    return;
  }
  hoverPlayerName = hit.name;
  opts.onPlayerHover?.(hit);
}

on(cv, 'pointermove', (e: PointerEvent) => {
  let portalHover: PortalNoMapa | null = null;
  const r = cv.getBoundingClientRect();
  const sx = e.clientX - r.left, sy = e.clientY - r.top;
  const alvo = screenToTile(sx, sy);
  hoverTile = inMap(alvo.x, alvo.y) ? alvo : null;
  const remote = hitRemote(sx, sy);
  // Manopla de mira (cursor do UO): sobre jogador, sobre loot e escolhendo um ponto no mapa.
  // Mob morto ainda é kind monster até virar cadáver: o nome sai no corpo, o saque também.
  const saque = (!!remote && ehSaque(remote)) || (!remote && saquesDoClique(sx, sy, null).length > 0);
  // A placa da casa (techspec-casa-interface-e-baus §09) também vira a manopla.
  const placa = !remote && !tilePick && !colocacaoAtiva() && !!placaNaTela(mapa, sx, sy, cam, VW, VH, zoom);
  cursorMira('mundo', !!tilePick || saque || placa || (!!remote?.name && (!remote.kind || remote.kind === 'player')));
  if (tilePick){
    cv.style.cursor = 'crosshair';
    notifyPlayerHover(null);
    return;
  }
  if (remote?.kind === 'npc'){
    cv.style.cursor = 'pointer';
    notifyPlayerHover(null);
  } else if (saque){
    cv.style.cursor = 'pointer';
    notifyPlayerHover(null);
  } else if (portalHover = hitPortal(sx, sy)){
    // A dica do portal: para onde ele leva (o mesmo rótulo da janela de atravessar).
    cv.style.cursor = 'pointer';
    notifyPlayerHover({ name: t(portalHover.rotulo), x: e.clientX + 14, y: e.clientY + 16 });
  } else if (remote?.name){
    cv.style.cursor = 'pointer';
    notifyPlayerHover({
      name: remote.kind === 'dummy' && remote.name.startsWith('manequim.')
        ? t(remote.name)
        : remote.kind && remote.kind !== 'player' ? remote.name : nomeComMarca(remote.name, remote.cargo ?? 0),
      x: e.clientX + 14,
      y: e.clientY + 16
    });
  } else {
    cv.style.cursor = '';
    notifyPlayerHover(null);
  }
});
on(cv, 'pointerleave', () => {
  hoverTile = null;
  cursorMira('mundo', false);
  if (!tilePick) cv.style.cursor = '';
  notifyPlayerHover(null);
});

/* Botão de andar (Configurações › Jogabilidade): o direito por padrão. O toque sempre anda. O tipo vem do
   pointerdown, porque o 'click' de navegador antigo não traz pointerType. */
let ultimoPonteiro = 'mouse';
const esquerdoAnda = () => ultimoPonteiro !== 'mouse' || lerBotaoAndar() === 'esquerdo';

/** Anda até a casa do ponto da tela (o mesmo caminho do clique no chão). `quieto`: sem aviso (o segurar repete). */
function andarAte(alvo: { x: number; y: number }, quieto = false){
  if (!inMap(alvo.x, alvo.y)) return;
  if (!insideBounds(alvo.x, alvo.y)){ if (!quieto) nudgeFence(); return; }
  if (!walkable(alvo.x, alvo.y)){ if (!quieto) logKey('mundo.naoCavalga', undefined, 'warn'); return; }
  const from = player.moving ? { x: player.nx, y: player.ny } : { x: player.tx, y: player.ty };
  const p = findPath(from.x, from.y, alvo.x, alvo.y);
  if (!p.length){ if (!quieto) logKey('mundo.semCaminho', undefined, 'warn'); return; }
  dummyChegada = null;
  path = p;
  // A marca do clique (techspec-marca-do-clique): só no clique, não a cada passo do segurar.
  if (!quieto) marcaClique = { x: alvo.x, y: alvo.y, t0: performance.now(), tipo: lerMarcaClique(), parada: -1 };
}

/**
 * A marca do clique de andar que está tocando. Enquanto o caminho ainda termina no tile dela, a animação para no
 * último quadro antes de sumir (PARADA_MARCA); quando o personagem chega (ou o caminho muda), segue dali até o fim.
 * `parada`: quanto tempo (ms) ela ficou parada, contado quando o caminho deixa de ir até ela; -1 = ainda indo.
 */
let marcaClique: { x: number; y: number; t0: number; tipo: MarcaClique; parada: number } | null = null;

/*
  Segurar o botão de andar (Renato, 28/10/2026): como no UO, com o botão segurado o personagem segue o cursor sem
  precisar clicar de novo. Soltar, sair da janela ou perder o foco para. No botão direito, segurar em cima de um
  jogador ou manequim abre o menu, como antes; no esquerdo, segurar em cima de algo clicável é só o clique.
  Segurando, o que vale é o RUMO do cursor em volta do personagem, não a casa debaixo dele (Renato, 04/11/2026): a
  câmera anda junto e a casa apontada mudava sozinha; dentro da casa, com a escada, ela caía do lado de fora e o
  caminho saía pela porta. Como no cliente da UO: oito fatias de 45° na tela, um passo por vez pelo mesmo trilho do
  teclado (escada, andar e mobília incluídos). Com o cursor em cima do corpo, para.
*/
const SEGUE_MS = 140;
/** O raio (px de arte) em volta do meio do corpo em que o cursor não aponta rumo nenhum. */
const SEGUE_PARADO = 14;
/** O meio do corpo acima do pé (px de arte), de onde sai o rumo do cursor. */
const SEGUE_MEIO = 26;
let segue: { botao: number; x: number; y: number; prox: number; andou: boolean; rumo: number[] } | null = null;
/** Quando o último segurar que andou acabou: o clique e o menu que o navegador manda depois do soltar não andam. */
let seguiuAte = -Infinity;

function pararDeSeguir(){
  if (segue?.andou) seguiuAte = performance.now();
  segue = null;
}

/**
 * O rumo do cursor em volta do personagem, nas oito fatias de 45° da tela: a direção (índice de DIRS) e, se ela não
 * passar, as duas vizinhas, a mais perto do cursor primeiro (como o CanWalk do cliente da UO). Vazio em cima do corpo.
 */
function rumoDoCursor(cx: number, cy: number): number[] {
  const r = cv.getBoundingClientRect();
  const px = w2sx(wx(player.x, player.y));
  const py = w2sy(wy(player.x, player.y) + TH / 2 - zJogador() * Z_PX) - SEGUE_MEIO * zoom;
  const vx = cx - r.left - px, vy = cy - r.top - py;
  if (Math.hypot(vx, vy) < SEGUE_PARADO * zoom) return [];
  // Na tela: direita = NE (7), baixo-direita = L (0), baixo = SE (1)… cima = NO (5).
  const passos = Math.atan2(vy, vx) / (Math.PI / 4);
  const fatia = (((Math.round(passos) + 7) % 8) + 8) % 8;
  const lado = passos >= Math.round(passos) ? 1 : -1;
  return [fatia, (fatia + lado + 8) % 8, (fatia - lado + 8) % 8];
}

function seguirCursor(now: number){
  if (!segue || now < segue.prox) return;
  if (!segue.andou) {
    // Virou segurar: o caminho do clique (se houver) sai; daqui em diante é passo a passo.
    segue.andou = true;
    path = [];
    dummyChegada = null;
  }
  segue.rumo = rumoDoCursor(segue.x, segue.y);
}

on(cv, 'pointerdown', (e: PointerEvent) => {
  ultimoPonteiro = e.pointerType || 'mouse';
  // Colocando um item na casa (techspec-casas §03.12): o clique põe, não anda.
  if (colocacaoAtiva()) return;
  if (e.pointerType !== 'mouse' || tilePick) return;
  const botao = lerBotaoAndar() === 'esquerdo' ? 0 : 2;
  if (e.button !== botao) return;
  const r = cv.getBoundingClientRect();
  const sx = e.clientX - r.left, sy = e.clientY - r.top;
  const remote = hitRemote(sx, sy);
  if (botao === 2 && remote && (ehDummyRemoto(remote)
    || (!!remote.name && remote.kind !== 'monster' && remote.kind !== 'npc' && remote.kind !== 'corpse' && remote.kind !== 'guard')))
    return;
  if (botao === 0 && (remote || saquesDoClique(sx, sy, null).length > 0 || hitPortal(sx, sy))) return;
  segue = { botao, x: e.clientX, y: e.clientY, prox: performance.now() + SEGUE_MS, andou: false, rumo: [] };
});
on(window, 'pointermove', (e: PointerEvent) => {
  if (!segue || e.pointerType !== 'mouse') return;
  // Soltou fora da janela (o pointerup não chegou): o botão já não está apertado.
  if ((e.buttons & (segue.botao === 2 ? 2 : 1)) === 0) { pararDeSeguir(); return; }
  segue.x = e.clientX;
  segue.y = e.clientY;
});
on(window, 'pointerup', (e: PointerEvent) => {
  if (segue && e.button === segue.botao) pararDeSeguir();
});
on(window, 'blur', pararDeSeguir);
on(document.documentElement, 'pointerleave', pararDeSeguir);

on(cv, 'click', (e: MouseEvent) => {
  const r = cv.getBoundingClientRect();
  let alvo = screenToTile(e.clientX - r.left, e.clientY - r.top);
  if (colocacaoAtiva()) {
    confirmarColocacao(mapa.id, alvo.x, alvo.y, player.tz, { x: player.tx, y: player.ty }, e.shiftKey);
    return;
  }
  if (tilePick) {
    if (!inMap(alvo.x, alvo.y) || !insideBounds(alvo.x, alvo.y) || !walkable(alvo.x, alvo.y)) {
      logKey('mundo.naoTeleporta', undefined, 'warn');
      return;
    }
    const pick = tilePick;
    tilePick = null;
    tilePickCancel = null;
    cv.style.cursor = '';
    cursorMira('mundo', false);
    pick(alvo.x, alvo.y);
    return;
  }
  const sx = e.clientX - r.left, sy = e.clientY - r.top;
  const remote = hitRemote(sx, sy);
  // A placa da casa (§09): o rótulo e a janela, como no UO; o clique não anda.
  if (!remote && cliqueNaPlaca(mapa, sx, sy, cam, VW, VH, zoom, e.clientX, e.clientY)) return;
  if (remote) {
    if (remote.kind === 'npc') {
      // O NPC de enfeite (sem ofício) não atende.
      if (!remote.titulo) return;
      opts.onNpcClick?.(remote.npcId, remote.name, remote.titulo, remote.toX, remote.toY, remote.temLoja, remote.temCorretor, remote.temBanco, remote.id, remote.alcance);
      return;
    }
    if (ehDummyRemoto(remote) || remote.kind === 'guard') {
      targetId = remote.id;
      if (warTargetId !== 0 && (remote.kind === 'guard' || dummyEhCombate(remote)))
        opts.onKeepAttack?.(targetId);
      return;
    }
    if (ehSaque(remote)) {
      const ids = saquesDoClique(sx, sy, remote);
      if (youDead) logKey('mundo.corpseDead', undefined, 'warn');
      else if (ids.length) opts.onCorpseClick?.(ids);
      return;
    }
    selecionarAlvo(remote.id);
    return;
  }
  const saques = saquesDoClique(sx, sy, null);
  if (saques.length) {
    if (youDead) logKey('mundo.corpseDead', undefined, 'warn');
    else opts.onCorpseClick?.(saques);
    return;
  }
  // Baú ou item preso da casa (techspec-casas §03.12): o menu de clique, não o caminho.
  if (!youDead && cliqueNaCasa(mapa.id, alvo.x, alvo.y, zClique(alvo.x, alvo.y), e.clientX, e.clientY)) return;
  const portalHit = hitPortal(sx, sy);
  if (portalHit) {
    if (youDead) {
      logKey('mundo.portalDead', undefined, 'warn');
      return;
    }
    const from = player.moving ? { x: player.nx, y: player.ny } : { x: player.tx, y: player.ty };
    if (chebyshev(from.x, from.y, portalHit.x, portalHit.y) <= PORTAL_ALCANCE) {
      abrirPortalPorClique(portalHit);
      return;
    }
    alvo = { x: portalHit.x, y: portalHit.y };
  }
  // Com o botão direito para andar, o esquerdo no chão não faz nada (portal longe ainda leva até ele).
  if (!portalHit && !esquerdoAnda()) return;
  // O clique que o navegador manda ao soltar um segurar que já andou não refaz o caminho até a casa do cursor.
  if (segue?.andou || performance.now() - seguiuAte < 400) return;
  andarAte(alvo);
});

on(cv, 'contextmenu', (e: MouseEvent) => {
  e.preventDefault();
  if (colocacaoAtiva()) { pararColocacao(); return; }
  if (tilePick) { cancelTilePick(); return; }
  const r = cv.getBoundingClientRect();
  const remote = hitRemote(e.clientX - r.left, e.clientY - r.top);
  if (remote && ehDummyRemoto(remote)) {
    if (!dummyEhCombate(remote))
      opts.onDummyMenu?.(remote.id, e.clientX, e.clientY);
    return;
  }
  const temMenu = !!remote?.name && remote.kind !== 'monster' && remote.kind !== 'npc' && remote.kind !== 'corpse' && remote.kind !== 'guard';
  if (temMenu && !emEvento()) {
    const rp = remotePos(remote!, performance.now());
    opts.onPlayerMenu?.(remote!.name, e.clientX, e.clientY, remote!.guildTag ?? '', remote!.cargo ?? 0, {
      notoriety: remote!.notoriety ?? 0,
      rel: remote!.rel ?? 0,
      dist: chebyshev(Math.round(player.x), Math.round(player.y), Math.round(rp.x), Math.round(rp.y))
    });
    return;
  }
  // Botão direito anda (padrão): no chão, em monstro, NPC, corpo ou portal, vai até a casa clicada.
  // O toque longo do celular não anda (o toque simples já anda).
  if (temMenu || esquerdoAnda()) return;
  // No Mac/Linux o menu sai no apertar; no Windows, no soltar. Se o segurar já levou o personagem, não refaz o caminho.
  if (segue?.andou || performance.now() - seguiuAte < 400) return;
  const portalHit = hitPortal(e.clientX - r.left, e.clientY - r.top);
  andarAte(portalHit ? { x: portalHit.x, y: portalHit.y } : screenToTile(e.clientX - r.left, e.clientY - r.top));
});

/* ══════════════════ 7. HUD ══════════════════ */

const lines: JournalLine[] = [];
const combatLines: JournalLine[] = [];
let lineSeq = 0;

function ehCombate(chave: string | undefined, tone: JournalTone): boolean {
  if (tone === 'spell') return true;
  if (!chave) return false;
  return chave.startsWith('mundo.cast')
    || chave.startsWith('mundo.poison')
    || chave.startsWith('mundo.melee')
    || chave === 'mundo.voceConjura'
    || chave === 'mundo.alguemConjura'
    || chave === 'mundo.voceFalhou'
    || chave === 'mundo.alguemFalhou'
    || chave === 'mundo.voceRecebeDano'
    || chave === 'mundo.voceCausaDano'
    || chave === 'mundo.voceErra'
    || chave === 'mundo.voceEsquiva'
    || chave === 'mundo.alvoEsquiva'
    || chave === 'mundo.voceMata'
    || chave === 'mundo.voceMorre'
    || chave === 'mundo.attackedInnocent';
}

function emitir(line: JournalLine, combate: boolean){
  const buf = combate ? combatLines : lines;
  const max = combate ? COMBAT_MAX : JOURNAL_MAX;
  buf.push(line);
  while (buf.length > max) buf.shift();
  if (combate) opts.onCombat?.(combatLines.slice());
  else opts.onJournal?.(lines.slice());
}

function log(text: string, tone: JournalTone = 'plain'){
  emitir({ id: ++lineSeq, text, tone, at: Date.now() }, ehCombate(undefined, tone));
}

/**
 * Linha com chave: o diário guarda a chave e os argumentos, não a frase, então
 * o histórico inteiro retraduz quando o jogador troca de idioma. Nome de
 * jogador dentro do argumento continua literal. Magia, falha e dano saem
 * na aba Combate, não aqui.
 */
function logKey(chave: string, args?: Record<string, string | number>, tone: JournalTone = 'plain'){
  emitir({ id: ++lineSeq, text: t(chave, args), chave, args, tone, at: Date.now() }, ehCombate(chave, tone));
}

anotarDiarioImpl = text => log(text, 'plain');
flutuarPrataImpl = preco => spawnTexto(youId, `−${preco} prata · banco`);
flutuarSobreMimImpl = texto => spawnTexto(youId, texto);
avisoSobreMimImpl = texto => avisarSobre(youId, texto);

function escreverPlaca(
  ctx: CanvasRenderingContext2D, plate: string, x: number, y: number, vip: boolean, morto: boolean, selo = 0
){
  const w = ctx.measureText(plate).width;
  // A coroa vai à direita do nome e o selo de fundador à esquerda (techspec-correio §05): o nome fica no meio.
  const extra = (vip ? 16 : 0) - (selo ? 16 : 0);
  const cx = x - extra / 2;
  ctx.strokeText(plate, cx, y);
  ctx.fillText(plate, cx, y);
  if (vip) desenharCoroa(ctx, cx + w / 2 + 10, y - 1, ouroDaCoroa(morto));
  if (selo) desenharSelo(ctx, cx - w / 2 - 10, y - 1, selo, morto);
}

function sextant(x: number, y: number){
  const cy = mapa.altura / 2, cx = mapa.largura / 2;
  const latAbs = (y - cy) * 1.72, lonAbs = (x - cx) * 1.72;
  const fmt = (v: number, pos: string, neg: string) => {
    const d = Math.abs(v) | 0;
    const m = Math.round((Math.abs(v) - d) * 60);
    return `${d}° ${String(m).padStart(2, '0')}'${v < 0 ? neg : pos}`;
  };
  // As letras cardeais são texto: em português o oeste é O, não W.
  return `${fmt(latAbs, t('mundo.sul'), t('mundo.norte'))}, ${fmt(lonAbs, t('mundo.leste'), t('mundo.oeste'))}`;
}

function regionAt(x: number, y: number){
  return mapa.regiaoEm(x, y);
}

/** Os pads duelo-a/duelo-b têm 3×3 tiles no meio da luta. O passo
 *  interpolado cruza a borda várias vezes por segundo e cada troca
 *  re-renderizava o mundo. Na arena o lugar estável é o nome do mapa. */
function anunciaChegada(lugar: string){
  if (lugar === 'duelo-a' || lugar === 'duelo-b') return false;
  if (mapa.id === 'arena-duelo-ice-age') return false;
  return true;
}

let lastRegion = '';

const MM = opts.minimap ?? null;
const mmCtx = MM ? must2d(MM) : null;
let mmBase = assarMinimapa(mapa);
/** Mesmos pontos de ChegadaMinoc. Assar antes do welcome evita a tela preta na casa sorteada. */
const CHEGADA_MINOC: ReadonlyArray<readonly [number, number]> = [
  [48, 52], [40, 46], [49, 39], [57, 46]
];
function aquecerChegada(m: typeof mapa) {
  if (m.id !== 'minoc') return;
  for (const [x, y] of CHEGADA_MINOC) aquecerEm(m, x, y);
}
aquecerSpawn(mapa);
aquecerChegada(mapa);
let trocaAc: AbortController | null = null;

function drawMinimap(time: number){
  if (!MM || !mmCtx) return;
  mmCtx.imageSmoothingEnabled = false;
  mmCtx.clearRect(0, 0, MM.width, MM.height);
  // +45° no canvas (Y para baixo): o passo da tela casa com o ponto.
  // Cima na tela sobe no radar; a seta para baixo-direita vai para
  // baixo-direita. O círculo recorta o mapa cheio, como antes.
  const s = MM.width / Math.max(mapa.largura, mapa.altura);
  const u = 1 / s;
  mmCtx.save();
  mmCtx.translate(MM.width / 2, MM.height / 2);
  mmCtx.rotate(Math.PI / 4);
  mmCtx.scale(s, s);
  mmCtx.translate(-mapa.largura / 2, -mapa.altura / 2);
  mmCtx.drawImage(mmBase, 0, 0);
  // Casas erguidas (techspec-casas §03.15): telhado em tijolo; a sua em ouro.
  for (const casa of casasNoMinimapa(mapa.id)) {
    mmCtx.fillStyle = casa.minha ? '#d9ae3c' : '#9a6a48';
    const cel = casa.celulas;
    for (let i = 0; i + 1 < cel.length; i += 2) mmCtx.fillRect(cel[i], cel[i + 1], 1, 1);
  }
  mmCtx.fillStyle = '#7fb0ff';
  for (const e of mapa.estaticos) {
    if (mapa.estaticosTs.tile(e.tile).nome !== 'moongate') continue;
    mmCtx.fillRect(e.x - u, e.y - u, 3 * u, 3 * u);
  }
  // Companheiro de grupo (Rel == 3) e colega de guilda/aliança (Rel == 1).
  // Sem o 3 aqui, o ponto do companheiro sumia do minimapa. Você fica por cima.
  for (const remote of remotes.values()) {
    if (remote.kind === 'monster' || remote.kind === 'corpse' || (remote.rel !== 1 && remote.rel !== 3)) continue;
    const rp = remotePos(remote, time);
    mmCtx.fillStyle = NICK.party;
    mmCtx.fillRect(rp.x - 1.5 * u, rp.y - 1.5 * u, 3 * u, 3 * u);
    mmCtx.strokeStyle = 'rgba(0,0,0,.55)';
    mmCtx.lineWidth = u;
    mmCtx.strokeRect(rp.x - 2 * u, rp.y - 2 * u, 4 * u, 4 * u);
  }
  const pulse = 5 + (REDUCED ? 0 : Math.sin(time * 3) * 1.4);
  mmCtx.fillStyle = 'rgba(201,162,39,.30)';
  mmCtx.beginPath();
  mmCtx.arc(player.x, player.y, pulse * u, 0, Math.PI * 2);
  mmCtx.fill();
  mmCtx.fillStyle = youOculto ? '#9a9a9a' : '#f2e4bd';
  mmCtx.fillRect(player.x - 1.5 * u, player.y - 1.5 * u, 3 * u, 3 * u);
  mmCtx.strokeStyle = 'rgba(0,0,0,.55)';
  mmCtx.lineWidth = u;
  mmCtx.strokeRect(player.x - 2 * u, player.y - 2 * u, 4 * u, 4 * u);
  mmCtx.restore();
}

/* paperdoll: pose SE idle a pé. Perfil = corpo inteiro; barra = close-up da cabeça. */
const CORPO_W = 72;
const CORPO_H = 96;
const ROSTO_TAM = 34;

/** O que o boneco desenha: o seu, ou o de outro jogador (Inspecionar). */
interface LookDoBoneco {
  skin: string; skinHue: number; cloth: string;
  hairStyle: string; hairHue: number; beardStyle: string; beardHue: number;
  shield: string; weapon: string; dead: boolean; oculto: boolean; feminino: boolean;
  /** Direção do motor (0–7). O boneco olha para a câmera (1); o provador do guarda-roupa gira. */
  dir?: number;
}

/** A direção do boneco que está sendo pintado agora (paintDoll). */
let dirDoBoneco = 1;

function lookDoYou(): LookDoBoneco {
  return {
    skin: youSkin, skinHue: youSkinHue, cloth: youCloth,
    hairStyle: youHairStyle, hairHue: youHairHue, beardStyle: youBeardStyle, beardHue: youBeardHue,
    shield: youShield, weapon: youWeapon, dead: youDead, oculto: youOculto, feminino: femininoDo(youSkin, false)
  };
}

function paintDoll(doll: HTMLCanvasElement, foco: FocoDoBoneco, look: LookDoBoneco = lookDoYou()){
  dirDoBoneco = look.dir ?? 1;
  try {
    pintarBoneco(doll, foco, look);
  } finally {
    dirDoBoneco = 1;
  }
}

type FocoDoBoneco = 'corpo' | 'rosto' | 'mochila' | 'provador';

function pintarBoneco(doll: HTMLCanvasElement, foco: FocoDoBoneco, look: LookDoBoneco){
  const pd = must2d(doll);
  pd.clearRect(0, 0, doll.width, doll.height);
  pd.imageSmoothingEnabled = false;
  const rider = tingirFolha(folhaIdleParada(look.skin, ''), look.skinHue);
  const art = arteDa(rider, dirDoBoneco, 0);
  if (!art) return;
  const ax = rider.ancoraX;
  const ay = rider.ancoraY;
  let scale: number;
  let ox: number;
  let oy: number;
  if (foco === 'rosto') {
    scale = Math.min(doll.width, doll.height) / ROSTO_TAM;
    const rostoY = ay - 56;
    ox = Math.round(doll.width / 2 - ax * scale);
    oy = Math.round(doll.height * 0.34 - rostoY * scale);
  } else if (foco === 'provador') {
    /* o provador do guarda-roupa: escala inteira e grande, o pé perto da base (a sombra do palco fica embaixo). */
    scale = Math.max(1, Math.floor(Math.min(doll.width / 46, doll.height / 88)));
    ox = Math.round(doll.width / 2 - ax * scale);
    oy = Math.round(doll.height - 8 * scale - ay * scale);
  } else if (foco === 'mochila') {
    /* inventário: escala inteira — o pixel da UO fica quadrado. Âncora no meio, pé acima da base para o pé não sumir na borda. */
    const PE_MOCHILA = 12;
    scale = 2;
    ox = Math.round(doll.width / 2 - ax * scale);
    oy = Math.round(doll.height - PE_MOCHILA * scale - ay * scale);
  } else {
    const pad = 8;
    scale = Math.min((doll.width - pad * 2) / CORPO_W, (doll.height - pad * 2) / CORPO_H);
    const corpoY = ay - CORPO_H * 0.48;
    ox = Math.round(doll.width / 2 - ax * scale);
    oy = Math.round(doll.height / 2 - corpoY * scale);
  }
  if (look.oculto) pd.filter = FILTRO_OCULTO;
  // Olhando para a câmera, a capa do guarda-roupa fica atrás do corpo.
  if (!look.dead && look.skin !== SKIN_PADRAO && capaAtras(dirDoBoneco)) {
    const capa = camadasDaCloth(look.cloth).find(c => ehCapa(c));
    const folha = capa ? pegarCloth(capa, '', 'idle') : undefined;
    if (capa && folha) {
      const parada = folha.frames > IDLE_HOLD_QUADRO ? folhaParada(folha, IDLE_HOLD_QUADRO) : folha;
      blitIdleOverlay(pd, ox, oy, scale, ax, ay, tingirCamada(parada, capa));
    }
  }
  desenharQuadro(pd, art, ox, oy, rider.cellW * scale, rider.cellH * scale);
  blitClothIdle(pd, ox, oy, scale, ax, ay, look);
  // O manto com capuz cobre a cabeça: sem cabelo, chapéu ou elmo por cima dele.
  const capuz = capuzNoFio(look.cloth);
  // Morto (o robe do fantasma tem capuz): sem cabelo nem barba, como no mundo.
  const semCabeca = look.dead || look.skin === SKIN_PADRAO;
  const hair = !capuz && !semCabeca && estiloDaFolha(look.hairStyle) ? pegarCabelo(look.hairStyle, '', 'idle') : undefined;
  if (hair) {
    const parada = hair.frames > IDLE_HOLD_QUADRO ? folhaParada(hair, IDLE_HOLD_QUADRO) : hair;
    blitIdleOverlay(pd, ox, oy, scale, ax, ay, tingirFolhaCabelo(parada, look.hairHue));
  }
  if (!semCabeca && !look.feminino && estiloValido(look.beardStyle)) {
    const beard = pegarBarba(look.beardStyle, '', 'idle');
    if (beard) {
      const parada = beard.frames > IDLE_HOLD_QUADRO ? folhaParada(beard, IDLE_HOLD_QUADRO) : beard;
      blitIdleOverlay(pd, ox, oy, scale, ax, ay, tingirFolhaCabelo(parada, look.beardHue || look.hairHue));
    }
  }
  for (const cabeca of look.dead || look.skin === SKIN_PADRAO || capuz ? [] : camadasDaCloth(soElmo(look.cloth))) {
    const helm = pegarCloth(cabeca, '', 'idle');
    if (!helm) continue;
    const parada = helm.frames > IDLE_HOLD_QUADRO ? folhaParada(helm, IDLE_HOLD_QUADRO) : helm;
    blitIdleOverlay(pd, ox, oy, scale, ax, ay, tingirCamada(parada, cabeca));
  }
  blitShieldIdle(pd, ox, oy, scale, ax, ay, '', look.shield);
  blitWeaponIdle(pd, ox, oy, scale, ax, ay, armaDoVisual(look.weapon, look.cloth), '', false);
  pd.filter = 'none';
}

/** Bonecos de outros jogadores (Inspecionar): canvas → nome. Repintados junto com o seu. */
const bonecosDeOutros = new Map<HTMLCanvasElement, string>();

function pintarBonecoDeOutro(cv: HTMLCanvasElement, nome: string){
  const alvo = nome.toLowerCase();
  for (const r of remotes.values()) {
    if (r.kind !== 'player' || r.name.toLowerCase() !== alvo) continue;
    paintDoll(cv, 'mochila', {
      skin: r.skin, skinHue: r.skinHue, cloth: r.cloth,
      hairStyle: r.hairStyle, hairHue: r.hairHue, beardStyle: r.beardStyle, beardHue: r.beardHue,
      shield: r.shield, weapon: r.weapon, dead: r.dead, oculto: r.oculto, feminino: femininoDo(r.skin, r.feminino)
    });
    return;
  }
}

/** Quem é do seu grupo (nomes em minúsculas): o alvo amigo não vira ataque e o lock do War não o toma (techspec-grupo-placas). */
let grupoNomes = new Set<string>();

function ehDoGrupo(r: { kind?: string; name: string } | undefined): boolean {
  return !!r && r.kind === 'player' && grupoNomes.has(r.name.toLowerCase());
}

function ehDoGrupoId(id: number | null): boolean {
  return id != null && ehDoGrupo(remotes.get(id));
}

/** Os retratos das placas do grupo: canvas → nome e a aparência que o canal do grupo mandou. */
const retratos = new Map<HTMLCanvasElement, { nome: string; look: RetratoDoMembro | null }>();
/** As folhas que um retrato já pediu: pede uma vez por aparência. */
const folhasDeRetrato = new Set<string>();

/** O boneco do retrato: o do mundo, se ele está na sala e vivo; senão, o que o canal do grupo mandou. */
function lookDoRetrato(nome: string, look: RetratoDoMembro | null): LookDoBoneco | null {
  const alvo = nome.toLowerCase();
  for (const r of remotes.values()) {
    if (r.kind !== 'player' || r.dead || r.name.toLowerCase() !== alvo) continue;
    return {
      skin: r.skin, skinHue: r.skinHue, cloth: r.cloth,
      hairStyle: r.hairStyle, hairHue: r.hairHue, beardStyle: r.beardStyle, beardHue: r.beardHue,
      shield: r.shield, weapon: r.weapon, dead: false, oculto: false, feminino: femininoDo(r.skin, r.feminino)
    };
  }
  if (!look) return null;
  const skin = look.skin && look.skin !== SKIN_PADRAO ? look.skin : 'human-male';
  return {
    skin, skinHue: look.skinHue ?? 0, cloth: look.cloth ?? '',
    hairStyle: look.hairStyle ?? '', hairHue: look.hairHue ?? 0, beardStyle: look.beardStyle ?? '', beardHue: look.beardHue ?? 0,
    shield: look.shield ?? '', weapon: look.weapon ?? '', dead: false, oculto: false, feminino: femininoDo(skin, look.feminino)
  };
}

function pintarRetrato(cv: HTMLCanvasElement, item: { nome: string; look: RetratoDoMembro | null }){
  const look = lookDoRetrato(item.nome, item.look);
  if (!look) { must2d(cv).clearRect(0, 0, cv.width, cv.height); return; }
  const chave = [look.skin, look.cloth, look.hairStyle, look.beardStyle, look.weapon, look.shield].join('|');
  if (!folhasDeRetrato.has(chave)) {
    folhasDeRetrato.add(chave);
    ensureSkin(look.skin);
    if (look.cloth) ensureCloth(look.cloth);
    if (look.hairStyle) ensureCabelo(look.hairStyle);
    if (look.beardStyle) ensureBarba(look.beardStyle);
    if (look.weapon) ensureArma(look.weapon);
    if (look.shield) ensureEscudo(look.shield);
  }
  paintDoll(cv, 'rosto', look);
}

/**
 * O retrato da placa do alvo (Renato, 29/10/2026), como o das placas do grupo: jogador e guarda pelo boneco (close do
 * rosto); monstro e manequim pelo primeiro quadro parado, recortado no alto do desenho. Canvas → ShortId do alvo.
 */
const retratosDeAlvo = new Map<HTMLCanvasElement, number>();
/** O recorte do busto de cada folha (url|direção): o quadro desenhado num canvas e a caixa do que não é transparente. */
const bustos = new Map<string, { cv: HTMLCanvasElement; x: number; y: number; w: number; h: number }>();

function bustoDa(folha: Folha, dir: number){
  const chave = `${folha.url}|${dir}`;
  const pronto = bustos.get(chave);
  if (pronto) return pronto;
  const q = arteDa(folha, dir, 0);
  if (!q) return null;
  const cv = document.createElement('canvas');
  cv.width = q.w;
  cv.height = q.h;
  const g = must2d(cv);
  desenharQuadro(g, q, 0, 0, q.w, q.h);
  let x0 = q.w, y0 = q.h, x1 = -1, y1 = -1;
  try {
    const px = g.getImageData(0, 0, q.w, q.h).data;
    for (let y = 0; y < q.h; y++)
      for (let x = 0; x < q.w; x++)
        if (px[(y * q.w + x) * 4 + 3] > 24) {
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
  } catch {
    x0 = 0; y0 = 0; x1 = q.w - 1; y1 = q.h - 1;
  }
  if (x1 < 0) return null;
  const caixa = { cv, x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
  bustos.set(chave, caixa);
  return caixa;
}

function pintarRetratoDoAlvo(cv: HTMLCanvasElement, id: number){
  const g = must2d(cv);
  g.clearRect(0, 0, cv.width, cv.height);
  const r = remotes.get(id);
  if (!r) return;
  if (r.kind === 'player' || r.kind === 'guard') {
    paintDoll(cv, 'rosto', {
      skin: r.skin, skinHue: r.skinHue, cloth: r.cloth,
      hairStyle: r.hairStyle, hairHue: r.hairHue, beardStyle: r.beardStyle, beardHue: r.beardHue,
      shield: r.shield, weapon: r.weapon, dead: r.dead, oculto: false, feminino: femininoDo(r.skin, r.feminino)
    });
    return;
  }
  let folha: Folha | undefined;
  let dir = 1;
  if (ehDummyRemoto(r)) {
    ensureDummy();
    folha = prontas.get(DUMMY_JSON);
    dir = varianteDummy(r.defId) === 1 ? 7 : 1;
  } else {
    ensureMob(r.defId);
    folha = folhaInimigo(r.defId, false);
  }
  if (!folha) return;
  const b = bustoDa(folha, dir);
  if (!b) return;
  // O alto do desenho, num quadrado: a cabeça e o tronco. Bicho baixo e largo (lobo, aranha) entra inteiro.
  const lado = Math.max(8, Math.min(Math.max(b.w, b.h * 0.62), b.h));
  const sx = Math.round(b.x + b.w / 2 - lado / 2);
  g.imageSmoothingEnabled = false;
  g.drawImage(b.cv, sx, b.y, lado, lado, 0, 0, cv.width, cv.height);
}

/** O provador do guarda-roupa: o seu boneco com o traje em prova, na direção escolhida. */
const provadores = new Map<HTMLCanvasElement, { cloth: string; dir: number; feminino: boolean }>();

/** O corpo do provador: o seu; sem corpo vestível (o fantasma de reserva), o humano do sexo do personagem. */
function corpoDoProvador(feminino: boolean): string {
  return youSkin !== SKIN_PADRAO ? youSkin : (feminino ? 'human-female' : 'human-male');
}

function pintarProvador(cv: HTMLCanvasElement, prova: { cloth: string; dir: number; feminino: boolean }){
  const skin = corpoDoProvador(prova.feminino);
  paintDoll(cv, 'provador', {
    ...lookDoYou(), skin, feminino: femininoDo(skin, prova.feminino), cloth: prova.cloth, dir: prova.dir, dead: false, oculto: false
  });
}

function paintDolls(){
  if (opts.paperdoll) paintDoll(opts.paperdoll, 'rosto');
  if (opts.profileDoll) paintDoll(opts.profileDoll, 'corpo');
  if (opts.inventoryDoll) paintDoll(opts.inventoryDoll, 'mochila');
  for (const [cv, nome] of bonecosDeOutros) pintarBonecoDeOutro(cv, nome);
  for (const [cv, prova] of provadores) pintarProvador(cv, prova);
  for (const [cv, item] of retratos) pintarRetrato(cv, item);
  for (const [cv, id] of retratosDeAlvo) pintarRetratoDoAlvo(cv, id);
}

/** A folha vazia: um pixel transparente nas 8 direções. É o que se desenha enquanto a folha certa não chegou. */
const vazia: Folha = (() => {
  const c = document.createElement('canvas');
  c.width = 1;
  c.height = 1;
  const q: Quadro = { fonte: c, sx: 0, sy: 0, w: 1, h: 1, flip: false };
  return { url: 'vazia', cellW: 1, cellH: 1, frames: 1, fps: 1, ancoraX: 0, ancoraY: 0, fonte: c, dirs: Array.from({ length: 8 }, () => [q]) };
})();
const prontas = new Map<string, Folha>();
let youDead = !!opts.dead;
let youOculto = false;
let lifeHeld = false;
let lifeLook: { mount: string; skin?: string; cloth?: string; weapon?: string; shield?: string } | null = null;
let serverSelfX = player.tx;
let serverSelfY = player.ty;
/** O z do pé que o servidor mandou por último (techspec-casas §03.10): o snap de volta não derruba do andar de cima. */
let serverSelfZ = 0;
let youNotoriety = opts.notoriety ?? 0;
let youGuildTag = '';
let youCargo = opts.staffCargo ?? 0;
let youVip = false;
/** Selo de fundador do próprio jogador (0..3). */
let youSelo = 0;
let youMount = '';
let youCloth = '';
let youWeapon = '';
let youShield = '';
let youSkin = youDead ? SKIN_PADRAO : ((opts.skin ?? '').trim() || SKIN_PADRAO);
let youSkinHue = peleNoIntervalo(opts.skinHue);
let youHairStyle = estiloDaFolha(opts.hairStyle ?? '');
let youHairHue = opts.hairHue ?? 0;
let youBeardStyle = estiloDaFolha(opts.beardStyle ?? '');
let youBeardHue = opts.beardHue ?? 0;
let youIdleAt = performance.now();
let youWasMoving = false;
let youAttackAt = 0;
let lastStrikeId = 0;
let swingPendenteEm = 0;
let swingPendenteDir: number | null = null;
let swingPendenteMs = 0;

/*
 * Não há tabela de posição aqui, e não deve voltar a haver.
 *
 * O assento no lombo e a gola do robe eram deslocamentos por direção, em
 * pixel, aplicados na hora de desenhar. Custavam três coisas: a mesma arte
 * caía em lugares diferentes conforme a SKIN de outra camada, o paperdoll
 * repetia a conta em outro lugar, e o validador media a folha na posição
 * errada — escondendo pele exposta que só aparecia no jogo.
 *
 * A parte constante virou âncora nas folhas (7 set). O que sobrava era
 * ajuste fino por direção, e ajuste fino mora na arte: a folha tem 5 linhas
 * e o motor espelha 3, então quem quiser mexer numa direção mexe na linha
 * dela. O motor desenha tudo em (rx, ry) e não sabe de mais nada.
 */

function pegarFolha(skin: string, mount: string, acao: string): Folha | undefined {
  return prontas.get(jsonPersonagem(skin, mount, acao))
    ?? (skin !== SKIN_PADRAO ? prontas.get(jsonPersonagem(SKIN_PADRAO, mount, acao)) : undefined);
}

function pegarCloth(cloth: string, mount: string, acao: string): Folha | undefined {
  const url = jsonCloth(cloth, mount, acao);
  return url ? prontas.get(url) : undefined;
}

function pegarCabelo(estilo: string, mount: string, acao: string): Folha | undefined {
  const url = jsonCabelo(estilo, mount, acao);
  return url ? prontas.get(url) : undefined;
}

function pegarBarba(estilo: string, mount: string, acao: string): Folha | undefined {
  const url = jsonBarba(estilo, mount, acao);
  return url ? prontas.get(url) : undefined;
}

/** O que pinta entre o corpo e o cabelo: tudo menos elmo e chapéu, e menos a capa quando ela vai atrás. */
function clothSemElmo(cloth: string, dir?: number): string {
  const atras = dir !== undefined && capaAtras(dir);
  return camadasDaCloth(cloth).filter(c => !ehCamadaCabeca(c) && !(atras && ehCapa(c))).join('+');
}

/** Elmo e chapéu: depois do cabelo. */
function soElmo(cloth: string): string {
  return camadasDaCloth(cloth).filter(c => ehCamadaCabeca(c)).join('+');
}

/** A capa do guarda-roupa quando ela vai atrás do corpo (olhando para a câmera); senão vazio. */
function capaDeTras(cloth: string, dir: number): string {
  if (!capaAtras(dir)) return '';
  return camadasDaCloth(cloth).find(c => ehCapa(c)) ?? '';
}

/** Placa leva a cor do material; peça do guarda-roupa, a matiz da UO. */
function tingirCamada(folha: Folha, kind: string): Folha {
  const traje = trajeDaCamada(kind);
  if (traje) return tingirFolhaTraje(folha, traje.hue);
  return tingirFolhaMaterial(folha, materialDaPeca(kind));
}

function femininoDo(skin: string, flag?: boolean): boolean {
  return flag === true || skin === 'human-female';
}

function acaoDaFolha(folha: Folha): string {
  const m = folha.url.match(/\/(idle|walking|running|spell(?:-area)?|attack(?:-[^/]+)?)\/[^/]+$/);
  return m?.[1] ?? 'idle';
}

/** Pose das camadas em movimento: a do corpo, se ele estiver correndo. */
function acaoAndando(bodyFolha?: Folha): string {
  return bodyFolha && acaoDaFolha(bodyFolha) === 'running' ? 'running' : 'walking';
}

/**
 * Correr só troca a folha quando TODAS as camadas que este personagem desenha
 * têm `running` pronta. Uma camada andando por cima de um corpo correndo fica
 * desencontrada quadro a quadro; aí ele continua andando, inteiro.
 * A regra é "quem tem walking precisa ter running": camada que nem anda
 * (estilo sem folha) não bloqueia.
 */
function podeCorrer(
  skin: string, cloth: string, hairStyle: string, beardStyle: string,
  feminino: boolean, weapon: string, shield: string
): boolean {
  if (skin === SKIN_PADRAO || !prontas.has(jsonPersonagem(skin, '', 'running'))) return false;
  const temPar = (walking: string, running: string) =>
    !walking || !prontas.has(walking) || prontas.has(running);
  for (const layer of camadasDaCloth(cloth)) {
    if (!temPar(jsonCloth(layer, '', 'walking'), jsonCloth(layer, '', 'running'))) return false;
  }
  const cabelo = estiloDaFolha(hairStyle);
  if (cabelo && !temPar(jsonCabelo(cabelo, '', 'walking'), jsonCabelo(cabelo, '', 'running'))) return false;
  const barba = !feminino && estiloValido(beardStyle) ? estiloDaFolha(beardStyle) : '';
  if (barba && !temPar(jsonBarba(barba, '', 'walking'), jsonBarba(barba, '', 'running'))) return false;
  const arma = animDaArma(weapon)?.overlay ?? '';
  if (arma && !prontas.has(jsonArma(arma, 'running', ''))) return false;
  const escudo = pastaDoEscudo(shield);
  if (escudo && !prontas.has(jsonArma(escudo, 'running', ''))) return false;
  return true;
}

function folhaIdleParada(skin: string, mount: string): Folha {
  const idle = pegarFolha(skin, mount, 'idle');
  if (idle && idle.frames > IDLE_HOLD_QUADRO) return folhaParada(idle, IDLE_HOLD_QUADRO);
  return folhaIdle(skin, mount);
}

function folhasClothIdle(look: LookDoBoneco = lookDoYou()): Folha[] {
  if (look.dead || !look.cloth || look.skin === SKIN_PADRAO) return [];
  const out: Folha[] = [];
  for (const kind of camadasDaCloth(look.cloth)) {
    // O chapéu vai depois do cabelo; a capa, atrás do corpo quando ele olha para a câmera (paintDoll).
    if (ehCamadaCabeca(kind) || (ehCapa(kind) && capaAtras(dirDoBoneco))) continue;
    const idle = pegarCloth(kind, '', 'idle');
    if (!idle) continue;
    const parada = idle.frames > IDLE_HOLD_QUADRO ? folhaParada(idle, IDLE_HOLD_QUADRO) : idle;
    out.push(tingirCamada(parada, kind));
  }
  return out;
}

function blitClothIdle(pd: Ctx2D, ox: number, oy: number, scale: number, ancoraX: number, ancoraY: number, look?: LookDoBoneco){
  for (const camada of folhasClothIdle(look)) {
    const rArt = arteDa(camada, dirDoBoneco, 0);
    if (!rArt) continue;
    desenharQuadro(
      pd, rArt,
      ox + (ancoraX - camada.ancoraX) * scale,
      oy + (ancoraY - camada.ancoraY) * scale,
      camada.cellW * scale,
      camada.cellH * scale
    );
  }
}

function blitIdleOverlay(pd: Ctx2D, ox: number, oy: number, scale: number, ancoraX: number, ancoraY: number, folha?: Folha){
  if (!folha) return;
  const wArt = arteDa(folha, dirDoBoneco, 0);
  if (!wArt) return;
  desenharQuadro(
    pd, wArt,
    ox + (ancoraX - folha.ancoraX) * scale,
    oy + (ancoraY - folha.ancoraY) * scale,
    folha.cellW * scale,
    folha.cellH * scale
  );
}

function blitWeaponIdle(pd: Ctx2D, ox: number, oy: number, scale: number, ancoraX: number, ancoraY: number, weapon: string, mount: string, horse: boolean){
  if (!weapon) return;
  const acao = horse ? animDaArma(weapon)?.idleHorse : animDaArma(weapon)?.idleFoot;
  blitIdleOverlay(pd, ox, oy, scale, ancoraX, ancoraY, folhaDaArma(weapon, mount, acao));
}

function blitShieldIdle(pd: Ctx2D, ox: number, oy: number, scale: number, ancoraX: number, ancoraY: number, mount: string, shield = youShield){
  blitIdleOverlay(pd, ox, oy, scale, ancoraX, ancoraY, folhaDoEscudo(shield, mount, 'idle'));
}

function pintarRoupa(
  g: Ctx2D, skin: string, cloth: string, mount: string, dir: number,
  moving: boolean, gait: number, now: number, rx: number, ry: number,
  once: boolean, bodyFolha?: Folha, holdQuadro?: number
) {
  if (!cloth || skin === SKIN_PADRAO) return;
  const camadas = camadasDaCloth(cloth);
  if (camadas.length === 0) return;
  if (camadas.length > 1 || camadas[0] !== cloth) {
    for (const layer of camadas) {
      pintarRoupa(g, skin, layer, mount, dir, moving, gait, now, rx, ry, once, bodyFolha, holdQuadro);
    }
    return;
  }
  const bodyMount = ehMontaria(mount) ? 'horse' : '';
  const acao = moving
    ? acaoAndando(bodyFolha)
    : (bodyFolha ? acaoDaFolha(bodyFolha) : 'idle');
  const camada = pegarCloth(cloth, bodyMount, acao)
    ?? pegarCloth(cloth, bodyMount, moving ? 'walking' : 'idle');
  if (!camada) return;
  let sync = bodyFolha && camada.fps !== bodyFolha.fps
    ? { ...camada, fps: bodyFolha.fps }
    : camada;
  if (!moving && holdQuadro !== undefined && sync.frames > 1) {
    sync = folhaParada(sync, holdQuadro);
  }
  desenharFolha(g, tingirCamada(sync, cloth), dir, moving, gait, now, rx, ry, zoom, once);
}

/** A capa que vai atrás do corpo: pinta antes dele, com as mesmas contas da roupa. */
function pintarCapaAtras(
  g: Ctx2D, skin: string, cloth: string, mount: string, dir: number,
  moving: boolean, gait: number, now: number, rx: number, ry: number,
  once: boolean, bodyFolha?: Folha, holdQuadro?: number
) {
  const capa = capaDeTras(cloth, dir);
  if (capa) pintarRoupa(g, skin, capa, mount, dir, moving, gait, now, rx, ry, once, bodyFolha, holdQuadro);
}

function pintarCamadaEstilo(
  g: Ctx2D, pegar: (estilo: string, mount: string, acao: string) => Folha | undefined,
  estilo: string, hue: number, mount: string, dir: number,
  moving: boolean, gait: number, now: number, rx: number, ry: number,
  once: boolean, bodyFolha?: Folha, holdQuadro?: number
) {
  const id = estiloDaFolha(estilo);
  if (!id) return;
  const bodyMount = ehMontaria(mount) ? 'horse' : '';
  const acao = moving ? acaoAndando(bodyFolha) : (bodyFolha ? acaoDaFolha(bodyFolha) : 'idle');
  const folha = pegar(id, bodyMount, acao) ?? pegar(id, bodyMount, moving ? 'walking' : 'idle');
  if (!folha) return;
  let sync = bodyFolha && folha.fps !== bodyFolha.fps ? { ...folha, fps: bodyFolha.fps } : folha;
  if (!moving && holdQuadro !== undefined && sync.frames > 1) {
    sync = folhaParada(sync, holdQuadro);
  }
  desenharFolha(g, tingirFolhaCabelo(sync, hue), dir, moving, gait, now, rx, ry, zoom, once);
}

function pintarCabelo(
  g: Ctx2D, estilo: string, hue: number, mount: string, dir: number,
  moving: boolean, gait: number, now: number, rx: number, ry: number,
  once: boolean, bodyFolha?: Folha, holdQuadro?: number
) {
  pintarCamadaEstilo(g, pegarCabelo, estilo, hue, mount, dir, moving, gait, now, rx, ry, once, bodyFolha, holdQuadro);
}

function pintarBarba(
  g: Ctx2D, estilo: string, hue: number, feminino: boolean, mount: string, dir: number,
  moving: boolean, gait: number, now: number, rx: number, ry: number,
  once: boolean, bodyFolha?: Folha, holdQuadro?: number
) {
  if (feminino || !estiloValido(estilo)) return;
  pintarCamadaEstilo(g, pegarBarba, estilo, hue, mount, dir, moving, gait, now, rx, ry, once, bodyFolha, holdQuadro);
}

function pintarAposCorpo(
  g: Ctx2D, skin: string, cloth: string, mount: string, dir: number,
  moving: boolean, gait: number, now: number, rx: number, ry: number,
  once: boolean, bodyFolha: Folha | undefined, holdQuadro: number | undefined,
  hairStyle: string, hairHue: number, beardStyle: string, beardHue: number, feminino: boolean,
  shield: string, weapon: string, acaoEscudo: string, acaoArma: string | undefined,
  armaOnce: boolean, armaMoving: boolean, armaGait: number
) {
  pintarRoupa(g, skin, clothSemElmo(cloth, dir), mount, dir, moving, gait, now, rx, ry, once, bodyFolha, holdQuadro);
  // O manto com capuz (Manto do GM, Manto da Morte) cobre a cabeça: sem cabelo, chapéu ou elmo por cima.
  const capuz = capuzNoFio(cloth);
  // O fantasma (pele ghost) já vem com o robe e o capuz na própria folha: nem cabelo nem barba por cima dele.
  const fantasma = skin === SKIN_PADRAO;
  if (!capuz && !fantasma) pintarCabelo(g, hairStyle, hairHue, mount, dir, moving, gait, now, rx, ry, once, bodyFolha, holdQuadro);
  if (!fantasma) pintarBarba(g, beardStyle, beardHue, feminino, mount, dir, moving, gait, now, rx, ry, once, bodyFolha, holdQuadro);
  const elmo = capuz ? '' : soElmo(cloth);
  if (elmo) pintarRoupa(g, skin, elmo, mount, dir, moving, gait, now, rx, ry, once, bodyFolha, holdQuadro);
  // Escudo por cima da arma: a lâmina não fura o disco.
  if (weapon && acaoArma) pintarArma(g, weapon, mount, now, dir, rx, ry, acaoArma, armaOnce, armaMoving, armaGait);
  pintarEscudo(g, shield, mount, now, dir, rx, ry, acaoEscudo, armaOnce, armaMoving, armaGait);
}

function folhaIdle(skin: string, mount: string): Folha {
  return pegarFolha(skin, mount, 'idle')
    ?? pegarFolha(SKIN_PADRAO, mount, 'idle')
    ?? vazia;
}

function folhasIdle(skin: string, mount: string): { hold?: Folha; fidget?: Folha } {
  const idle = pegarFolha(skin, mount, 'idle');
  return { hold: idle, fidget: idle && idle.frames > 1 ? idle : undefined };
}

function folhaAndar(skin: string, mount: string): Folha {
  return pegarFolha(skin, mount, 'walking')
    ?? pegarFolha(SKIN_PADRAO, mount, 'walking')
    ?? folhaIdle(skin, mount);
}

function folhaHorse(moving: boolean, kind?: string): Folha | undefined {
  const id = kind && ehMontaria(kind) ? kind : MONTARIA_PADRAO;
  return prontas.get(jsonAnimal(id, moving))
    ?? prontas.get(jsonAnimal(id, false));
}

function pegarArma(stem: string, acao: string, mount: string): Folha | undefined {
  const url = jsonArma(stem, acao, mount);
  const hit = url ? prontas.get(url) : undefined;
  if (hit) return hit;
  // Sem folha a pé, cai na de cavalo.
  if (mount !== 'horse') {
    const horse = jsonArma(stem, acao, 'horse');
    return horse ? prontas.get(horse) : undefined;
  }
  return undefined;
}

function folhaDaArma(weapon: string, mount: string, acao?: string): Folha | undefined {
  const mounted = ehMontaria(mount);
  const ficha = animDaArma(weapon);
  const stem = ficha?.overlay ?? '';
  if (!stem) return undefined;
  const pose = acao
    ?? (mounted ? (ficha?.overlayHit ?? 'attack') : (ficha?.overlayHit ?? 'attack'));
  const folha = pegarArma(stem, pose, mounted ? 'horse' : '');
  // A variante (`bow@oak`) usa a folha da base, multiplicada pela cor do material.
  return folha ? tingirFolhaMaterial(folha, materialDoItem(weapon)) : undefined;
}

/** O escudo no fio vira o id do item (`heater@golden`): a pasta sai da base, a cor do material. */
function escudoDoFio(defId: string | null | undefined): string {
  return pastaDoEscudo(defId) ? (defId ?? '').trim() : '';
}

function folhaDoEscudo(shield: string, mount: string, acao: string): Folha | undefined {
  const folha = folhaDoEscudoCrua(shield, mount, acao);
  return folha ? tingirFolhaMaterial(folha, materialDoItem(shield)) : undefined;
}

function folhaDoEscudoCrua(shield: string, mount: string, acao: string): Folha | undefined {
  const stem = pastaDoEscudo(shield);
  if (!stem || !acao) return undefined;
  const horse = ehMontaria(mount) ? 'horse' : '';
  const hit = pegarArma(stem, acao, horse);
  if (hit) return hit;
  const url = jsonArma(stem, acao, horse);
  if (url && !prontas.has(url)) {
    void carregarFolha(url).then(f => {
      if (atlasDead) return;
      prontas.set(url, f);
      paintDolls();
    }).catch(() => {});
  }
  return pegarArma(stem, 'idle', horse);
}

function pintarArma(
  g: Ctx2D, weapon: string, mount: string,
  elapsed: number, dir: number, rx: number, ry: number,
  acao?: string, once = true, moving = false, gait = 0
) {
  const folha = folhaDaArma(weapon, mount, acao);
  if (!folha) return;
  desenharFolha(g, folha, dir, moving, gait, elapsed, rx, ry, zoom, once);
}

function pintarEscudo(
  g: Ctx2D, shield: string, mount: string,
  elapsed: number, dir: number, rx: number, ry: number,
  acao: string, once = true, moving = false, gait = 0
) {
  const folha = folhaDoEscudo(shield, mount, acao);
  if (!folha) return;
  desenharFolha(g, folha, dir, moving, gait, elapsed, rx, ry, zoom, once);
}

/**
 * Staff invisível, como no UO: o corpo cinza (Renato, 25/10/2026). Vale para o próprio personagem, para o boneco do
 * paperdoll e para o GM oculto que outra staff ainda enxerga.
 */
const FILTRO_OCULTO = 'grayscale(1) contrast(0.55) brightness(1.1)';

/** Desenha com o filtro do oculto quando `ligado`; devolve o que o desenho devolver (a altura da cabeça). */
function comOculto(g: Ctx2D, ligado: boolean | undefined, desenhar: () => number): number {
  if (!ligado) return desenhar();
  const antes = g.filter;
  g.filter = FILTRO_OCULTO;
  try {
    return desenhar();
  } finally {
    g.filter = antes;
  }
}

function blitAvatar(
  g: Ctx2D, skin: string, hue: number, mount: string, cloth: string, dir: number, moving: boolean,
  gait: number, now: number, rx: number, ry: number, idleAt: number, attackAt = 0,
  weapon = '', shield = '',
  hairStyle = '', hairHue = 0, beardStyle = '', beardHue = 0, feminino = false,
  correndo = false, conj: Conjuracao | null = null, sentado: PoseSentada | null = null
): number {
  const bodyMount = ehMontaria(mount) ? 'horse' : '';
  const roupa = skin === SKIN_PADRAO ? '' : cloth;
  weapon = armaDoVisual(weapon, roupa);
  const fem = femininoDo(skin, feminino);
  const corpo = (folha: Folha) => tingirFolha(folha, hue);
  const elapsed = attackAt > 0 ? now - attackAt : -1;
  const ficha = animDaArma(weapon);
  // Sem arma na mão, a pé: o soco da UO (Wrestle, grupo 31). Montado, o golpe do cavaleiro de sempre.
  const acaoHit = bodyMount
    ? (ficha?.horseBody ?? 'attack-slash')
    : (ficha?.foot ?? (weapon ? 'attack-slash-2h' : 'attack-wrestle'));
  const swing = elapsed >= 0 ? pegarFolha(skin, bodyMount, acaoHit) : undefined;
  if (swing && elapsed >= 0 && elapsed < msDaFolha(swing)) {
    const rider = corpo(swing);
    if (ehMontaria(mount)) {
      const horse = folhaHorse(false, mount);
      if (horse) {
        desenharFolha(g, horse, dir, false, 0, now, rx, ry, zoom);
        pintarCapaAtras(g, skin, roupa, mount, dir, false, 0, elapsed, rx, ry, true, rider, undefined);
        desenharFolha(g, rider, dir, false, 0, elapsed, rx, ry, zoom, true);
        pintarAposCorpo(
          g, skin, roupa, mount, dir, false, 0, elapsed, rx, ry, true, rider, undefined,
          hairStyle, hairHue, beardStyle, beardHue, fem,
          shield, weapon, acaoHit, ficha?.overlayHit ?? 'attack', true, false, 0);
        return Math.max(horse.ancoraY, rider.ancoraY);
      }
    }
    pintarCapaAtras(g, skin, roupa, '', dir, false, 0, elapsed, rx, ry, true, rider, undefined);
    desenharFolha(g, rider, dir, false, 0, elapsed, rx, ry, zoom, true);
    pintarAposCorpo(
      g, skin, roupa, '', dir, false, 0, elapsed, rx, ry, true, rider, undefined,
      hairStyle, hairHue, beardStyle, beardHue, fem,
      shield, weapon, acaoHit, ficha?.overlayHit ?? 'attack', true, false, 0);
    return rider.ancoraY;
  }

  // Conjurando, parado e a pé (techspec-magias-animacoes): o gesto da UO toca uma vez a cada 1,5 s, como no RunUO.
  // A arma fica fora do gesto (as folhas de arma não têm a pose); o escudo e as roupas acompanham.
  if (conj && !moving && !bodyMount) {
    const acao = conj.area ? 'spell-area' : 'spell';
    const gesto = pegarFolha(skin, '', acao);
    const passou = (now - conj.desde) % CICLO_GESTO_MS;
    if (gesto && passou < msDaFolha(gesto)) {
      const body = corpo(gesto);
      pintarCapaAtras(g, skin, roupa, '', dir, false, 0, passou, rx, ry, true, body, undefined);
      desenharFolha(g, body, dir, false, 0, passou, rx, ry, zoom, true);
      pintarAposCorpo(
        g, skin, roupa, '', dir, false, 0, passou, rx, ry, true, body, undefined,
        hairStyle, hairHue, beardStyle, beardHue, fem,
        shield, weapon, acao, undefined, true, false, 0);
      return body.ancoraY;
    }
  }

  if (moving) {
    if (ehMontaria(mount)) {
      const horse = folhaHorse(true, mount);
      const rider = corpo(folhaAndar(skin, 'horse'));
      if (horse) {
        desenharFolha(g, horse, dir, true, gait, now, rx, ry, zoom);
        pintarCapaAtras(g, skin, roupa, mount, dir, true, gait, now, rx, ry, false, rider, undefined);
        desenharFolha(g, rider, dir, true, gait, now, rx, ry, zoom);
        pintarAposCorpo(
          g, skin, roupa, mount, dir, true, gait, now, rx, ry, false, rider, undefined,
          hairStyle, hairHue, beardStyle, beardHue, fem,
          shield, weapon, 'walking', animDaArma(weapon)?.walkHorse, false, true, gait);
        return Math.max(horse.ancoraY, rider.ancoraY);
      }
      pintarCapaAtras(g, skin, roupa, mount, dir, true, gait, now, rx, ry, false, rider, undefined);
      desenharFolha(g, rider, dir, true, gait, now, rx, ry, zoom);
      pintarAposCorpo(
        g, skin, roupa, mount, dir, true, gait, now, rx, ry, false, rider, undefined,
        hairStyle, hairHue, beardStyle, beardHue, fem,
        shield, weapon, 'walking', undefined, false, true, gait);
      return rider.ancoraY;
    }
    // Corrida a pé: grupo 2 da UO. Sem todas as camadas prontas, anda (podeCorrer).
    const corre = correndo && podeCorrer(skin, roupa, hairStyle, beardStyle, fem, weapon, shield);
    const correr = corre ? prontas.get(jsonPersonagem(skin, '', 'running')) : undefined;
    const body = corpo(correr ?? folhaAndar(skin, ''));
    pintarCapaAtras(g, skin, roupa, '', dir, true, gait, now, rx, ry, false, body, undefined);
    desenharFolha(g, body, dir, true, gait, now, rx, ry, zoom);
    pintarAposCorpo(
      g, skin, roupa, '', dir, true, gait, now, rx, ry, false, body, undefined,
      hairStyle, hairHue, beardStyle, beardHue, fem,
      shield, weapon, correr ? 'running' : 'walking', correr ? 'running' : animDaArma(weapon)?.walkFoot, false, true, gait);
    return body.ancoraY;
  }

  // Sentado (techspec-sentar): parado, a pé e com corpo (o fantasma não senta).
  if (sentado && !bodyMount && skin !== SKIN_PADRAO) {
    const h = blitSentado(g, skin, hue, roupa, sentado, now, rx, ry, idleAt, weapon, shield, hairStyle, hairHue, beardStyle, beardHue, fem);
    if (h !== null) return h;
  }

  const picked = folhasIdle(skin, bodyMount);
  const hold = picked.hold
    ?? folhasIdle(SKIN_PADRAO, bodyMount).hold;
  const fidget = picked.hold
    ? picked.fidget
    : folhasIdle(SKIN_PADRAO, bodyMount).fidget;
  const pick = cicloIdle(hold, fidget, idleAt, now, hold ?? vazia);

  if (ehMontaria(mount)) {
    const horse = folhaHorse(false, mount);
    const rider = corpo(pick.folha);
    if (horse) {
      desenharFolha(g, horse, dir, false, gait, now, rx, ry, zoom);
      pintarCapaAtras(g, skin, roupa, mount, dir, false, gait, pick.now, rx, ry, pick.once, pick.folha, pick.holdQuadro);
      desenharFolha(g, rider, dir, false, gait, pick.now, rx, ry, zoom, pick.once);
      pintarAposCorpo(
        g, skin, roupa, mount, dir, false, gait, pick.now, rx, ry, pick.once, pick.folha, pick.holdQuadro,
        hairStyle, hairHue, beardStyle, beardHue, fem,
        shield, weapon, 'idle', animDaArma(weapon)?.idleHorse, pick.once, false, gait);
      return Math.max(horse.ancoraY, rider.ancoraY);
    }
    pintarCapaAtras(g, skin, roupa, mount, dir, false, gait, pick.now, rx, ry, pick.once, pick.folha, pick.holdQuadro);
    desenharFolha(g, rider, dir, false, gait, pick.now, rx, ry, zoom, pick.once);
    pintarAposCorpo(
      g, skin, roupa, mount, dir, false, gait, pick.now, rx, ry, pick.once, pick.folha, pick.holdQuadro,
      hairStyle, hairHue, beardStyle, beardHue, fem,
      shield, weapon, 'idle', undefined, pick.once, false, gait);
    return rider.ancoraY;
  }
  const body = corpo(pick.folha);
  pintarCapaAtras(g, skin, roupa, '', dir, false, gait, pick.now, rx, ry, pick.once, pick.folha, pick.holdQuadro);
  desenharFolha(g, body, dir, false, gait, pick.now, rx, ry, zoom, pick.once);
  pintarAposCorpo(
    g, skin, roupa, '', dir, false, gait, pick.now, rx, ry, pick.once, pick.folha, pick.holdQuadro,
    hairStyle, hairHue, beardStyle, beardHue, fem,
    shield, weapon, 'idle', animDaArma(weapon)?.idleFoot, pick.once, false, gait);
  return body.ancoraY;
}

/* ── Sentar (techspec-sentar): como o cliente da UO desenha quem está numa cadeira (ver world/sentar.ts). ── */
const telaSentado = document.createElement('canvas');
const gSentado = telaSentado.getContext('2d')!;
/** O alto e o pé do corpo (as linhas com pixel) em cada quadro: as faixas da cintura e do joelho saem daí. */
const corpoDoQuadro = new Map<string, { topo: number; base: number } | null>();
const sondaCorpo = document.createElement('canvas');

function linhasDoCorpo(folha: Folha, dir: number): { topo: number; base: number } | null {
  const chave = `${folha.url}|${dir}`;
  if (corpoDoQuadro.has(chave)) return corpoDoQuadro.get(chave)!;
  const q = arteDa(folha, dir, 0);
  let r: { topo: number; base: number } | null = null;
  if (q) {
    try {
      sondaCorpo.width = q.w;
      sondaCorpo.height = q.h;
      const sg = sondaCorpo.getContext('2d', { willReadFrequently: true })!;
      sg.clearRect(0, 0, q.w, q.h);
      sg.drawImage(q.fonte, q.sx, q.sy, q.w, q.h, 0, 0, q.w, q.h);
      const px = sg.getImageData(0, 0, q.w, q.h).data;
      let topo = -1, base = -1;
      for (let y = 0; y < q.h; y++) {
        let tem = false;
        for (let x = 0; x < q.w && !tem; x++) tem = px[(y * q.w + x) * 4 + 3] > 0;
        if (tem) { if (topo < 0) topo = y; base = y; }
      }
      if (topo >= 0) r = { topo, base };
    } catch {
      r = null; // imagem que não deixa ler (outra origem): fica de pé
    }
  }
  corpoDoQuadro.set(chave, r);
  return r;
}

/**
 * Desenha o personagem sentado. De costas: a pose do cavaleiro parado sem o cavalo. De frente: o personagem de pé,
 * com tudo o que veste, numa tela à parte, e daí para o mapa em três faixas (tronco deslizado, coxa inclinada, canela
 * encolhida). Devolve a altura da cabeça, ou `null` quando a folha ainda não chegou (fica de pé).
 */
function blitSentado(
  g: Ctx2D, skin: string, hue: number, roupa: string, pose: PoseSentada, now: number, rx: number, ry: number,
  idleAt: number, weapon: string, shield: string,
  hairStyle: string, hairHue: number, beardStyle: string, beardHue: number, fem: boolean
): number | null {
  const x0 = rx + pose.dx * zoom, y0 = ry + pose.dy * zoom;
  const dir = pose.dir;
  if (pose.costas) {
    const montaria = MONTARIA_PADRAO; // só escolhe a pose do cavaleiro: o cavalo não é desenhado
    const picked = folhasIdle(skin, 'horse');
    if (!picked.hold) return null;
    const pick = cicloIdle(picked.hold, picked.fidget, idleAt, now, picked.hold);
    pintarCapaAtras(g, skin, roupa, montaria, dir, false, 0, pick.now, x0, y0, pick.once, pick.folha, pick.holdQuadro);
    desenharFolha(g, tingirFolha(pick.folha, hue), dir, false, 0, pick.now, x0, y0, zoom, pick.once);
    pintarAposCorpo(
      g, skin, roupa, montaria, dir, false, 0, pick.now, x0, y0, pick.once, pick.folha, pick.holdQuadro,
      hairStyle, hairHue, beardStyle, beardHue, fem,
      shield, weapon, 'idle', animDaArma(weapon)?.idleHorse, pick.once, false, 0);
    return pick.folha.ancoraY - pose.dy;
  }
  // De frente: o quadro parado de pé (o grupo 4, quadro 0, como no cliente).
  const folha = pegarFolha(skin, '', 'idle');
  if (!folha) return null;
  const linhas = linhasDoCorpo(folha, dir);
  if (!linhas) return null;
  const w = Math.ceil(folha.cellW * zoom * 1.6), h = Math.ceil(folha.cellH * zoom * 1.4);
  if (telaSentado.width !== w || telaSentado.height !== h) {
    telaSentado.width = w;
    telaSentado.height = h;
  } else {
    gSentado.clearRect(0, 0, w, h);
  }
  gSentado.imageSmoothingEnabled = g.imageSmoothingEnabled;
  const ox = Math.round(w / 2), oy = Math.round(h * 0.85);
  pintarCapaAtras(gSentado, skin, roupa, '', dir, false, 0, 0, ox, oy, false, folha, 0);
  desenharFolha(gSentado, tingirFolha(folha, hue), dir, false, 0, 0, ox, oy, zoom);
  pintarAposCorpo(
    gSentado, skin, roupa, '', dir, false, 0, 0, ox, oy, false, folha, 0,
    hairStyle, hairHue, beardStyle, beardHue, fem,
    shield, weapon, 'idle', animDaArma(weapon)?.idleFoot, false, false, 0);
  // As linhas do corpo, na tela à parte: o pixel da âncora cai em (ox, oy).
  const topo = oy + (linhas.topo - folha.ancoraY) * zoom;
  const altura = (linhas.base - linhas.topo + 1) * zoom;
  const bx = Math.round(x0 - ox), by = Math.round(y0 - oy);
  for (const f of faixasSentado(topo, altura, pose.encosto * zoom)) {
    if (f.srcH <= 0 || f.dstH <= 0) continue;
    g.save();
    g.transform(1, 0, (f.desliza1 - f.desliza0) / f.dstH, 1, bx + f.desliza0, by + f.dstY);
    g.drawImage(telaSentado, 0, f.srcY, w, f.srcH, 0, 0, w, f.dstH);
    g.restore();
  }
  return folha.ancoraY - pose.dy;
}

type RemoteDoMapa = typeof remotes extends Map<number, infer R> ? R : never;

/** O outro jogador parado num assento: senta como o seu (só quem tem corpo de gente e está a pé). */
function sentadoDoRemoto(r: RemoteDoMapa, rp: { x: number; y: number }, agora: number) {
  if (r.kind && r.kind !== 'player') return null;
  if (r.dead || ehMontaria(r.mount) || agora - (r.moveStamp || 0) < 240) return null;
  return sentadoEm(Math.round(rp.x), Math.round(rp.y), zDoRemoto(r.id, rp), r.dir);
}

/** A pose de quem está parado em (x, y, z) olhando para `dir`, se ali há um assento. */
function sentadoEm(x: number, y: number, z: number, dir: number): (PoseSentada & { prio: number }) | null {
  const a = assentoEm(mapa.id, x, y, z);
  const pose = a ? poseSentada(a.dados, dir) : null;
  return pose && a ? { ...pose, prio: a.z + a.altura + 1 } : null;
}

function tickIdleClock(moving: boolean, now: number, idleAt: number, wasMoving: boolean){
  if (moving) return { idleAt, wasMoving: true };
  if (wasMoving) return { idleAt: now, wasMoving: false };
  return { idleAt, wasMoving: false };
}

function applyYouMount(defId: string | null | undefined){
  const antes = youMount;
  youMount = youDead ? '' : (ehMontaria(defId) ? (defId ?? '').trim() : '');
  if (youMount) ensureAnimal(youMount);
  if (youMount && youMount !== antes && performance.now() > somDeEquipDesde) {
    const sons = sonsDaMontaria(youMount);
    if (sons) tocarUo(sons[OCIOSO], 'criaturas', { prioridade: true });
  }
  paintDolls();
}

function applyYouCloth(defId: string | null | undefined){
  youCloth = youDead ? '' : fioDoVisual(defId);
  if (youCloth) ensureCloth(youCloth);
  paintDolls();
}

function applyYouWeapon(defId: string | null | undefined){
  if (defId === undefined) return;
  const next = youDead ? '' : (defId ?? '').trim();
  const mudou = youWeapon !== next;
  youWeapon = next;
  if (youWeapon) ensureArma(youWeapon);
  if (mudou && youWeapon && performance.now() > somDeEquipDesde) tocarUo(ITEM.equiparArma, 'combate', { prioridade: true });
  if (mudou) opts.onMainHand?.(youWeapon || null);
  paintDolls();
}

function applyYouShield(defId: string | null | undefined){
  if (defId === undefined) return;
  youShield = youDead ? '' : escudoDoFio(defId);
  if (youShield) ensureEscudo(youShield);
  paintDolls();
}

let dieSfx = false;

function cueDie(){
  if (dieSfx) return;
  dieSfx = true;
  playDie(femininoDoYou());
}

/* ── Sons do mundo (docs/proposta-sons.md §07) ──
 * A regra é a da UO: quem bate toca o som da arma (ou o ataque da espécie) e quem apanha toca a própria
 * dor (a da espécie, o "oomph" do gênero ou a madeira do manequim). O que é seu toca cheio; o resto cai
 * com a distância. */
const raivaTocada = new Map<number, number>();
const mobsPreCarregados = new Set<string>();
let passoAlterna = 0;
let passoEm = 0;
let ociosoEm = 0;
/** Montar e trocar de arma só tocam depois que a sala assentou (o login manda os dois). */
let somDeEquipDesde = performance.now() + 4000;

function femininoDoYou(){
  return femininoDo(youSkin, false);
}

/** Cheio até 3 casas, some em 14. */
function volDistancia(id: number): number {
  if (id === youId) return 1;
  const r = remotes.get(id);
  if (!r) return 0;
  const d = Math.max(Math.abs(r.toX - player.tx), Math.abs(r.toY - player.ty));
  if (d <= 3) return 1;
  if (d >= 14) return 0;
  return 1 - (d - 3) / 11;
}

function sonsDoMob(id: number): readonly number[] | null {
  const r = remotes.get(id);
  if (!r || r.kind !== 'monster') return null;
  const ap = r.aparencia || r.defId;
  const sons = MOB_SONS[ap] ?? null;
  if (sons && !mobsPreCarregados.has(ap)) {
    mobsPreCarregados.add(ap);
    prefetchUo(sons);
  }
  return sons;
}

function somDeDor(id: number, vol: number, prioridade: boolean){
  if (id === youId) { playHurt(femininoDoYou()); return; }
  const r = remotes.get(id);
  if (!r) return;
  const chave = `dor:${id}`;
  if (r.kind === 'dummy') { tocarUm(MANEQUIM, 'combate', { vol, chave, intervaloMs: 200, prioridade }); return; }
  const mob = sonsDoMob(id);
  if (mob) { tocarUo(mob[DOR], 'criaturas', { vol, chave, intervaloMs: 300, prioridade }); return; }
  if (r.kind === 'player' || r.kind === 'guard' || r.kind === 'npc')
    tocarUm(femininoDo(r.skin, r.feminino) ? DOR_F : DOR_M, 'combate', { vol, chave, intervaloMs: 300, prioridade });
}

function somDeMorte(id: number){
  const r = remotes.get(id);
  if (!r || r.kind === 'dummy') return;
  const vol = volDistancia(id);
  const mob = sonsDoMob(id);
  if (mob) { tocarUo(mob[MORTE], 'criaturas', { vol, prioridade: vol >= 1 }); return; }
  tocarUm(femininoDo(r.skin, r.feminino) ? MORTE_F : MORTE_M, 'combate', { vol });
}

/** O som de quem bate: o ataque da espécie, ou a arma (acerto ou erro). Magia já tem o próprio som. */
function somDoGolpe(s: NetStrike, vol: number, prioridade: boolean){
  if (s.kind !== 'melee' || !s.atacante) return;
  const a = s.atacante;
  const mob = sonsDoMob(a);
  if (mob) { tocarUo(mob[ATAQUE], 'criaturas', { vol, chave: `ataque:${a}`, intervaloMs: 250, prioridade }); return; }
  const arma = somDaArma(a === youId ? youWeapon : remotes.get(a)?.weapon);
  tocarUm(s.miss ? arma.erro : arma.acerto, 'combate', { vol, chave: `arma:${a}`, intervaloMs: 120, prioridade });
}

/** O mob que parte para cima de você solta a raiva, uma vez a cada 20 s. */
function somDeRaiva(id: number){
  const mob = sonsDoMob(id);
  if (!mob) return;
  const agora = performance.now();
  if (agora - (raivaTocada.get(id) ?? -Infinity) < 20000) return;
  raivaTocada.set(id, agora);
  tocarUo(mob[RAIVA], 'criaturas', { vol: volDistancia(id) });
}

/** De 8 a 20 s, um mob vivo perto de você solta o ocioso, baixo. */
function somOcioso(now: number){
  if (now < ociosoEm) return;
  ociosoEm = now + 8000 + Math.random() * 12000;
  const perto = [...remotes.values()].filter(r => r.kind === 'monster' && r.hits > 0 && !r.dead && volDistancia(r.id) > 0.3);
  if (perto.length === 0) return;
  const r = perto[(Math.random() * perto.length) | 0];
  const mob = sonsDoMob(r.id);
  if (mob) tocarUo(mob[OCIOSO], 'criaturas', { vol: volDistancia(r.id) * 0.6 });
}

/**
 * A coleta: o servidor manda um golpe (`act = 1`) a cada fatia da tentativa, com a picareta ou o machado na mão.
 * O som sai no quadro em que a ferramenta bate, não no começo do balanço: a metade da folha (a attack-slash-2h tem 7 quadros a 10 fps; os 3 primeiros erguem a ferramenta e o 4º desce).
 */
const IMPACTO_DO_GOLPE = 0.5;
function somDaFerramenta(arma: string | null | undefined, folhaMs: number, vol: number, prioridade: boolean){
  const id = (arma ?? '').split('@')[0];
  const minerio = id.includes('pickaxe');
  if (!minerio && !id.includes('hatchet')) return;
  if (vol <= 0.05) return;
  window.setTimeout(() => playColeta(minerio, vol, prioridade), Math.round(folhaMs * IMPACTO_DO_GOLPE));
}

/** Passos com o ritmo do ClassicUO: a pé 400 ms, montado 350 ms, galopando 150 ms. Fantasma não pisa. */
function somDePasso(now: number, galope: boolean){
  if (youDead) return;
  const montado = ehMontaria(youMount);
  const ids = montado && galope ? PASSO_GALOPE : PASSO;
  const ms = montado ? (galope ? PASSO_GALOPE_MS : PASSO_MONTADO_MS) : PASSO_MS;
  if (now - passoEm < ms) return;
  passoEm = now;
  passoAlterna ^= 1;
  tocarUo(ids[passoAlterna], 'passos', { vol: montado ? 0.8 : 0.6, prioridade: true });
}

/** Quando o último golpe em você foi escrito no diário (aplicarGolpe). */
let golpeEmVoceAt = 0;

function noteYouHits(prev: number, next: number){
  if (next >= prev) return;
  const n = prev - next;
  const ja = temGolpe(youId);
  // A vida do quadro chega antes dos golpes do mesmo quadro (e o "v" pode vir antes do quadro): escrever na hora
  // duplicava o dano no diário de combate. Espera o golpe; se ele não vier (dano sem golpe), escreve.
  if (n > 0 && !ja) {
    const marca = performance.now();
    window.setTimeout(() => {
      if (golpeEmVoceAt >= marca - 50) return;
      logKey('mundo.voceRecebeDano', { n }, 'warn');
    }, 300);
  }
  if (next <= 0) {
    logKey('mundo.voceMorre', undefined, 'warn');
    cueDie();
    return;
  }
  if (!ja) playHurt(femininoDoYou());
}

function cueRemoteDie(id: number){
  if (remoteDieSfx.has(id)) return;
  remoteDieSfx.add(id);
  somDeMorte(id);
}

function apagarRemote(id: number){
  const era = remotes.get(id);
  remotes.delete(id);
  calarBalao(id);
  localBrains.delete(id);
  remoteDieSfx.delete(id);
  dyingRemotes.delete(id);
  pendingVitals.delete(id);
  if (targetId !== id) return;
  if (warTargetId !== 0 && era && (era.kind === 'monster' || era.kind === 'player')) {
    const prox = proximoAlvoVivo(era.kind);
    if (prox != null) {
      targetId = prox;
      opts.onKeepAttack?.(prox);
      return;
    }
  }
  targetId = null;
}

function morteAindaVisivel(id: number, now: number){
  const r = remotes.get(id);
  if (!r || (r.kind !== 'monster' && r.kind !== 'corpse')) return false;
  if (r.morteEspera > 0) return true;
  if ((r.hits ?? 1) > 0 && r.deathAt <= 0) return false;
  if (r.deathAt <= 0) r.deathAt = now;
  const die = r.defId ? prontas.get(jsonInimigo(r.defId, false, false, true)) : undefined;
  const dur = die ? msDaFolha(die) : 650;
  return now - r.deathAt < dur;
}

const MORTE_ESPERA_MS = 900;
const SWING_ESPERA_MS = 700;

function pertoDoAlvoVisual(x: number, y: number){
  return chebyshev(player.x, player.y, x, y) <= 3
    || chebyshev(player.x, player.y, serverSelfX, serverSelfY) <= 1;
}

function revelarMorte(r: { hits: number; deathAt: number; morteEspera: number }, now: number){
  r.hits = 0;
  if (r.deathAt <= 0) r.deathAt = now;
  r.morteEspera = 0;
}

function segurarMorte(r: { kind: string; hits: number; toX: number; toY: number; morteEspera: number }, now: number){
  if (r.kind !== 'monster') return false;
  if (pertoDoAlvoVisual(r.toX, r.toY)) return false;
  r.morteEspera = r.morteEspera || now;
  if (r.hits <= 0) r.hits = 1;
  return true;
}

function soltarMortesPendentes(now: number){
  for (const r of remotes.values()){
    if (!r.morteEspera) continue;
    if (pertoDoAlvoVisual(r.toX, r.toY) || now - r.morteEspera > MORTE_ESPERA_MS) {
      const id = r.id;
      revelarMorte(r, now);
      cueRemoteDie(id);
    }
  }
}

function dispararSwingSelf(dir?: number | null, recoverMs?: number, desde?: number){
  youAttackAt = performance.now();
  const montado = ehMontaria(youMount);
  const armaVista = armaDoVisual(youWeapon, youSkin === SKIN_PADRAO ? '' : youCloth);
  const ficha = animDaArma(armaVista);
  // Sem arma: o soco (o tempo do golpe sai da mesma folha que o blitAvatar desenha).
  const acaoHit = montado
    ? (ficha?.horseBody ?? 'attack-slash')
    : (ficha?.foot ?? (armaVista ? 'attack-slash-2h' : 'attack-wrestle'));
  const swing = pegarFolha(youSkin, montado ? 'horse' : '', acaoHit);
  const folha = swing ? msDaFolha(swing) : (montado ? 417 : 583);
  const total = recoverMs && recoverMs > 0 ? recoverMs : folha;
  // Só na coleta: com a picareta na mão, o golpe de combate já tem o som do golpe.
  if (coletaTravada) somDaFerramenta(youWeapon, folha, 1, true);
  const gasto = desde ? Math.max(0, performance.now() - desde) : 0;
  opts.onSwingStarted?.(Math.max(0, total - gasto));
  if (dir != null) player.dir = dir & 7;
}

function talvezSwing(){
  if (!swingPendenteEm) return;
  if (chebyshev(player.x, player.y, serverSelfX, serverSelfY) <= 2
      || performance.now() - swingPendenteEm > SWING_ESPERA_MS) {
    dispararSwingSelf(swingPendenteDir, swingPendenteMs, swingPendenteEm);
    swingPendenteEm = 0;
    swingPendenteDir = null;
    swingPendenteMs = 0;
  }
}

/** Left no mesmo pacote do kill apagava o sprite antes da animação. */
function sairDoQuadro(id: number){
  const now = performance.now();
  if (morteAindaVisivel(id, now)) {
    dyingRemotes.add(id);
    return;
  }
  apagarRemote(id);
}

function soltarMortos(now: number){
  if (dyingRemotes.size === 0) return;
  for (const id of [...dyingRemotes]){
    if (morteAindaVisivel(id, now)) continue;
    apagarRemote(id);
  }
}

function noteRemoteHits(remote: { id: number; name: string; hits: number; kind?: string }, next: number){
  const n = remote.hits - next;
  const ja = temGolpe(remote.id);
  // No shard o servidor manda o aviso de dano. Sem isto o cliente inventa
  // "46 de dano" quando os vitais atrasam e a morte chega de uma vez.
  if (n > 0 && !net && !ja) {
    const agora = performance.now();
    const meu = targetId === remote.id
      || lastStrikeId === remote.id
      || (youAttackAt > 0 && agora - youAttackAt < 5000);
    if (meu) {
      const nome = remote.name || t('mundo.aventureiro');
      logKey('mundo.voceCausaDano', { n, nome }, 'note');
      if (next <= 0) logKey('mundo.voceMata', { nome }, 'note');
    }
  }
  if (remote.kind === 'monster' || remote.kind === 'corpse') return;
  if (next >= remote.hits) return;
  if (next <= 0) {
    cueRemoteDie(remote.id);
    return;
  }
  if (!ja) somDeDor(remote.id, volDistancia(remote.id), false);
}

function showFizzle(id: number){
  conjurando.delete(id);
  balloons.set(id, { text: t('balao.falhou'), until: performance.now() + SPEECH_MS });
  playFizzle();
  if (id === youId) {
    awaitingHit = false;
    logKey('mundo.voceFalhou', undefined, 'spell');
    return;
  }
  const name = remotes.get(id)?.name;
  if (name) logKey('mundo.alguemFalhou', { nome: name }, 'spell');
}

function spriteAtrasadoDoServidor(){
  if (player.moving || serverPath.length > 0) return true;
  return chebyshev(player.tx, player.ty, serverSelfX, serverSelfY) > 1;
}

function vestirVidaPendente(){
  const look = lifeLook;
  lifeLook = null;
  if (!look) return;
  applyYouMount(look.mount);
  if (look.cloth !== undefined) applyYouCloth(look.cloth);
  if (look.weapon !== undefined) applyYouWeapon(look.weapon);
  if (look.shield !== undefined) applyYouShield(look.shield);
  if (look.skin) applyYouSkin(look.skin);
}

function applyYouDead(dead?: boolean, silent = false, force = false){
  const next = !!dead;
  if (next) {
    lifeHeld = false;
    lifeLook = null;
  } else if (!force && youDead && spriteAtrasadoDoServidor()) {
    // O servidor já está no raio; o sprite da viagem do botão ainda não.
    lifeHeld = true;
    return;
  }
  lifeHeld = false;
  if (next === youDead) return;
  if (next) {
    if (silent) dieSfx = true;
    else cueDie();
  } else {
    if (youDead && !silent) playAnkh();
    dieSfx = false;
  }
  youDead = next;
  youIdleAt = performance.now();
  youWasMoving = false;
  // Fantasma não abre portal. Sem isto, a viagem do Reviver pisa no
  // portal (Minoc 71,56), trava o WASD e o lock sobrevive ao ApplyLife.
  passoTravado = false;
  portalSobre = null;
  if (youDead) {
    youMount = '';
    youCloth = '';
    youSkin = SKIN_PADRAO;
    ensureSkin(youSkin);
    applyYouWeapon(null);
    applyYouShield(null);
  } else {
    vestirVidaPendente();
  }
  opts.onDead?.(youDead);
  paintDolls();
}

function soltarVidaPendente(){
  if (!lifeHeld) return;
  const [kx, ky] = keyDelta();
  if (!kx && !ky && spriteAtrasadoDoServidor()) return;
  // WASD no meio da interpolação revivia no tile do sprite — em Minoc isso
  // é o portal do vilarejo (71,56) e o WASD trava sem o gump aparecer.
  if (spriteAtrasadoDoServidor()) snapSelf(serverSelfX, serverSelfY);
  applyYouDead(false, false, true);
}

let atlasDead = false;
teardown.push(() => { atlasDead = true; });

function applyYouSkin(raw?: string){
  const next = youDead ? SKIN_PADRAO : ((raw ?? '').trim() || SKIN_PADRAO);
  if (next === youSkin) return;
  youSkin = next;
  ensureSkin(youSkin);
  paintDolls();
}

function applyYouHair(style?: string, hue?: number, beard?: string, beardHue?: number, feminino?: boolean){
  youHairStyle = estiloDaFolha(style ?? '');
  youHairHue = hue ?? 0;
  youBeardStyle = femininoDo(youSkin, feminino) ? '' : estiloDaFolha(beard ?? '');
  youBeardHue = beardHue ?? 0;
  if (youHairStyle) ensureCabelo(youHairStyle);
  if (youBeardStyle) ensureBarba(youBeardStyle);
  paintDolls();
}

function applyYouSkinHue(raw?: number){
  if (!peleValida(raw) || raw === youSkinHue) return;
  youSkinHue = raw;
  paintDolls();
}

function ensureSkin(body: string){
  const id = (body ?? '').trim() || SKIN_PADRAO;
  for (const url of folhasDaSkin(id)) {
    if (prontas.has(url)) continue;
    void carregarFolha(url).then(f => {
      if (atlasDead) return;
      prontas.set(url, f);
      paintDolls();
    }).catch(() => {});
  }
}

function ensureMob(defId: string){
  const id = (defId ?? '').trim() || 'skeleton';
  for (const url of folhasDoInimigo(id)) {
    if (prontas.has(url)) continue;
    void carregarFolha(url).then(f => {
      if (atlasDead) return;
      prontas.set(url, f);
      // O retrato da placa do alvo espera a folha do bicho.
      if (retratosDeAlvo.size > 0) paintDolls();
    }).catch(() => {});
  }
}

function folhaInimigo(defId: string, moving: boolean, attack = false): Folha | undefined {
  return prontas.get(jsonInimigo(defId, moving, attack));
}

const ATTACK_MS = 1000 * 4 / 12;

function folhaCadaver(): Folha | undefined {
  return prontas.get(jsonPersonagem(PELE_CADAVER, '', 'die'));
}

function ensureDie(){
  const url = jsonPersonagem(PELE_CADAVER, '', 'die');
  if (prontas.has(url)) return;
  void carregarFolha(url).then(f => {
    if (atlasDead) return;
    prontas.set(url, f);
  }).catch(() => {});
}

function blitCadaver(
  g: Ctx2D, dir: number, rx: number, ry: number, deathAt: number, now: number
): number {
  const die = folhaCadaver();
  if (!die) {
    ensureDie();
    return 70;
  }
  if (deathAt > 0) {
    const elapsed = now - deathAt;
    if (elapsed < msDaFolha(die)) {
      desenharFolha(g, die, dir, false, 0, elapsed, rx, ry, zoom, true);
      return die.ancoraY;
    }
  }
  const hold = folhaParada(die, die.frames - 1);
  desenharFolha(g, hold, dir, false, 0, 0, rx, ry, zoom);
  return hold.ancoraY;
}

function blitMob(
  g: Ctx2D, defId: string, dir: number, moving: boolean,
  gait: number, now: number, rx: number, ry: number, attackAt = 0,
  deathAt = 0, hits = 1
): number {
  if (hits <= 0) {
    const die = prontas.get(jsonInimigo(defId, false, false, true));
    if (die) {
      if (deathAt > 0) {
        const elapsed = now - deathAt;
        if (elapsed < msDaFolha(die)) {
          desenharFolha(g, die, dir, false, 0, elapsed, rx, ry, zoom, true);
          return die.ancoraY;
        }
      }
      const hold = folhaParada(die, die.frames - 1);
      desenharFolha(g, hold, dir, false, 0, 0, rx, ry, zoom);
      return hold.ancoraY;
    }
  }
  const elapsed = attackAt > 0 ? now - attackAt : -1;
  if (elapsed >= 0 && elapsed < ATTACK_MS) {
    const swing = folhaInimigo(defId, false, true);
    if (swing) {
      desenharFolha(g, swing, dir, false, 0, elapsed, rx, ry, zoom, true);
      return swing.ancoraY;
    }
  }
  const folha = folhaInimigo(defId, moving) ?? folhaInimigo(defId, false);
  if (!folha) return 70;
  desenharFolha(g, folha, dir, moving, moving ? gaitDoMob(gait, folha.frames) : gait, now, rx, ry, zoom);
  return folha.ancoraY;
}

/**
 * Passada do mob (Renato, 02/11/2026): um ciclo inteiro do walk a cada 2 casas, como no cliente da UO (pé esquerdo e
 * pé direito). O quadroDa conta WALK_FRAMES_PER_TILE (1,35) quadros por casa, ritmo do jogador; no Esqueleto
 * (10 quadros, 700 ms por casa) isso dava ~2 quadros/s e o andar parecia em câmera lenta. Aqui a fase é convertida
 * para 5 quadros por casa (~7 quadros/s no Esqueleto).
 */
function gaitDoMob(gait: number, quadros: number){
  const porCasa = Math.max(WALK_FRAMES_PER_TILE, quadros / 2);
  return gait * porCasa / WALK_FRAMES_PER_TILE;
}

const DUMMY_JSON = '/sprites/itens/objects/dummy/dummy.json';

function ensureDummy(){
  if (prontas.has(DUMMY_JSON)) return;
  void carregarFolha(DUMMY_JSON).then(f => {
    if (atlasDead) return;
    prontas.set(DUMMY_JSON, f);
    if (retratosDeAlvo.size > 0) paintDolls();
  }).catch(() => {});
}

function varianteDummy(defId: string): number {
  return defId.includes('noroeste') ? 1 : 0;
}

function dummyCombate(defId: string | undefined): boolean {
  return (defId ?? '').includes('combate');
}

function ehDummyRemoto(r: { kind: string; defId: string }): boolean {
  return r.kind === 'dummy' || r.defId.startsWith('dummy-');
}

function dummyNoTile(x: number, y: number) {
  const tx = Math.round(x), ty = Math.round(y);
  return manequinsGuardados.find(m => m.x === tx && m.y === ty);
}

function dummyEhCombate(r: { defId: string; toX: number; toY: number }): boolean {
  if (dummyCombate(r.defId)) return true;
  return dummyNoTile(r.toX, r.toY)?.uso === 'combate';
}

function nomeDoManequim(name: string): string {
  return name.startsWith('manequim.') ? t(name) : name;
}

function blitDummy(
  g: Ctx2D, defId: string, now: number, rx: number, ry: number, attackAt: number
): number {
  // O Boneco de Punguista da casa (techspec-marceneiro-e-oficios-em-casa §06): a arte é a peça da casa.
  if (defId === 'dummy-casa') return 70;
  const folha = prontas.get(DUMMY_JSON);
  if (!folha) {
    ensureDummy();
    return 90;
  }
  const row = varianteDummy(defId);
  const elapsed = attackAt > 0 ? now - attackAt : -1;
  const swingMs = msDaFolha(folha);
  if (elapsed >= 0 && elapsed < swingMs) {
    desenharFolha(g, folha, row, false, 0, elapsed, rx, ry, zoom, true);
    return folha.ancoraY;
  }
  const hold = folhaParada(folha, 0);
  desenharFolha(g, hold, row, false, 0, 0, rx, ry, zoom);
  return hold.ancoraY;
}

function ensureCloth(cloth: string){
  for (const kind of camadasDaCloth(cloth)) {
    for (const url of folhasDaCloth(kind)) {
      if (prontas.has(url)) continue;
      void carregarFolha(url).then(f => {
        if (atlasDead) return;
        prontas.set(url, f);
        paintDolls();
      }).catch(() => {});
    }
  }
}

function ensureCabelo(estilo: string){
  const id = estiloDaFolha(estilo);
  if (!id) return;
  for (const url of folhasDoCabelo(id)) {
    if (prontas.has(url)) continue;
    void carregarFolha(url).then(f => {
      if (atlasDead) return;
      prontas.set(url, f);
      paintDolls();
    }).catch(() => {});
  }
}

function ensureBarba(estilo: string){
  const id = estiloDaFolha(estilo);
  if (!id) return;
  for (const url of folhasDaBarba(id)) {
    if (prontas.has(url)) continue;
    void carregarFolha(url).then(f => {
      if (atlasDead) return;
      prontas.set(url, f);
      paintDolls();
    }).catch(() => {});
  }
}

function ensureAnimal(kind: string){
  if (!ehMontaria(kind)) return;
  for (const moving of [false, true]) {
    const url = jsonAnimal(kind, moving);
    if (prontas.has(url)) continue;
    void carregarFolha(url).then(f => {
      if (atlasDead) return;
      prontas.set(url, f);
      paintDolls();
    }).catch(() => {});
  }
}

function ensureArma(defId: string){
  const ficha = animDaArma(defId);
  if (!ficha) return;
  const urls = [
    jsonArma(ficha.overlay, ficha.overlayHit, ''),
    jsonArma(ficha.overlay, ficha.idleFoot ?? '', ''),
    jsonArma(ficha.overlay, ficha.walkFoot ?? '', ''),
    jsonArma(ficha.overlay, ficha.overlayHit, 'horse'),
    jsonArma(ficha.overlay, ficha.idleHorse ?? '', 'horse'),
    jsonArma(ficha.overlay, ficha.walkHorse ?? '', 'horse'),
    jsonPersonagem(SKIN_PADRAO, '', ficha.foot),
    jsonPersonagem(youSkin !== SKIN_PADRAO ? youSkin : SKIN_PADRAO, '', ficha.foot),
    jsonPersonagem(SKIN_PADRAO, 'horse', ficha.horseBody),
    jsonPersonagem(youSkin !== SKIN_PADRAO ? youSkin : SKIN_PADRAO, 'horse', ficha.horseBody)
  ];
  for (const url of urls) {
    if (!url || prontas.has(url)) continue;
    void carregarFolha(url).then(f => {
      if (atlasDead) return;
      prontas.set(url, f);
      paintDolls();
    }).catch(() => {});
  }
}

/** A arma que aparece: a skin de cajado no lugar da maça (techspec-skins §03). Pede as folhas dela uma vez. */
const skinsDeArmaPedidas = new Set<string>();
function armaDoVisual(weapon: string, cloth: string): string {
  const visivel = armaComSkin(weapon, cloth);
  if (visivel !== weapon && !skinsDeArmaPedidas.has(visivel)) {
    skinsDeArmaPedidas.add(visivel);
    ensureArma(visivel);
  }
  return visivel;
}

function ensureEscudo(defId: string){
  for (const url of folhasDoEscudo(defId)) {
    if (!url || prontas.has(url)) continue;
    void carregarFolha(url).then(f => {
      if (atlasDead) return;
      prontas.set(url, f);
      paintDolls();
    }).catch(() => {});
  }
}

paintDolls();
ensureDummy();

void aquecerCabelo();
void aquecerTraje();
quandoTrajeCarregar(() => { if (!atlasDead) paintDolls(); });
void carregarDano();
void prefetchFolhas(youSkin).then(async () => {
  if (atlasDead) return;
  const urls = [
    ...folhasDaSkin(SKIN_PADRAO),
    ...(youSkin !== SKIN_PADRAO ? folhasDaSkin(youSkin) : []),
    ...(youSkin !== SKIN_PADRAO ? folhasDaCloth('robe') : []),
    ...(youSkin !== SKIN_PADRAO ? folhasDaCloth('studded') : []),
    ...(youSkin !== SKIN_PADRAO ? folhasDaCloth('plate') : []),
    ...(youSkin !== SKIN_PADRAO ? folhasDaCloth('plate-arms') : []),
    ...(youSkin !== SKIN_PADRAO ? folhasDaCloth('plate-gloves') : []),
    ...(youSkin !== SKIN_PADRAO ? folhasDaCloth('plate-helm') : []),
    ...(youSkin !== SKIN_PADRAO ? folhasDaCloth('plate-legs') : []),
    // As outras pecas de studded e as cinco de bone NAO entram aqui de
    // proposito: este laco e' sequencial e segura o paintDolls() no fim.
    // Sao 16 folhas por camada -- oito camadas a mais dobrariam a espera da
    // primeira pintura para aquecer roupa que a maioria nunca veste. Elas
    // carregam sob demanda no pegarCloth, com um quadro de atraso ao equipar.
    jsonAnimal(MONTARIA_PADRAO, false),
    jsonAnimal(MONTARIA_PADRAO, true),
    ...folhasDaArma('katana'),
    ...folhasDoEscudo('heater'),
    ...(youSkin !== SKIN_PADRAO ? [jsonPersonagem(youSkin, '', 'attack-bow')] : []),
    jsonPersonagem(PELE_CADAVER, '', 'die'),
    jsonPersonagem('human-female', '', 'die'),
    ...folhasDoCabelo('ponytail'),
    ...folhasDaBarba('short'),
    ...folhasDoInimigo('skeleton')
  ];
  let warned = false;
  for (const url of urls) {
    try {
      const f = await carregarFolha(url);
      if (atlasDead) return;
      prontas.set(url, f);
    } catch {
      if (!warned && !atlasDead) {
        warned = true;
        logKey('mundo.folhaFaltando', undefined, 'note');
      }
    }
  }
  paintDolls();
});

/* ══════════════════ 8. LOOP ══════════════════ */

/**
 * Velocidade do passo, em "tiles por segundo" do WASD (com passoDeGrade). Vem das Configurações › Movimento, em ms
 * por casa, no Welcome (`aplicarVelocidades`). Os padrões são os do servidor: a pé 300/166 ms, montado 200/100 ms.
 */
let SPEED_TROT = velDoMs(300), SPEED_GALLOP = velDoMs(166);
let SPEED_TROT_MONTADO = velDoMs(200), SPEED_GALLOP_MONTADO = velDoMs(100);
/** ms por casa → velocidade do laço (todo passo do WASD tem passoDeGrade ≈ 1,29). */
function velDoMs(ms: number){
  return passoDeGrade(1, 0) * 1000 / Math.max(60, Math.min(1000, ms || 300));
}
function velocidadeDoPasso(galope: boolean){
  if (ehMontaria(youMount)) return galope ? SPEED_GALLOP_MONTADO : SPEED_TROT_MONTADO;
  return galope ? SPEED_GALLOP : SPEED_TROT;
}
function aplicarVelocidades(w: { peAndarMs?: number; peCorrerMs?: number; montadoAndarMs?: number; montadoCorrerMs?: number }){
  if (w.peAndarMs) SPEED_TROT = velDoMs(w.peAndarMs);
  if (w.peCorrerMs) SPEED_GALLOP = velDoMs(w.peCorrerMs);
  if (w.montadoAndarMs) SPEED_TROT_MONTADO = velDoMs(w.montadoAndarMs);
  if (w.montadoCorrerMs) SPEED_GALLOP_MONTADO = velDoMs(w.montadoCorrerMs);
  // O ritmo medido dos outros (passos/s) cabe entre o andar mais lento e a corrida mais rápida.
  PACE_MIN = Math.min(SPEED_TROT, SPEED_TROT_MONTADO) * 0.85;
  PACE_MAX = Math.max(SPEED_GALLOP, SPEED_GALLOP_MONTADO) * 1.05;
}
// Seguindo a IA da sala. O sprite corre no ritmo medido dos passos do
// servidor (CombatMath.CorridaTilesPorSegundo × VelMovimento) e fica de
// propósito ~1 casa atrás: se alcançasse o servidor, pararia até o pacote
// seguinte, e cada parada de 100 ms era um tranco. Atrás de 2 casas, acelera.
// Chebyshev, sem piso visual.
const SPEED_FOLLOW_MIN = 4.5;

/** Casas que o sprite ainda tem a percorrer até a posição do servidor. */
function atrasoDoServidor(){
  return serverPath.length + (player.moving ? 1 - player.t : 0);
}

function velocidadeDeSeguir(){
  const ritmo = Math.max(SPEED_FOLLOW_MIN, selfPace);
  const atras = atrasoDoServidor();
  if (atras > 2.5) return ritmo * 1.35;   // ficou para trás (pacote perdido, snap): recupera
  if (atras > 1.2) return ritmo;          // a folga certa: mesmo ritmo do servidor
  return ritmo * 0.9;                     // perto demais: deixa o servidor abrir a folga
}
const STAM_GALOPE = 0.06; // o mesmo GallopCost da sala (só WASD / cancelar)
const STAM_REGEN = 3;       // por segundo parado, o StamRegenPerTick da sala (0,3 × 10 Hz)
// Atravessar corpo (proposta-atravessar): barra cheia, 25% dela por passo. O mesmo AtravessarRegras da sala.
const ATRAVESSAR_FRACAO = 0.25;
const ATRAVESSAR_FOLGA = 0.5;
// Viagem do Reviver: 1 passo / 2 tiques a 10 Hz = 5 casas/s. Um pouco acima
// para o sprite não acumular atraso — senão a vida volta longe do ankh.
const SPEED_GHOST = 5.5;

const net = !!opts.net;
let joined = !net;
/** No fio, o spawn do arquivo (banco de Minoc) não é a chegada. Não pinta até o welcome. */
let cenaPronta = !net;
/* A tela de carregamento (techspec-loading). `cargaPronta`: já avisou "pronto" nesta entrada; `cargaDesde`: quando a
   cena ficou pronta (o boneco tem até CARGA_MAX_MS para a folha chegar; depois a tela sai assim mesmo). */
const CARGA_MAX_MS = 6000;
let cargaPronta = false;
let cargaDesde = performance.now();
let cargaViagem = false;
let cargaDe = '';
let cargaCidade = mapa.nome;
let jaEntrou = false;
function avisarCarga(etapa: CargaMundo['etapa']){
  opts.onCarga?.({ etapa, cidade: cargaCidade, viagem: cargaViagem, ...(cargaDe ? { de: cargaDe } : {}) });
}
function recomecarCarga(etapa: CargaMundo['etapa'], cidade: string, viagem: boolean, de: string){
  cargaPronta = false;
  cargaViagem = viagem;
  cargaDe = viagem ? de : '';
  cargaCidade = cidade;
  avisarCarga(etapa);
}
/** A folha parada do próprio boneco já está pronta? (morto: a do fantasma). */
function bonecoDesenhavel(): boolean {
  const pele = youDead ? SKIN_PADRAO : youSkin;
  const montaria = !youDead && ehMontaria(youMount) ? 'horse' : '';
  return prontas.has(jsonPersonagem(pele, montaria, 'idle')) || prontas.has(jsonPersonagem(pele, '', 'idle'));
}
let youId = 0;
const pendingVitals = new Map<number, { hits: number; hitsMax: number; mana: number; manaMax: number; poisoned?: boolean }>();
let seq = 0;
let welcomeGen = 0;
const pending: Array<{ seq: number; dir: number; x: number; y: number }> = [];
const remotes = new Map<number, {
  id: number; name: string; dir: number;
  fromX: number; fromY: number; toX: number; toY: number;
  fromAt: number; toAt: number;
  gait: number;
  moveStamp: number;
  pace: number;
  packetAt: number;
  mount: string;
  cloth: string;
  skin: string;
  skinHue: number;
  hairStyle: string;
  hairHue: number;
  beardStyle: string;
  beardHue: number;
  feminino: boolean;
  idleAt: number;
  wasMoving: boolean;
  hits: number;
  hitsMax: number;
  mana: number;
  manaMax: number;
  notoriety: number;
  dead: boolean;
  guildTag: string;
  cargo: number;
  vip: boolean;
  /** Selo de fundador (0..3). */
  selo: number;
  oculto: boolean;
  rel: number;
  kind: TargetKind;
  defId: string;
  aparencia: string;
  attackAt: number;
  deathAt: number;
  morteEspera: number;
  fila: Step[];
  envenenado: boolean;
  weapon: string;
  shield: string;
  npcId: string;
  titulo: string;
  temLoja: boolean;
  temCorretor: boolean;
  temBanco: boolean;
  alcance: number;
}>();
/** A casa tem corpo que dá para atravessar (jogador, mob, NPC, guarda) e nenhum manequim. */
function soCorpos(x: number, y: number){
  let achou = false;
  for (const r of remotes.values()){
    if (r.dead || r.kind === 'corpse' || r.oculto) continue;
    if (r.kind === 'monster' && r.hits <= 0) continue;
    if (youOculto && r.kind === 'player') continue;
    if (r.toX !== x || r.toY !== y) continue;
    if (r.kind === 'dummy') return false;
    achou = true;
  }
  return achou;
}

function podeAtravessar(){
  if (youDead || youCargo > 0) return true;
  return player.stamMax > 0 && player.stam >= player.stamMax - ATRAVESSAR_FOLGA;
}

function ocupada(x: number, y: number){
  if (!youDead && player.hp > 0 && player.tx === x && player.ty === y) return true;
  for (const r of remotes.values()){
    if (r.dead) continue;
    if (r.kind === 'corpse') continue;
    if (r.kind === 'monster' && r.hits <= 0) continue;
    if (r.oculto) continue;
    if (youOculto && r.kind === 'player') continue;
    if (r.toX === x && r.toY === y) return true;
  }
  return false;
}
let targetId: number | null = null;
let warTargetId = 0;
/** Chebyshev do Tab e do War que pega o próximo. Espelha AlvoCiclo.Casas. Sobe os dois juntos. */
const ALVO_ALCANCE = 8;
let npcsGuardados: readonly NpcNoMapa[] = mapa.npcs;
let manequinsGuardados: readonly ManequimNoMapa[] = mapa.manequins;
let mundoVersao = 0;
let mundoNome = '';
/** Canal da sala. O Welcome traz o número; 0 é a primeira sala, e o radar mostra mesmo assim. */
let salaCanal = 0;
let salaProtegida = false;
function aplicarMundoLabel(meta: { mundoEtag?: string; mundoNome?: string }){
  if (meta.mundoEtag) mundoVersao = versaoDoEtag(meta.mundoEtag);
  if (meta.mundoNome !== undefined) mundoNome = meta.mundoNome.trim();
  lastKey = '';
}
let portaisDoMapa: readonly PortalNoMapa[] = mapa.portais;
let portaEventoAberta = false;
let portaisGuardados: readonly PortalNoMapa[] = portaisAtivos(mapa.portais);
let portalSobre: string | null = null;
/* Santuário do Pentagrama (techspec-pentagrama §09): a ponta desperta, as orbes engastadas e o ritual. */
let pentaCor = -1;
let pentaMascara = 0;
let pentaRitual = false;
let pentaLugar = '';
function checarLugarPenta(){
  if (mapa.id !== MAPA_SANTUARIO) {
    if (pentaLugar) { pentaLugar = ''; opts.onPentaLugar?.(null); }
    return;
  }
  const l = youDead ? null : lugarPerto(mapa.regioes, player.tx, player.ty);
  const k = chaveDoLugar(l);
  if (k === pentaLugar) return;
  pentaLugar = k;
  opts.onPentaLugar?.(l);
}
let passoTravado = false;
/** Coleta (techspec-coleta §07): o personagem fica na árvore; só o servidor o move. */
let coletaTravada = false;
let coletaAvisoEm = 0;
/** `x`/`y`: onde o manequim está; o treino automático manda de longe, antes de ele entrar na visão. */
let dummyChegada: { dummyId: number; x: number; y: number; raio: number; onArrive: () => void } | null = null;
/** Treino automático que chegou durante a troca de mapa (Recall): anda quando a cena ficar pronta. */
let treinoPendente: { dummyId: number; alcance: number; onArrive: () => void; alvo: { x: number; y: number } } | null = null;

function tentarDummyChegada(){
  if (!dummyChegada) return;
  const d = remotes.get(dummyChegada.dummyId);
  const [dx, dy] = d && d.kind === 'dummy' ? [d.toX, d.toY] : [dummyChegada.x, dummyChegada.y];
  const here = player.moving ? { x: player.nx, y: player.ny } : { x: player.tx, y: player.ty };
  if (chebyshev(here.x, here.y, dx, dy) > dummyChegada.raio) return;
  const fn = dummyChegada.onArrive;
  dummyChegada = null;
  fn();
}

/**
 * Anda até o alcance do manequim e chama `onArrive`. Sem o manequim na visão, usa `alvo` (o treino automático
 * manda o ponto de longe). Com a cena trocando de mapa, espera ela ficar pronta.
 */
function aproximarManequim(
  dummyId: number, alcance: number, onArrive: () => void, alvo?: { x: number; y: number }
): boolean {
  if (!cenaPronta && alvo) {
    treinoPendente = { dummyId, alcance, onArrive, alvo };
    return true;
  }
  const dummy = remotes.get(dummyId);
  const temDummy = !!dummy && dummy.kind === 'dummy';
  const x = temDummy ? dummy!.toX : alvo?.x;
  const y = temDummy ? dummy!.toY : alvo?.y;
  if (x == null || y == null) return false;
  const raio = alcance > 0 ? alcance : Math.max(1, (temDummy ? dummy!.alcance : 0) || 1);
  dummyChegada = { dummyId, x, y, raio, onArrive };
  const from = player.moving ? { x: player.nx, y: player.ny } : { x: player.tx, y: player.ty };
  if (chebyshev(from.x, from.y, x, y) <= raio) {
    dummyChegada = null;
    onArrive();
    return true;
  }
  let p = temDummy ? findPath(from.x, from.y, x, y) : [];
  if (!temDummy) {
    // Fora da visão o manequim ainda não ocupa a casa: mira uma casa livre do lado dele.
    for (const d of DIRS) {
      const nx = x + d.dx, ny = y + d.dy;
      if (!walkable(nx, ny) || !insideBounds(nx, ny) || ocupada(nx, ny) || dummyNoTile(nx, ny)) continue;
      p = findPath(from.x, from.y, nx, ny);
      if (p.length) break;
    }
  }
  if (!p.length) {
    dummyChegada = null;
    logKey('manequim.longe', undefined, 'warn');
    return false;
  }
  path = p;
  return true;
}

function aquecerPortais(lista: readonly PortalNoMapa[]){
  for (const p of lista) {
    if (p.efeito === EFEITO_PORTAL_PENTA) CORES_PENTA.forEach((_, i) => prefetchEfeito(spriteDoPortalPenta(i)));
    else prefetchEfeito(p.efeito);
  }
}

/** O sprite que o portal desenha agora: o do santuário troca com a cor da ponta (techspec-pentagrama §02.1). */
function spriteDoPortal(efeito: string, agoraS: number){
  return efeito === EFEITO_PORTAL_PENTA ? spriteDoPortalPenta(corDoPortalPenta(pentaCor, agoraS).cor) : efeito;
}

function portaisAtivos(todos: readonly PortalNoMapa[]){
  if (mapa.id !== 'ultimate-battle') return todos;
  return portaEventoAberta ? todos : [];
}

let viagensGuardadas: readonly ViagemDestino[] = [];

async function renovarViagens() {
  viagensGuardadas = await carregarViagens();
}

function aplicarPortais(todos: readonly PortalNoMapa[]){
  portaisDoMapa = todos;
  const next = portaisAtivos(todos);
  portaisGuardados = next;
  if (portalSobre && !next.some(p => p.id === portalSobre)) {
    portalSobre = null;
    passoTravado = false;
    opts.onPortal?.(null);
  }
  aquecerPortais(next);
}

function setPortaEvento(aberta: boolean){
  if (portaEventoAberta === aberta) return;
  portaEventoAberta = aberta;
  aplicarPortais(portaisDoMapa);
}

aquecerPortais(portaisGuardados);
void renovarViagens().then(() => opts.onSala?.());

function portalNoTile(x: number, y: number){
  for (const p of portaisGuardados)
    if (p.x === x && p.y === y) return p;
  return null;
}

function hitPortalSprite(sx: number, sy: number, rx: number, ry: number, efeito: string){
  const def = defEfeito(spriteDoPortal(efeito, performance.now() / 1000));
  if (!def) return hitAvatar(sx, sy, rx, ry, '', true);
  const [cw, ch] = def.celula;
  const [ax, ay] = def.ancora;
  const left = rx - ax * zoom;
  const top = ry - ay * zoom;
  return sx >= left && sx <= left + cw * zoom && sy >= top && sy <= top + ch * zoom;
}

function hitPortal(sx: number, sy: number){
  let best: PortalNoMapa | null = null;
  let bestKey = -Infinity;
  for (const p of portaisGuardados){
    const { rx, ry } = pésNaTela(p.x, p.y);
    if (!pontoNoLosango(sx, sy, p.x, p.y) && !hitPortalSprite(sx, sy, rx, ry, p.efeito))
      continue;
    const key = p.x + p.y;
    if (!best || key > bestKey){
      best = p;
      bestKey = key;
    }
  }
  return best;
}

function abrirPortalPorClique(p: PortalNoMapa){
  travarPasso(true);
  opts.onPortalClick?.(p);
}

function travarColeta(on: boolean){
  coletaTravada = on;
  if (!on) return;
  path = [];
  player.authoredRun = false;
}

function travarPasso(on: boolean){
  passoTravado = on;
  if (!on) return;
  path = [];
  serverPath = [];
  player.authoredRun = false;
}

function marcarPortal(){
  if (youDead) {
    if (portalSobre !== null) opts.onPortal?.(null);
    portalSobre = null;
    passoTravado = false;
    return;
  }
  const hit = portalNoTile(player.tx | 0, player.ty | 0);
  const id = hit?.id ?? null;
  // Fora do portal o passo tem de soltar. O early-return com os dois nulos
  // deixava o travamento de um gate (Minoc 71,56) vivo depois do teleporte
  // para a ankh: o sprite já estava ao lado dela e o WASD não andava até o F5.
  if (id === portalSobre) {
    if (!hit) passoTravado = false;
    return;
  }
  portalSobre = id;
  if (hit) travarPasso(true);
  else passoTravado = false;
  opts.onPortal?.(hit);
}
/** Passo do monstro local (mock), em tiques: o mesmo StepTicks do esqueleto na sala (MobCatalog). */
const PASSO_MOB_TICKS = 6;
const localBrains = new Map<number, {
  homeX: number; homeY: number; radius: number;
  wait: number; walking: boolean; destX: number; destY: number; stepWait: number;
  avoidX: number; avoidY: number;
  hunting: boolean; swingWait: number;
}>();
let localMobSeq = 60000;
let wanderAcc = 0;

function inWanderArea(b: { homeX: number; homeY: number; radius: number }, x: number, y: number){
  return Math.max(Math.abs(x - b.homeX), Math.abs(y - b.homeY)) <= b.radius;
}

function cheby(ax: number, ay: number, bx: number, by: number){
  return Math.max(Math.abs(bx - ax), Math.abs(by - ay));
}

function canApproachLocal(fx: number, fy: number, d: number, destX: number, destY: number, here: number, brain: { homeX: number; homeY: number; radius: number }){
  const dir = DIRS[d];
  const nx = fx + dir.dx, ny = fy + dir.dy;
  if (cheby(nx, ny, destX, destY) >= here) return false;
  if (!inWanderArea(brain, nx, ny) || !canStep(fx, fy, dir.dx, dir.dy)) return false;
  return true;
}

function nudgeLocal(id: number, remote: { name: string; toX: number; toY: number; hits: number; hitsMax: number; defId: string }, brain: { homeX: number; homeY: number; radius: number }){
  const order = [0, 1, 2, 3, 4, 5, 6, 7].sort(() => Math.random() - 0.5);
  for (const d of order){
    const dir = DIRS[d];
    const nx = remote.toX + dir.dx, ny = remote.toY + dir.dy;
    if (!inWanderArea(brain, nx, ny) || !canStep(remote.toX, remote.toY, dir.dx, dir.dy)) continue;
    upsertRemote(id, remote.name, nx, ny, d, false, '', '', undefined, false, '', remote.hits, remote.hitsMax, 0, 0, 0, '', 0, 'monster', remote.defId);
    return;
  }
}

function roll2d7(){
  return 2 + ((Math.random() * 7) | 0) + ((Math.random() * 7) | 0);
}

function tickLocalHunt(id: number, remote: { name: string; toX: number; toY: number; hits: number; hitsMax: number; defId: string }, brain: { homeX: number; homeY: number; radius: number; swingWait: number; stepWait: number }){
  const px = player.tx, py = player.ty;
  if (youDead || player.hp <= 0 || cheby(brain.homeX, brain.homeY, px, py) > 20)
    return false;
  let best = 0, bestDist = 1e9;
  for (let d = 0; d < 8; d++){
    const dist = cheby(remote.toX + DIRS[d].dx, remote.toY + DIRS[d].dy, px, py);
    if (dist < bestDist){ bestDist = dist; best = d; }
  }
  const d = cheby(remote.toX, remote.toY, px, py);
  if (d > 1){
    if (--brain.stepWait > 0) return true;
    brain.stepWait = PASSO_MOB_TICKS;
    const tryStep = (dir: number) => {
      const step = DIRS[dir];
      const nx = remote.toX + step.dx, ny = remote.toY + step.dy;
      if (cheby(nx, ny, px, py) >= d) return false;
      if (cheby(brain.homeX, brain.homeY, nx, ny) > 20) return false;
      if (!canStep(remote.toX, remote.toY, step.dx, step.dy)) return false;
      upsertRemote(id, remote.name, nx, ny, dir, false, '', '', undefined, false, '', remote.hits, remote.hitsMax, 0, 0, 0, '', 0, 'monster', remote.defId);
      return true;
    };
    if (!tryStep(best)){
      for (let i = 0; i < 8; i++) if (tryStep(i)) break;
    }
    return true;
  }
  if (brain.swingWait > 0){
    brain.swingWait--;
    return true;
  }
  brain.swingWait = 18;
  const live = remotes.get(id);
  if (live) live.attackAt = performance.now();
  upsertRemote(id, remote.name, remote.toX, remote.toY, best, true, '', '', undefined, false, '', remote.hits, remote.hitsMax, 0, 0, 0, '', 0, 'monster', remote.defId);
  const after = remotes.get(id);
  if (after) after.attackAt = performance.now();
  const dmg = Math.max(0, roll2d7());
  if (dmg > 0){
    const prev = player.hp;
    player.hp = Math.max(0, player.hp - dmg);
    lastKey = '';
    noteYouHits(prev, player.hp);
  }
  return true;
}

function tickLocalWander(dt: number){
  wanderAcc += dt;
  while (wanderAcc >= 0.1){
    wanderAcc -= 0.1;
    for (const [id, brain] of localBrains){
      const remote = remotes.get(id);
      if (!remote) continue;
      const near = !youDead && player.hp > 0 && cheby(remote.toX, remote.toY, player.tx, player.ty) <= 10;
      if (near || brain.hunting){
        brain.hunting = tickLocalHunt(id, remote, brain);
        if (brain.hunting) continue;
        remote.hits = remote.hitsMax;
        brain.swingWait = 0;
      }
      if (brain.radius <= 0) continue;
      if (!brain.walking){
        if (--brain.wait > 0) continue;
        let found = false;
        for (let i = 0; i < 16; i++){
          const x = brain.homeX + ((Math.random() * (brain.radius * 2 + 1)) | 0) - brain.radius;
          const y = brain.homeY + ((Math.random() * (brain.radius * 2 + 1)) | 0) - brain.radius;
          if (!inWanderArea(brain, x, y) || !walkable(x, y) || !insideBounds(x, y)) continue;
          if (x === brain.avoidX && y === brain.avoidY) continue;
          if (x === remote.toX && y === remote.toY){
            brain.wait = 8 + ((Math.random() * 18) | 0);
            found = true;
            break;
          }
          const here = cheby(remote.toX, remote.toY, x, y);
          let approach = false;
          for (let d = 0; d < 8; d++){
            if (canApproachLocal(remote.toX, remote.toY, d, x, y, here, brain)){ approach = true; break; }
          }
          if (!approach) continue;
          brain.destX = x;
          brain.destY = y;
          brain.walking = true;
          brain.stepWait = PASSO_MOB_TICKS;
          found = true;
          break;
        }
        if (!found){
          brain.wait = 8 + ((Math.random() * 18) | 0);
          nudgeLocal(id, remote, brain);
        }
        continue;
      }
      if (--brain.stepWait > 0) continue;
      brain.stepWait = PASSO_MOB_TICKS;
      const here = cheby(remote.toX, remote.toY, brain.destX, brain.destY);
      const tryApproach = (d: number) => {
        if (!canApproachLocal(remote.toX, remote.toY, d, brain.destX, brain.destY, here, brain)) return false;
        const dir = DIRS[d];
        upsertRemote(id, remote.name, remote.toX + dir.dx, remote.toY + dir.dy, d, false, '', '', undefined, false, '', remote.hits, remote.hitsMax, 0, 0, 0, '', 0, 'monster', remote.defId);
        return true;
      };
      let best = 0, bestDist = 1e9;
      for (let d = 0; d < 8; d++){
        const nx = remote.toX + DIRS[d].dx;
        const ny = remote.toY + DIRS[d].dy;
        const dist = cheby(nx, ny, brain.destX, brain.destY);
        if (dist < bestDist){ bestDist = dist; best = d; }
      }
      let stepped = tryApproach(best);
      if (!stepped){
        for (let d = 0; d < 8; d++){
          if (tryApproach(d)){ stepped = true; break; }
        }
      }
      const after = remotes.get(id);
      if (!stepped){
        brain.avoidX = brain.destX;
        brain.avoidY = brain.destY;
        brain.walking = false;
        brain.wait = 8 + ((Math.random() * 18) | 0);
        nudgeLocal(id, remote, brain);
      } else if (after && after.toX === brain.destX && after.toY === brain.destY){
        brain.walking = false;
        brain.wait = 8 + ((Math.random() * 18) | 0);
      }
    }
  }
}
const ignored = new Set<number>();
/** `magia`: balão das palavras de poder, que fica até a magia acertar ou falhar (kind 2 ou falha). */
const balloons = new Map<number, { text: string; until: number; magia?: boolean }>();

/**
 * Quem está conjurando (techspec-magias-animacoes): começa nas palavras de poder e acaba no acerto, na falha ou no
 * efeito. `area`: braços para o alto (grupo 17), nas magias só em si ou em área; o resto aponta (grupo 16).
 */
const conjurando = new Map<number, Conjuracao>();
const CONJURAR_MAX_MS = 8000;
const MAGIA_EM_AREA = new Set(['reactive-armor', 'protection', 'magic-reflection', 'arch-protection', 'arch-cure', 'mass-curse', 'recall']);
const AREA_POR_PALAVRAS = new Map(Object.values(MAGIAS).map(m => [m.palavras.toLowerCase(), MAGIA_EM_AREA.has(m.id)]));

function conjuracaoDe(id: number, agora: number): Conjuracao | null {
  const c = conjurando.get(id);
  if (!c) return null;
  if (agora - c.desde > CONJURAR_MAX_MS) {
    conjurando.delete(id);
    return null;
  }
  return c;
}
/** Palavras já saíram; o impacto ainda não. Só então o *falhou...* vale. */
let awaitingHit = false;
const SPEECH_MS = 3000;
/** Rede de segurança do balão das palavras: o servidor manda o fim (acertou) ou a falha bem antes disso. */
const PALAVRAS_MAX_MS = 20000;
/** O que o servidor manda hoje pelo canal das palavras. Ponte; sai com o servidor novo. */
const FIZZLE_LEGADO = 'falhou...';
const remoteDieSfx = new Set<number>();
const dyingRemotes = new Set<number>();
let PACE_MIN = Math.min(SPEED_TROT, SPEED_TROT_MONTADO) * 0.85;
let PACE_MAX = Math.max(SPEED_GALLOP, SPEED_GALLOP_MONTADO) * 1.05;
/** Remoto correndo: acima do trote do teclado (4,27) e abaixo da corrida da caçada (6,0 × VelMovimento). */
const PACE_CORRIDA = 5.3;

function cycleTarget(){
  if (remotes.size === 0){ targetId = null; return; }
  const px = player.tx, py = player.ty;
  const vivos = [...remotes.values()].filter(r => {
    if (r.kind === 'npc' || r.kind === 'corpse' || r.kind === 'guard') return false;
    if (ehDummyRemoto(r)) return chebyshev(px, py, r.toX, r.toY) <= ALVO_ALCANCE;
    return r.hits > 0 && !r.dead;
  });
  const perto = vivos.filter(r => chebyshev(px, py, r.toX, r.toY) <= ALVO_ALCANCE);
  const sorted = (perto.length > 0 ? perto : vivos).sort((a, b) => {
    const da = chebyshev(px, py, a.toX, a.toY);
    const db = chebyshev(px, py, b.toX, b.toY);
    return da - db || a.id - b.id;
  });
  if (sorted.length === 0){ targetId = null; return; }
  let next: number;
  if (targetId === null) next = sorted[0].id;
  else {
    const idx = sorted.findIndex(r => r.id === targetId);
    next = sorted[(idx + 1) % sorted.length].id;
  }
  targetId = next;
  // Membro do grupo vira alvo de cura e buff, nunca de ataque (techspec-grupo-placas).
  if (warTargetId !== 0 && !ehDoGrupoId(next)) opts.onKeepAttack?.(next);
}

function selecionarAlvo(id: number){
  const mesmo = targetId === id;
  targetId = mesmo ? null : id;
  if (!mesmo && targetId != null && warTargetId !== 0 && !ehDoGrupoId(targetId)) opts.onKeepAttack?.(targetId);
}

function alvoAindaVive(id: number){
  const r = remotes.get(id);
  if (!r) return false;
  if (ehDummyRemoto(r)) return true;
  // A morte à distância segura o sprite (hits fictícios). Para o War o
  // alvo já caiu — senão a Guerra apagava e o pack não encadeava.
  if (r.morteEspera > 0) return false;
  return r.hits > 0 && !r.dead;
}

function proximoAlvoVivo(kind?: TargetKind){
  const px = player.tx, py = player.ty;
  let bestId: number | null = null;
  let bestD = 0;
  for (const r of remotes.values()) {
    if (r.kind === 'npc' || r.kind === 'corpse' || r.kind === 'guard') continue;
    if (kind && r.kind !== kind && !(kind === 'dummy' && ehDummyRemoto(r))) continue;
    if (ehDummyRemoto(r)) {
      if (chebyshev(px, py, r.toX, r.toY) > ALVO_ALCANCE) continue;
    } else if (r.hits <= 0 || r.dead) continue;
    const d = chebyshev(px, py, r.toX, r.toY);
    if (!ehDummyRemoto(r) && d > ALVO_ALCANCE) continue;
    if (bestId == null || d < bestD || (d === bestD && r.id < bestId)) {
      bestId = r.id;
      bestD = d;
    }
  }
  return bestId;
}

function retargetMorto(){
  if (warTargetId !== 0 && alvoAindaVive(warTargetId)) {
    targetId = warTargetId;
    return;
  }
  const morto = targetId != null ? remotes.get(targetId) : undefined;
  const kind = morto?.kind === 'monster' || morto?.kind === 'player' ? morto.kind : 'monster';
  const prox = proximoAlvoVivo(kind);
  if (prox != null && warTargetId !== 0) {
    targetId = prox;
    opts.onKeepAttack?.(prox);
    return;
  }
  targetId = null;
}

function chebyshev(ax: number, ay: number, bx: number, by: number){
  return Math.max(Math.abs(bx - ax), Math.abs(by - ay));
}

/** O balão de fala sobe acima das palavras de poder quando as duas estão no ar. */
function baseDaFala(id: number, y: number, now: number): number {
  const b = balloons.get(id);
  return b && now < b.until ? y - 16 * zoom : y;
}

function drawBalloon(ctx: Ctx2D, x: number, y: number, id: number, now: number){
  const b = balloons.get(id);
  if (!b) return;
  if (now >= b.until){ balloons.delete(id); return; }
  ctx.save();
  ctx.font = '12px Tahoma, Segoe UI, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(0,0,0,.75)';
  ctx.strokeText(b.text, x, y);
  ctx.fillStyle = '#a0a0a0';
  ctx.fillText(b.text, x, y);
  ctx.restore();
}

/** A folha tem folga acima da cabeça; desce o nick até encostar nela. */
const NICK_DA_CABECA = 20;
const HP_BAR_W = 40;
const HP_BAR_H = 5;

/** Vida nos pés de jogador/inimigo. Borda dourada no alvo; vermelha no War contra jogador. */
function drawHitsBar(
  ctx: Ctx2D, x: number, feetY: number,
  hits: number, hitsMax: number, poisoned: boolean, selected = false, warHostil = false
){
  if (hitsMax <= 0) return;
  const z = zoom;
  const w = HP_BAR_W * z, h = HP_BAR_H * z;
  const x0 = x - w / 2;
  const y = feetY + 5 * z + 4;
  const frac = Math.max(0, Math.min(1, hits / hitsMax));
  ctx.save();
  ctx.fillStyle = '#000';
  ctx.fillRect(x0, y, w, h);
  const pad = 1;
  if (frac > 0) {
    ctx.fillStyle = poisoned ? '#c06bff' : '#3db4e6';
    ctx.fillRect(x0 + pad, y + pad, Math.max(1, (w - pad * 2) * frac), h - pad * 2);
  }
  if (selected) {
    ctx.strokeStyle = warHostil ? NICK.war : NICK.target;
    ctx.lineWidth = 2;
    ctx.strokeRect(x0 - 1, y - 1, w + 2, h + 2);
  } else {
    ctx.strokeStyle = 'rgba(0,0,0,.9)';
    ctx.lineWidth = 1;
    ctx.strokeRect(x0 + 0.5, y + 0.5, w - 1, h - 1);
  }
  ctx.restore();
}

/** Vida, mana e vigor acima do nick do próprio personagem. */
function drawYouVitals(ctx: Ctx2D, x: number, nameY: number): number {
  const barW = 50 * zoom, barH = 4 * zoom, gap = 2 * zoom;
  const rows: readonly { frac: number; fill: string }[] = [
    { frac: player.hpMax > 0 ? player.hp / player.hpMax : 0, fill: player.envenenado ? '#c06bff' : '#e04a3f' },
    { frac: player.manaMax > 0 ? player.mana / player.manaMax : 0, fill: '#5b7fc4' },
    { frac: player.stamMax > 0 ? player.stam / player.stamMax : 0, fill: '#e0c44a' }
  ];
  const barsH = barH * 3 + gap * 2;
  const baseY = nameY - 16 * zoom - barsH;
  const bx0 = x - barW / 2;
  ctx.save();
  for (let i = 0; i < rows.length; i++) {
    const y = baseY + i * (barH + gap);
    ctx.fillStyle = 'rgba(0,0,0,.55)';
    ctx.fillRect(bx0, y, barW, barH);
    const f = Math.max(0, Math.min(1, rows[i].frac));
    if (f > 0) {
      ctx.fillStyle = rows[i].fill;
      ctx.fillRect(bx0, y, barW * f, barH);
    }
    if (i === 2 && pressionada('galopar')) {
      ctx.strokeStyle = '#ffe08a';
      ctx.lineWidth = 1;
      ctx.strokeRect(bx0 - 0.5, y - 0.5, barW + 1, barH + 1);
    }
  }
  ctx.strokeStyle = 'rgba(0,0,0,.75)';
  ctx.lineWidth = 1;
  ctx.strokeRect(bx0 - 1, baseY - 1, barW + 2, barsH + 2);
  ctx.restore();
  return baseY;
}

/**
 * Enquanto o mapa da sala nova não carrega, Welcome já chegou e a caçada
 * já ataca. Sem a fila o snapshot aplica no mapa velho, `instalarMapa`
 * apaga os remotes, e os `entered` seguintes caem no vazio — fogo num
 * tile sem esqueleto. Guardamos o snapshot e os frames até o mapa existir.
 */
type PendingJoin =
  | { k: 'n'; entities: ReadonlyArray<NetEntered> }
  | { k: 'f'; frame: NetFrame }
  | { k: 'v'; id: number; hits: number; hitsMax: number; mana: number; manaMax: number; poisoned?: boolean }
  | { k: 'l'; id: number; look: Parameters<typeof applyLook>[1] };
const pendingNet: PendingJoin[] = [];
let sawJoinSnap = false;
const MAX_PENDING_NET = 192;
const pendingGolpes: NetStrike[] = [];
const alvoComGolpe = new Set<number>();
let golpeAt = 0;

function marcarGolpe(id: number){
  const agora = performance.now();
  if (agora - golpeAt > 250) alvoComGolpe.clear();
  golpeAt = agora;
  alvoComGolpe.add(id);
}

function temGolpe(id: number){
  if (performance.now() - golpeAt > 250) {
    alvoComGolpe.clear();
    return false;
  }
  return alvoComGolpe.has(id);
}

function meuGolpe(s: NetStrike){
  if (s.atacante === 0) return false;
  return s.atacante === youId;
}

/* ── Volta da aba / rajada de quadros ──
 * Aba escondida ou página congelada: o navegador para o rAF e, às vezes, segura
 * os quadros da sala e os entrega de uma vez na volta. Cada sprite ganhava a fila
 * inteira de passos e a andava acelerado até alcançar o agora (Renato, 15/10).
 * Na volta (ou numa rajada), o que está atrás vai direto para onde o servidor diz. */
const VAO_MS = 400;          // sem pintar há mais que isto: o rAF parou
const RAJADA_QUADROS = 4;    // mais que isto de quadros da sala (10 Hz) entre duas pinturas
const JANELA_VOLTA_MS = 2000; // rajada que chega logo depois de voltar também encaixa
let quadrosDesdePintura = 0;
let ressincAte = 0;

function temAtraso(){
  if (atrasoDoServidor() > 2) return true;
  for (const r of remotes.values()) if (r.fila.length > 1) return true;
  return false;
}

function ressincronizar(now: number){
  if (net && joined && pending.length === 0 && mapa.dentro(serverSelfX, serverSelfY)
      && (atrasoDoServidor() > 1.5 || chebyshev(player.tx, player.ty, serverSelfX, serverSelfY) > 1)) {
    snapSelf(serverSelfX, serverSelfY);
    selfPaceAt = 0;
  }
  for (const r of remotes.values()){
    if (r.fila.length === 0) continue;
    const fim = r.fila[r.fila.length - 1];
    r.fila = [];
    r.fromX = r.toX = fim.x;
    r.fromY = r.toY = fim.y;
    r.fromAt = r.toAt = now;
    r.packetAt = now;
  }
}

on(document, 'visibilitychange', () => {
  if (document.hidden) return;
  ressincAte = performance.now() + JANELA_VOLTA_MS;
  quadrosDesdePintura = RAJADA_QUADROS + 1;
});

/** Testes de navegador no modo de desenvolvimento: põe o personagem num ponto (não existe no build de produção). */
if (import.meta.env.DEV && typeof window !== 'undefined')
  (window as unknown as { __mundoDev?: unknown }).__mundoDev = { snap: (x: number, y: number) => snapSelf(x, y) };

function snapSelf(x: number, y: number){
  player.x = player.tx = player.nx = x;
  player.y = player.ty = player.ny = y;
  // Volta (recusa, atraso, troca de mapa) mantém o andar: o z do servidor, ou o de agora, se é um pé dali.
  const manter = [serverSelfZ, player.tz].find(z => z !== 0 && peValido(mapa.id, x, y, z) === true);
  player.tz = player.nz = manter ?? zPe(x, y);
  player.moving = false;
  player.t = 1;
  player.authoredRun = false;
  serverPath = [];
  if (!portalNoTile(x, y)) {
    passoTravado = false;
    if (portalSobre !== null) {
      portalSobre = null;
      opts.onPortal?.(null);
    }
  }
  const z = zPe(player.tx, player.ty);
  cam = { x: wx(player.x, player.y), y: wy(player.x, player.y) - z * Z_PX };
}

function teleportSelf(x: number, y: number){
  pending.length = 0;
  path = [];
  snapSelf(x, y);
}

function instalarMapa(novo: typeof mapa){
  invalidarMapa(mapa.id);
  invalidarMapa(novo.id);
  aquecerSpawn(novo);
  aquecerChegada(novo);
  mapa = novo;
  opts.onMapa?.(mapa.id);
  TW = mapa.tileW;
  TH = mapa.tileH;
  marcaClique = null;
  bounds = mapa.cerca;
  mmBase = assarMinimapa(mapa);
  remotes.clear();
  pendingVitals.clear();
  pendingGolpes.length = 0;
  localBrains.clear();
  remoteDieSfx.clear();
  dyingRemotes.clear();
  pending.length = 0;
  path = [];
  serverPath = [];
  seq = 0;
  targetId = null;
  if (warTargetId !== 0) {
    warTargetId = 0;
    opts.onWar?.(false);
  }
  cancelTilePick();
  npcsGuardados = novo.npcs;
  manequinsGuardados = novo.manequins;
  if (novo.id !== 'ultimate-battle') portaEventoAberta = false;
  aplicarPortais(novo.portais);
  reporNpcs();
  opts.onSala?.();
}

function replayPending(){
  let x = player.tx, y = player.ty;
  for (const p of pending){
    const d = DIRS[p.dir];
    if (canStep(x, y, d.dx, d.dy)){ x += d.dx; y += d.dy; }
    p.x = x; p.y = y;
  }
  player.nx = x; player.ny = y;
  if (pending.length && (x !== player.tx || y !== player.ty)){
    player.moving = true;
    player.t = 0;
    stepVis = Math.max(1, chebyshev(player.tx, player.ty, x, y));
  } else {
    player.moving = false;
    player.t = 1;
    player.x = player.tx;
    player.y = player.ty;
  }
}

function nickPlate(tag: string, name: string, cargo = 0){
  return nomeComMarca(name, cargo, tag);
}

function duracaoPassoRemote(dx: number, dy: number, pace: number){
  const vis = Math.max(passoVisual(dx, dy), 0.05);
  return Math.min(800, Math.max(80, (vis / pace) * 1000));
}

function avancarFilaRemote(r: {
  fila: Step[]; attackAt: number;
  fromX: number; fromY: number; toX: number; toY: number;
  fromAt: number; toAt: number; pace: number; dir: number;
}, now: number){
  if (now < r.toAt) return;
  const n = r.fila.shift();
  if (!n) return;
  const dx = n.x - r.toX;
  const dy = n.y - r.toY;
  r.fromX = r.toX;
  r.fromY = r.toY;
  r.toX = n.x;
  r.toY = n.y;
  r.fromAt = now;
  if (n.ms && n.ms > 0) {
    // Passo real do servidor: a duração é a dele. Com fila (rede atrasou um pacote), acelera até 2× para alcançar
    // sem pular casa; sem fila, anda no ritmo exato.
    const atraso = r.fila.length;
    const pressa = Math.min(2, 1 + Math.max(0, atraso - 1) * 0.35);
    r.toAt = now + Math.max(40, n.ms / pressa);
    r.pace = 1000 / n.ms;
  } else {
    r.toAt = now + duracaoPassoRemote(dx, dy, SPEED_GALLOP);
    r.pace = SPEED_GALLOP;
  }
  if (dx || dy) r.dir = dirIndex(dx, dy);
}

/**
 * Passos de outro jogador com o caminho do servidor (Moved.Passos): refaz casa por casa, na ordem, sem cortar esquina.
 * O começo é (x, y) menos os passos; ele tem de encostar no fim do que já está na fila (até 2 casas: uma ponte cobre
 * um pacote perdido). Fora disso devolve false e o upsertRemote cuida (caminho calculado ou snap).
 */
function seguirPassos(m: NetMoved): boolean {
  const r = remotes.get(m.id);
  const passos = m.passos;
  if (!r || !passos || passos.length === 0 || r.kind !== 'player') return false;
  let sx = m.x, sy = m.y;
  for (let i = passos.length - 1; i >= 0; i--) {
    const d = DIRS[passos[i]];
    if (!d) return false;
    sx -= d.dx; sy -= d.dy;
  }
  const fim = r.fila.length > 0 ? r.fila[r.fila.length - 1] : { x: Math.round(r.toX), y: Math.round(r.toY) };
  const vao = chebyshev(fim.x, fim.y, sx, sy);
  if (vao > 2) return false;
  const ms = m.passoMs && m.passoMs > 0 ? m.passoMs : 0;
  const novos: Step[] = [];
  if (vao > 0) {
    const ponte = encadearAte(fim.x, fim.y, sx, sy);
    const ultimo = ponte[ponte.length - 1];
    if (!ultimo || ultimo.x !== sx || ultimo.y !== sy) return false;
    for (const p of ponte) novos.push({ x: p.x, y: p.y, ms });
  }
  let x = sx, y = sy;
  for (const p of passos) {
    const d = DIRS[p];
    x += d.dx; y += d.dy;
    novos.push({ x, y, ms });
  }
  r.fila.push(...novos);
  // Atraso grande (aba em segundo plano, rede travada): não vale andar 3 s atrás. Fica com as últimas casas.
  if (r.fila.length > 12) r.fila.splice(0, r.fila.length - 6);
  const now = performance.now();
  r.packetAt = now;
  if (now >= r.toAt) avancarFilaRemote(r, now);
  return true;
}

/**
 * O mob anda no ritmo que o servidor manda (Moved.PassoMs = StepTicks × 100 ms; techspec-mobs-andar). Sem isso o
 * upsertRemote media o intervalo dos pacotes e travava no PACE_MIN (85% do trote do jogador): o Esqueleto, de 700 ms,
 * cruzava a casa em ~340 ms e ficava parado o resto, piscando entre andar e parado a cada casa.
 */
function ritmoDoMob(id: number, passoMs: number){
  const r = remotes.get(id);
  if (!r || r.kind !== 'monster') return;
  const agora = performance.now();
  const passo = chebyshev(r.fromX, r.fromY, r.toX, r.toY);
  // Passo que começou neste pacote: a duração é a do servidor, proporcional ao que falta da casa.
  if (passo > 0.02 && passo <= 1.5 && r.fromAt >= agora - 5) {
    r.toAt = r.fromAt + Math.max(40, passoMs * Math.min(1, passo));
    r.pace = 1000 / passoMs;
  }
  // Passo que chegou com o anterior ainda andando: entra na fila já com o ritmo (avancarFilaRemote usa o ms).
  for (const s of r.fila) if (!s.ms) s.ms = passoMs;
}

function upsertRemote(id: number, name: string, x: number, y: number, dir: number, snap: boolean, mount?: string, skin?: string, skinHue?: number, dead?: boolean, cloth?: string, hits?: number, hitsMax?: number, mana?: number, manaMax?: number, notoriety?: number, guildTag?: string, rel?: number, kind?: TargetKind, defId?: string, poisoned?: boolean, weapon?: string, shield?: string, hairStyle?: string, hairHue?: number, beardStyle?: string, beardHue?: number, feminino?: boolean, aparencia?: string, cargo?: number, vip?: boolean, oculto?: boolean, selo?: number){
  const now = performance.now();
  const prev = remotes.get(id);
  const nextDead = dead === undefined ? (prev?.dead ?? false) : !!dead;
  const nextNoto = notoriety === undefined ? (prev?.notoriety ?? 0) : notoriety;
  const nextTag = guildTag === undefined ? (prev?.guildTag ?? '') : guildTag;
  const nextCargo = cargo === undefined ? (prev?.cargo ?? 0) : cargo;
  const nextVip = vip === undefined ? (prev?.vip ?? false) : vip;
  const nextSelo = selo === undefined ? (prev?.selo ?? 0) : selo;
  const nextOculto = oculto === undefined ? (prev?.oculto ?? false) : oculto;
  const nextRel = rel === undefined ? (prev?.rel ?? 0) : rel;
  const nextMount = nextDead
    ? ''
    : (mount === undefined ? (prev?.mount ?? '') : (ehMontaria(mount) ? mount : ''));
  const nextCloth = nextDead
    ? ''
    : (cloth === undefined ? (prev?.cloth ?? '') : fioDoVisual(cloth));
  const nextWeapon = nextDead
    ? ''
    : (weapon === undefined ? (prev?.weapon ?? '') : weapon.trim());
  const nextShield = nextDead
    ? ''
    : (shield === undefined ? (prev?.shield ?? '') : escudoDoFio(shield));
  const nextSkin = nextDead
    ? SKIN_PADRAO
    : (skin === undefined ? (prev?.skin ?? SKIN_PADRAO) : ((skin.trim() || SKIN_PADRAO)));
  const nextHue = peleValida(skinHue)
    ? skinHue
    : (prev?.skinHue ?? youSkinHue);
  const nextHairStyle = hairStyle === undefined ? (prev?.hairStyle ?? '') : estiloDaFolha(hairStyle);
  const nextHairHue = hairHue === undefined ? (prev?.hairHue ?? 0) : hairHue;
  const nextBeardStyle = beardStyle === undefined ? (prev?.beardStyle ?? '') : estiloDaFolha(beardStyle);
  const nextBeardHue = beardHue === undefined ? (prev?.beardHue ?? 0) : beardHue;
  const nextFem = feminino === undefined ? (prev?.feminino ?? femininoDo(nextSkin, false)) : !!feminino;
  const nextKind: TargetKind = kind === 'npc' || prev?.kind === 'npc'
    ? 'npc'
    : kind === 'corpse' || prev?.kind === 'corpse'
    ? 'corpse'
    : kind === 'dummy' || prev?.kind === 'dummy'
    || !!(defId && defId.startsWith('dummy-'))
    || !!(prev?.defId && prev.defId.startsWith('dummy-'))
    ? 'dummy'
    : kind === 'guard' || prev?.kind === 'guard'
    ? 'guard'
    : kind === 'monster'
    || !!(defId && defId.trim())
    || prev?.kind === 'monster'
    ? 'monster'
    : (kind ?? prev?.kind ?? 'player');
  const nextDef = nextKind === 'monster'
    ? ((defId ?? prev?.defId ?? 'skeleton').trim() || 'skeleton')
    : nextKind === 'dummy'
    ? ((defId ?? prev?.defId ?? 'dummy-nordeste').trim() || 'dummy-nordeste')
    : '';
  const nextAparencia = nextKind === 'monster'
    ? ((aparencia ?? prev?.aparencia ?? nextDef).trim() || nextDef)
    : '';
  if (nextKind === 'monster') ensureMob(nextAparencia);
  else if (nextKind === 'dummy') ensureDummy();
  else if (nextKind === 'corpse') ensureDie();
  else {
    ensureSkin(nextSkin);
    if (nextCloth) ensureCloth(nextCloth);
    if (nextMount) ensureAnimal(nextMount);
    if (nextWeapon) ensureArma(nextWeapon);
    if (nextShield) ensureEscudo(nextShield);
    if (nextHairStyle) ensureCabelo(nextHairStyle);
    if (nextBeardStyle && !nextFem) ensureBarba(nextBeardStyle);
  }
  if (!prev || snap || nextKind === 'corpse'){
    remotes.set(id, {
      id, name, dir,
      fromX: x, fromY: y, toX: x, toY: y,
      fromAt: now, toAt: now,
      gait: prev?.gait ?? 0,
      moveStamp: prev?.moveStamp ?? 0,
      pace: SPEED_TROT,
      packetAt: now,
      mount: nextMount,
      cloth: nextCloth,
      skin: nextSkin,
      skinHue: nextHue,
      hairStyle: nextHairStyle,
      hairHue: nextHairHue,
      beardStyle: nextBeardStyle,
      beardHue: nextBeardHue,
      feminino: nextFem,
      idleAt: prev?.idleAt ?? now,
      wasMoving: prev?.wasMoving ?? false,
      hits: hits ?? prev?.hits ?? 0,
      hitsMax: hitsMax ?? prev?.hitsMax ?? 0,
      mana: mana ?? prev?.mana ?? 0,
      manaMax: manaMax ?? prev?.manaMax ?? 0,
      notoriety: nextNoto,
      dead: nextDead,
      guildTag: nextTag,
      cargo: nextCargo,
      vip: nextVip,
      selo: nextSelo,
      oculto: nextOculto,
      rel: nextRel,
      kind: nextKind,
      defId: nextDef,
      aparencia: nextAparencia,
      attackAt: prev?.attackAt ?? 0,
      deathAt: nextKind === 'corpse' ? (prev?.deathAt || now) : (prev?.deathAt ?? 0),
      morteEspera: nextKind === 'corpse' ? 0 : (prev?.morteEspera ?? 0),
      fila: [],
      envenenado: poisoned ?? prev?.envenenado ?? false,
      weapon: nextWeapon,
      shield: nextShield,
      npcId: prev?.npcId ?? '',
      titulo: prev?.titulo ?? '',
      temLoja: prev?.temLoja ?? false,
      temCorretor: prev?.temCorretor ?? false,
      temBanco: prev?.temBanco ?? false,
      alcance: nextKind === 'dummy'
        ? (dummyCombate(nextDef) || dummyNoTile(x, y)?.uso === 'combate' ? ALVO_ALCANCE : (prev?.alcance || 1))
        : (prev?.alcance ?? NPC_ALCANCE_PADRAO)
    });
    soltarVitalPendente(id);
    return;
  }
  const t = Math.min(1, (now - prev.fromAt) / Math.max(1, prev.toAt - prev.fromAt));
  const cx = prev.fromX + (prev.toX - prev.fromX) * t;
  const cy = prev.fromY + (prev.toY - prev.fromY) * t;
  const interval = now - prev.packetAt;
  const stepped = chebyshev(prev.toX, prev.toY, x, y);
  let pace = prev.pace;
  if (stepped > 0.01 && stepped <= 1.01 && interval > 40 && interval < 800)
    pace = Math.min(PACE_MAX, Math.max(PACE_MIN, stepped / (interval / 1000)));
  else if (interval >= 800 || stepped > 1.01)
    pace = SPEED_TROT;
  let fromX = cx, fromY = cy, toX = x, toY = y;
  let fromAt = now;
  let toAt = now + duracaoPassoRemote(x - cx, y - cy, pace);
  let fila: Step[] = [];
  const destNaFila = prev.fila.length > 0
    && prev.fila[prev.fila.length - 1].x === x
    && prev.fila[prev.fila.length - 1].y === y;
  const destNoPasso = prev.toX === x && prev.toY === y && prev.fila.length === 0;
  const aindaAnda = now < prev.toAt && chebyshev(prev.fromX, prev.fromY, prev.toX, prev.toY) > 0.02;
  if (destNaFila || destNoPasso) {
    fromX = prev.fromX; fromY = prev.fromY;
    toX = prev.toX; toY = prev.toY;
    fromAt = prev.fromAt; toAt = prev.toAt;
    fila = prev.fila;
  } else if (aindaAnda && stepped > 0.01) {
    fromX = prev.fromX; fromY = prev.fromY;
    toX = prev.toX; toY = prev.toY;
    fromAt = prev.fromAt; toAt = prev.toAt;
    fila = encadearAte(Math.round(prev.toX), Math.round(prev.toY), x, y);
    if (fila.length === 0 && stepped > 1.01) {
      const longa = findPath(Math.round(prev.toX), Math.round(prev.toY), x, y, true);
      fila = longa.length > 8 ? longa.slice(0, 8) : longa;
    }
    pace = prev.pace;
  } else if (stepped > 1.01) {
    const ox = Math.round(prev.toX), oy = Math.round(prev.toY);
    const alcanca = (p: Step[]) => p.length > 0 && p[p.length - 1].x === x && p[p.length - 1].y === y;
    let rota = encadearAte(ox, oy, x, y);
    if (!alcanca(rota)) {
      const longa = findPath(ox, oy, x, y, true);
      rota = alcanca(longa) ? (longa.length > 8 ? longa.slice(0, 8) : longa) : [];
    }
    pace = SPEED_GALLOP;
    if (rota.length > 0) {
      const p = rota[0];
      fromX = cx; fromY = cy;
      toX = p.x; toY = p.y;
      fromAt = now;
      toAt = now + duracaoPassoRemote(p.x - cx, p.y - cy, pace);
      fila = rota.slice(1);
    } else {
      // A ankh de Minoc bloqueia o desenho (35,66 e 35,67); o ponto é
      // (37,67). Sem caminho o sprite ficava na casa da esquerda até o F5.
      fromX = x; fromY = y; toX = x; toY = y;
      fromAt = now; toAt = now;
      fila = [];
    }
  } else if (stepped < 0.01) {
    fromX = x; fromY = y; toX = x; toY = y;
    fromAt = now; toAt = now;
    fila = [];
  }
  remotes.set(id, {
    id, name: prev.name || name, dir,
    fromX, fromY, toX, toY,
    fromAt, toAt,
    gait: prev.gait,
    moveStamp: prev.moveStamp,
    pace,
    packetAt: now,
    mount: nextMount,
    cloth: nextCloth,
    skin: nextSkin,
    skinHue: nextHue,
    hairStyle: nextHairStyle,
    hairHue: nextHairHue,
    beardStyle: nextBeardStyle,
    beardHue: nextBeardHue,
    feminino: nextFem,
    idleAt: prev.idleAt,
    wasMoving: prev.wasMoving,
    hits: hits ?? prev.hits,
    hitsMax: hitsMax ?? prev.hitsMax,
    mana: mana ?? prev.mana,
    manaMax: manaMax ?? prev.manaMax,
    notoriety: nextNoto,
    dead: nextDead,
    guildTag: nextTag,
    cargo: nextCargo,
    vip: nextVip,
    selo: nextSelo,
    oculto: nextOculto,
    rel: nextRel,
    kind: nextKind,
    defId: nextDef,
    aparencia: nextAparencia,
    attackAt: prev.attackAt,
    deathAt: prev.deathAt,
    morteEspera: prev.morteEspera,
    fila,
    envenenado: poisoned ?? prev.envenenado,
    weapon: nextWeapon,
    shield: nextShield,
    npcId: prev.npcId,
    titulo: prev.titulo,
    temLoja: prev.temLoja,
    temCorretor: prev.temCorretor,
    temBanco: prev.temBanco,
    alcance: prev.alcance
  });
  soltarVitalPendente(id);
}

function reporNpcs(){
  for (let i = 0; i < npcsGuardados.length; i++){
    const n = npcsGuardados[i];
    const id = NPC_ID_BASE + i;
    upsertRemote(id, n.nome, n.x, n.y, n.dir, true, '', n.corpo, n.pele, false, n.visual || n.roupa, 1, 1, 0, 0, 0, '', 0, 'npc',
      undefined, undefined, n.mao, n.esquerda, n.cabelo, n.cabeloCor || undefined, n.barba, n.barbaCor || undefined,
      n.corpo === 'human-female');
    const r = remotes.get(id);
    if (!r) continue;
    r.npcId = n.id;
    r.titulo = n.titulo;
    r.temLoja = n.temLoja;
    r.temCorretor = n.temCorretor;
    r.temBanco = n.temBanco;
    r.alcance = n.alcance;
  }
}
reporNpcs();
opts.onSala?.();

function soltarVitalPendente(id: number){
  const pend = pendingVitals.get(id);
  if (!pend) return;
  pendingVitals.delete(id);
  const r = remotes.get(id);
  // Morte atrasada de um id velho não zera o esqueleto que acabou de entrar.
  if (r && r.hits > 0 && pend.hits <= 0) return;
  aplicarVitals(id, pend.hits, pend.hitsMax, pend.mana, pend.manaMax, pend.poisoned);
}

function posSeguida(id: number, now: number): { x: number; y: number } | null {
  if (id === youId) return { x: player.x, y: player.y };
  const r = remotes.get(id);
  return r ? remotePos(r, now) : null;
}

/** Para o remoto onde ele está agora: corta o passo pela metade e esvazia a fila. */
function congelarRemote(r: { fila: Step[]; fromX: number; fromY: number; toX: number; toY: number; fromAt: number; toAt: number }, now: number){
  if (r.fila.length === 0 && r.fromX === r.toX && r.fromY === r.toY) return;
  const p = remotePos(r, now);
  r.fila.length = 0;
  r.fromX = r.toX = p.x;
  r.fromY = r.toY = p.y;
  r.fromAt = r.toAt = now;
}

function remotePos(r: { fromX: number; fromY: number; toX: number; toY: number; fromAt: number; toAt: number }, now: number){
  const span = Math.max(1, r.toAt - r.fromAt);
  const t = Math.min(1, Math.max(0, (now - r.fromAt) / span));
  return { x: r.fromX + (r.toX - r.fromX) * t, y: r.fromY + (r.toY - r.fromY) * t };
}

function dropRemotesNamed(name: string, keepId: number, kind?: string){
  if (!name) return;
  // Cadáver, mob e NPC reusam o nome do dono/ficha. Não são reconexão do
  // jogador — se apagasse o remoto vivo, o revive não tinha sprite.
  if (kind === 'corpse' || kind === 'monster' || kind === 'npc' || kind === 'dummy' || kind === 'guard') return;
  const key = name.toLowerCase();
  for (const [id, remote] of remotes){
    if (id !== keepId && remote.kind !== 'monster' && remote.kind !== 'npc' && remote.kind !== 'corpse' && remote.kind !== 'dummy' && remote.kind !== 'guard' && remote.name.toLowerCase() === key)
      remotes.delete(id);
  }
}

function absorbSnapshot(entities: ReadonlyArray<NetEntered>){
  remotes.clear();
  stamRemota.clear();
  remoteDieSfx.clear();
  dyingRemotes.clear();
  pendingVitals.clear();
  pendingGolpes.length = 0;
  for (const e of entities){
    if (e.id === youId) {
      applyYouDead(e.dead, true);
      applyYouMount(e.mount);
      applyYouCloth(e.cloth);
      applyYouWeapon(e.weapon);
      applyYouShield(e.shield);
      applyYouSkin(e.skin);
      applyYouSkinHue(e.skinHue);
      applyYouHair(e.hairStyle, e.hairHue, e.beardStyle, e.beardHue, e.feminino);
      if (e.notoriety !== undefined) youNotoriety = e.notoriety;
      if (e.guildTag !== undefined) youGuildTag = e.guildTag;
      if (e.cargo !== undefined) youCargo = e.cargo;
      if (e.vip !== undefined) youVip = e.vip;
      if (e.selo !== undefined) youSelo = e.selo;
      if (e.hitsMax) { player.hp = e.hits ?? 0; player.hpMax = e.hitsMax; }
      if (e.manaMax != null) { player.mana = e.mana ?? 0; player.manaMax = e.manaMax; }
      player.envenenado = !!e.poisoned;
      continue;
    }
    upsertRemote(e.id, e.name, e.x, e.y, e.dir, true, e.mount, e.skin, e.skinHue, e.dead, e.cloth, e.hits, e.hitsMax, e.mana, e.manaMax, e.notoriety, e.guildTag, e.rel, e.kind, e.defId, e.poisoned, e.weapon, e.shield, e.hairStyle, e.hairHue, e.beardStyle, e.beardHue, e.feminino, e.aparencia, e.cargo, e.vip, e.oculto, e.selo);
    zRemoto.set(e.id, e.z ?? 0);
    anotarStam(e);
  }
  reporNpcs();
}

function applyRemoteMount(id: number, mount: string){
  applyLook(id, { mount });
}

function applyLook(id: number, look: {
  mount: string; skin?: string; dead?: boolean; cloth?: string; weapon?: string; shield?: string;
  notoriety?: number; guildTag?: string; rel?: number;
  hairStyle?: string; hairHue?: number; beardStyle?: string; beardHue?: number; feminino?: boolean;
  vip?: boolean; selo?: number;
}){
  if (id === youId){
    if (look.vip !== undefined) youVip = look.vip;
    if (look.selo !== undefined) youSelo = look.selo;
    if (look.notoriety !== undefined) youNotoriety = look.notoriety;
    if (look.guildTag !== undefined) youGuildTag = look.guildTag;
    if (look.dead === false) lifeLook = look;
    if (look.dead !== undefined) applyYouDead(look.dead);
    applyYouMount(youDead ? null : look.mount);
    if (look.cloth !== undefined) applyYouCloth(youDead ? null : look.cloth);
    if (look.weapon !== undefined) applyYouWeapon(youDead ? null : look.weapon);
    if (look.shield !== undefined) applyYouShield(youDead ? null : look.shield);
    if (!youDead && look.skin) applyYouSkin(look.skin);
    if (look.hairStyle !== undefined || look.hairHue !== undefined || look.beardStyle !== undefined) {
      applyYouHair(look.hairStyle ?? youHairStyle, look.hairHue ?? youHairHue, look.beardStyle ?? youBeardStyle, look.beardHue ?? youBeardHue, look.feminino);
    }
    return;
  }
  const remote = remotes.get(id);
  if (!remote) return;
  if (remote.kind === 'monster' || remote.kind === 'npc' || remote.kind === 'corpse' || remote.kind === 'dummy' || remote.kind === 'guard') return;
  if (look.vip !== undefined) remote.vip = look.vip;
  if (look.selo !== undefined) remote.selo = look.selo;
  if (look.notoriety !== undefined) remote.notoriety = look.notoriety;
  if (look.guildTag !== undefined) remote.guildTag = look.guildTag;
  if (look.rel !== undefined) remote.rel = look.rel;
  if (look.dead === true) {
    const wasAlive = !remote.dead;
    remote.dead = true;
    remote.mount = '';
    remote.cloth = '';
    remote.weapon = '';
    remote.shield = '';
    remote.skin = SKIN_PADRAO;
    remote.idleAt = performance.now();
    remote.wasMoving = false;
    ensureSkin(remote.skin);
    if (wasAlive) cueRemoteDie(id);
    return;
  }
  if (look.dead === false) {
    remote.dead = false;
    remoteDieSfx.delete(id);
    remote.deathAt = 0;
    const nextSkin = (look.skin ?? '').trim();
    remote.skin = !nextSkin || nextSkin === SKIN_PADRAO ? 'human-male' : nextSkin;
    remote.idleAt = performance.now();
    remote.wasMoving = false;
    ensureSkin(remote.skin);
  }
  remote.mount = ehMontaria(look.mount) ? look.mount : '';
  if (remote.mount) ensureAnimal(remote.mount);
  if (look.cloth !== undefined) {
    remote.cloth = fioDoVisual(look.cloth);
    if (remote.cloth) ensureCloth(remote.cloth);
  }
  if (look.weapon !== undefined) {
    remote.weapon = remote.dead ? '' : look.weapon.trim();
    if (remote.weapon) ensureArma(remote.weapon);
  }
  if (look.shield !== undefined) {
    remote.shield = remote.dead ? '' : escudoDoFio(look.shield);
    if (remote.shield) ensureEscudo(remote.shield);
  }
  if (look.hairStyle !== undefined) {
    remote.hairStyle = estiloDaFolha(look.hairStyle);
    if (remote.hairStyle) ensureCabelo(remote.hairStyle);
  }
  if (look.hairHue !== undefined) remote.hairHue = look.hairHue;
  if (look.feminino !== undefined) remote.feminino = look.feminino;
  if (look.beardStyle !== undefined) {
    remote.beardStyle = remote.feminino ? '' : estiloDaFolha(look.beardStyle);
    if (remote.beardStyle) ensureBarba(remote.beardStyle);
  }
  if (look.beardHue !== undefined) remote.beardHue = look.beardHue;
}

function startStep(dx: number, dy: number, modo: 'comando' | 'autor' | 'seguir' = 'comando'){
  if (net && !joined) return false;
  dx = clamp(dx, -1, 1);
  dy = clamp(dy, -1, 1);
  if (!dx && !dy) return false;
  let atravessa = false;
  if (!canStep(player.tx, player.ty, dx, dy, modo !== 'comando', player.tz)) {
    // O passo do jogador pode atravessar corpo com a barra cheia; o servidor decide e desfaz se recusar.
    const ax = player.tx + dx, ay = player.ty + dy;
    if (modo !== 'comando' || !canStep(player.tx, player.ty, dx, dy, true, player.tz) || !soCorpos(ax, ay) || !podeAtravessar())
      return false;
    atravessa = !youDead && youCargo === 0;
  }
  player.nx = player.tx + dx;
  player.ny = player.ty + dy;
  player.nz = passoNz;
  player.dir = dirIndex(dx, dy);
  player.t = 0;
  player.moving = true;
  player.authoredRun = modo === 'autor';
  // A IA da sala conta 1 tile Chebyshev por passo. passoDeGrade (~1.29)
  // é o ritmo do WASD em pixels; no follow isso atrasa o sprite ~23%
  // por segundo — golpe longe, depois snap de dessinc.
  stepVis = modo === 'comando' ? passoDeGrade(dx, dy) : 1;
  if (modo === 'comando' && net && opts.onStep){
    seq += 1;
    pending.push({ seq, dir: player.dir, x: player.nx, y: player.ny });
    opts.onStep(seq, player.dir, pressionada('galopar') && player.stam >= STAM_GALOPE);
  }
  if (atravessa) player.stam = Math.max(0, player.stam - player.stamMax * ATRAVESSAR_FRACAO);
  return true;
}

function pedirProximoPasso(){
  if (passoTravado) return false;
  // Paralyze segura o passo (o servidor recusa); sem isto o boneco andaria e voltaria.
  if (paralizado) { path = []; return false; }
  if (coletaTravada) {
    const [cx, cy] = keyDelta();
    if (cx || cy || path.length) {
      path = [];
      const agora = performance.now();
      if (agora - coletaAvisoEm > 3000) {
        coletaAvisoEm = agora;
        opts.onColetaTravada?.();
      }
    }
  }
  const [kx, ky] = coletaTravada ? [0, 0] : keyDelta();
  if (kx || ky){
    dummyChegada = null;
    serverPath = [];
    player.authoredRun = false;
    swingPendenteEm = 0;
    swingPendenteDir = null;
    swingPendenteMs = 0;
    if (startStep(kx, ky)) return true;
    // Segurando o mouse: as vizinhas na ordem do cursor (na escada, a diagonal que não entra vira o degrau certo,
    // e não o lado que leva para a porta).
    const rumoMouse = !teclaDeAndar() && segue?.andou ? segue.rumo : null;
    if (rumoMouse) {
      for (const d of rumoMouse.slice(1)) if (startStep(DIRS[d].dx, DIRS[d].dy)) return true;
    } else {
      if (kx && startStep(kx, 0)) return true;
      if (ky && startStep(0, ky)) return true;
    }
    player.dir = dirIndex(kx, ky);
    if (!insideBounds(player.tx + kx, player.ty + ky)) nudgeFence();
    return false;
  }
  const n = path.shift();
  if (n){
    const ok = startStep(n.x - player.tx, n.y - player.ty);
    if (ok) tentarDummyChegada();
    else path = [];
    return ok;
  }
  const s = serverPath.shift();
  if (!s) return false;
  const modo = youDead ? 'seguir' : 'autor';
  const dx = clamp(s.x - player.tx, -1, 1);
  const dy = clamp(s.y - player.ty, -1, 1);
  if (startStep(dx, dy, modo)) {
    if (player.nx !== s.x || player.ny !== s.y) serverPath.unshift(s);
    return true;
  }
  serverPath.length = 0;
  if (serverSelfX !== player.tx || serverSelfY !== player.ty)
    snapSelf(serverSelfX, serverSelfY);
  return false;
}

/** A IA move o self no servidor. Um tile por vez, sem recortar a
 *  interpolação atual. Snap em dessinc de mapa e quando o caminho não
 *  fecha — senão o sprite fica na casa ao lado da ankh até o reload. */
function followServerSelf(x: number, y: number){
  const ancorX = player.moving ? player.nx : player.tx;
  const ancorY = player.moving ? player.ny : player.ty;
  const jump = chebyshev(ancorX, ancorY, x, y);
  if (jump === 0) return;
  // O ritmo dos passos do servidor, suavizado: a sala anda a 6 casas/s em
  // tiques de 100 ms, então os passos chegam ora a 100, ora a 200 ms. Sem a
  // média o sprite alternava 10 e 5 casas/s e parecia travar.
  const agora = performance.now();
  const intervalo = agora - selfPaceAt;
  if (selfPaceAt > 0 && jump <= 1.01 && intervalo > 40 && intervalo < 800) {
    const medido = Math.min(PACE_MAX, Math.max(PACE_MIN, jump / (intervalo / 1000)));
    selfPace = selfPace > 0 ? selfPace * 0.75 + medido * 0.25 : medido;
  } else if (intervalo >= 800 || jump > 1.01)
    selfPace = SPEED_TROT;
  selfPaceAt = agora;
  if (jump > 32){
    snapSelf(x, y);
    return;
  }
  const fim = serverPath.length ? serverPath[serverPath.length - 1] : null;
  if (fim && fim.x === x && fim.y === y) {
    if (!player.moving) pedirProximoPasso();
    return;
  }
  const noCaminho = serverPath.findIndex(s => s.x === x && s.y === y);
  if (noCaminho >= 0) {
    serverPath = serverPath.slice(0, noCaminho + 1);
    if (!player.moving) pedirProximoPasso();
    return;
  }
  if (fim && chebyshev(fim.x, fim.y, x, y) === 1
      && chebyshev(ancorX, ancorY, x, y) >= chebyshev(ancorX, ancorY, fim.x, fim.y)) {
    const extra = passoGuloso(fim.x, fim.y, x, y);
    if (extra) {
      serverPath.push(extra);
      if (!player.moving) pedirProximoPasso();
      return;
    }
  }
  const guloso = encadearAte(ancorX, ancorY, x, y, Math.max(8, jump));
  const chegou = guloso.length > 0
    && guloso[guloso.length - 1].x === x
    && guloso[guloso.length - 1].y === y;
  let rota = guloso;
  if (!chegou) {
    const longa = findPath(ancorX, ancorY, x, y, true);
    if (longa.length > 0) rota = longa;
  }
  const destino = rota[rota.length - 1];
  if (!destino || destino.x !== x || destino.y !== y) {
    snapSelf(x, y);
    return;
  }
  serverPath = rota;
  if (!player.moving) pedirProximoPasso();
}

/** Alguma tecla de andar apertada? */
function teclaDeAndar(){
  return ANDAR.some(([acao]) => pressionada(acao));
}

function keyDelta(){
  let dx = 0, dy = 0;
  for (const [acao, ax, ay] of ANDAR){
    if (!pressionada(acao)) continue;
    dx += ax;
    dy += ay;
  }
  // Sem tecla, o botão do mouse segurado anda no rumo do cursor, como uma tecla de direção.
  if (!dx && !dy && segue?.andou && segue.rumo.length) return [DIRS[segue.rumo[0]].dx, DIRS[segue.rumo[0]].dy];
  return [clamp(dx, -1, 1), clamp(dy, -1, 1)];
}

let last = performance.now();
let raf: number | null = null;

function frame(now: number){
  resizeSePreciso();
  novoQuadroBaloes();
  if (now - last > VAO_MS || quadrosDesdePintura > RAJADA_QUADROS || (now < ressincAte && temAtraso()))
    ressincronizar(now);
  quadrosDesdePintura = 0;
  const dt = Math.min((now - last) / 1000, 0.05);
  last = now;
  const time = now / 1000;
  soltarMortos(now);
  if (joined) somOcioso(now);
  seguirCursor(now);

  /* --- galope e vigor --- */
  const segueServidor = !youDead && (player.authoredRun || serverPath.length > 0);
  const podeGalopar = !youDead && player.stam >= STAM_GALOPE;
  const wantsGallop = podeGalopar && (
    (pressionada('galopar') && player.stam >= STAM_GALOPE)
    || segueServidor
  );
  const speed = youDead
    ? SPEED_GHOST
    : segueServidor
      ? velocidadeDeSeguir()
      : wantsGallop
        ? velocidadeDoPasso(true)
        : velocidadeDoPasso(false);

  /* --- avanço do passo --- */
  if (player.moving){
    player.moveStamp = now;   // entre um passo e outro há um quadro parado: sem isso a animação pisca
    player.t += dt * speed / stepVis;
    player.gait += dt * speed / stepVis;
    while (player.t >= 1){
      const resto = player.t - 1;
      player.tx = player.nx; player.ty = player.ny; player.tz = player.nz;
      player.t = 1; player.moving = false;
      somDePasso(now, wantsGallop);
      if (wantsGallop && !player.authoredRun) player.stam = Math.max(0, player.stam - STAM_GALOPE);
      // Sem isto o WASD encadeia o passo seguinte no mesmo quadro em que
      // o avatar chega no portal. O aceite ainda vale nos SQMs próximos,
      // mas o gump precisa aparecer com o personagem parado.
      if (!youDead && portalNoTile(player.tx, player.ty)) {
        travarPasso(true);
        marcarPortal();
        break;
      }
      if (!pedirProximoPasso()) break;
      player.t = resto > 0.2 ? 0 : resto;
    }
  }

  if (!player.moving) pedirProximoPasso();
  soltarVidaPendente();
  checarLugarPenta();

  const [andaX, andaY] = keyDelta();
  const querAndar = player.moving || path.length > 0 || serverPath.length > 0
    || andaX !== 0 || andaY !== 0 || player.authoredRun;
  // O atraso de 400 ms só cobre o vão entre dois passos. Sem um passo a
  // caminho, a pose para no mesmo quadro em que o pé chega no tile.
  const riding = querAndar && (player.moving || now - (player.moveStamp || 0) < 400);
  if (!riding)
    player.stam = Math.min(player.stamMax, player.stam + dt * STAM_REGEN);

  for (const remote of remotes.values()){
    // Mob morto não anda (Renato, 02/11/2026): o corpo parava onde o passo terminaria. Fica onde caiu.
    if (remote.kind === 'monster' && (remote.deathAt > 0 || (remote.hits <= 0 && remote.hitsMax > 0))) { congelarRemote(remote, now); continue; }
    if (now >= remote.toAt) avancarFilaRemote(remote, now);
    const steps = chebyshev(remote.fromX, remote.fromY, remote.toX, remote.toY);
    if (steps > 0.02 && now < remote.toAt){
      remote.moveStamp = now;
      const span = Math.max(0.001, (remote.toAt - remote.fromAt) / 1000);
      remote.gait += dt * (steps / span);
    }
  }
  if (!net) {
    player.mana = Math.min(player.manaMax, player.mana + dt * 1.2);
    tickLocalWander(dt);
  }

  player.x = lerp(player.tx, player.nx, player.moving ? player.t : 1);
  player.y = lerp(player.ty, player.ny, player.moving ? player.t : 1);
  soltarMortesPendentes(now);
  talvezSwing();

  /* --- câmera --- */
  if (!mapa.dentro(player.tx, player.ty)) {
    if (mapa.dentro(serverSelfX, serverSelfY)) snapSelf(serverSelfX, serverSelfY);
    else snapSelf(mapa.spawn.x, mapa.spawn.y);
  }
  // O pé fora de lugar (troca de mapa, teleporte, caçada): volta ao padrão da casa ou ao terreno.
  const peOk = peValido(mapa.id, player.tx, player.ty, player.tz);
  if (!player.moving && (peOk === false || (peOk === null && player.tz !== mapa.zEm(player.tx, player.ty))))
    player.tz = player.nz = zPe(player.tx, player.ty);
  const pz = zJogador();
  const targetX = wx(player.x, player.y), targetY = wy(player.x, player.y) - pz * Z_PX;
  // Presa no pé. A suavização antiga ficava ~um tile atrás na corrida e,
  // ao parar, o chão ainda andava até a câmera alcançar o tile.
  cam.x = targetX;
  cam.y = targetY;
  if (blocosVisiveis(mapa, cam, VW, VH, zoom).length === 0)
    aquecerEm(mapa, player.tx, player.ty);

  if (!cenaPronta) {
    raf = requestAnimationFrame(frame);
    return;
  }
  if (!cargaPronta && (bonecoDesenhavel() || time - cargaDesde > CARGA_MAX_MS)) {
    cargaPronta = true;
    jaEntrou = true;
    avisarCarga('pronto');
  }

  /* --- cenário --- */
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.imageSmoothingEnabled = false;
  if (!marFundo || !marLuz) aquecerMar();
  if (marFundo && marLuz) {
    ctx.fillStyle = marFundo;
    ctx.fillRect(0, 0, VW, VH);
    ctx.fillStyle = marLuz;
    ctx.fillRect(0, 0, VW, VH);
  }

  desenharChao(ctx, mapa, cam, VW, VH, zoom);
  if (!REDUCED) brilharAgua(ctx, mapa, cam, VW, VH, zoom, time);
  // O forro é uma foto dos altos na régua de sempre; no relevo a régua é outra, e os altos vão um a um.
  const forro = mapa.relevo ? null : forroDeAltos(mapa);
  if (forro) desenharForro(ctx, forro, cam, VW, VH, zoom);

  type Drawable =
    | { k: number; kind: 'e'; e: (typeof mapa.altos)[number] }
    | { k: number; kind: 'player' }
    | { k: number; kind: 'remote'; id: number }
    | { k: number; kind: 'fx'; s: import('./efeitos').ShotEfeito; tx: number; ty: number; tz: number }
    | { k: number; kind: 'portal'; x: number; y: number; z: number; efeito: string }
    | { k: number; kind: 'penta'; x: number; y: number; z: number; cor: number; modo: 'chama' | 'orbe' | 'forte' }
    | { k: number; kind: 'pentaRitual' }
    | { k: number; kind: 'c'; p: PecaPosta }
    | { k: number; kind: 't'; x: number; y: number };

  const drawList: Drawable[] = [];
  const pzDraw = zJogador();
  // A régua de desenho: x + y + z do terreno + a fração da prio (corpo: z do pé + 2), a mesma das peças da casa.
  // Sentado numa cadeira (techspec-sentar): o corpo vai na frente da peça (a prio passa do alto dela).
  const youSentado = !youDead && !ehMontaria(youMount) && !riding
    ? sentadoEm(Math.round(player.x), Math.round(player.y), pzDraw, player.dir) : null;
  // Andando, o corpo entra na ordem pelo tile mais à frente (o de chegada, quando é ele), como no cliente da UO: sem
  // isso o piso da casa de chegada era desenhado por cima dos pés durante o passo e o boneco parecia afundar e voltar.
  const [ordX, ordY] = tileDeOrdem(player.tx, player.ty, player.moving ? player.nx : player.tx, player.moving ? player.ny : player.ty, player.x, player.y);
  const kJogador = regua(ordX, ordY, mapa.zEm(Math.round(player.x), Math.round(player.y)), Math.max(pzDraw + 2, youSentado?.prio ?? 0));
  const pScreenX = w2sx(wx(player.x, player.y));
  const pScreenY = w2sy(wy(player.x, player.y) + TH / 2 - pzDraw * Z_PX);
  const nowDraw = performance.now();
  const corpos = [{ k: kJogador, sx: pScreenX, sy: pScreenY }];
  for (const remote of remotes.values()){
    const rp = remotePos(remote, nowDraw);
    const rz = zDoRemoto(remote.id, rp);
    corpos.push({
      k: regua(rp.x, rp.y, mapa.zEm(Math.round(rp.x), Math.round(rp.y)), rz + 2),
      sx: w2sx(wx(rp.x, rp.y)),
      sy: w2sy(wy(rp.x, rp.y) + TH / 2 - rz * Z_PX)
    });
  }
  const visBlocos = blocosVisiveis(mapa, cam, VW, VH, zoom);
  for (const { bx, by } of visBlocos){
    for (const e of mapa.altosNoBloco(bx, by)){
      // o forro é uma foto: item animado fica fora dele e é desenhado todo quadro
      if (forro && !ehAnimado(mapa, e.tile) && !estaticoNaFrente(mapa, e, cam, VW, VH, zoom, corpos)) continue;
      drawList.push({
        k: mapa.relevo ? regua(e.x, e.y, 0, e.z + ((mapa.estaticosTs.tile(e.tile).altura ?? 0) > 0 ? 1 : 0)) : e.x + e.y + e.z,
        kind: 'e', e
      });
    }
    // Relevo: a parede da caverna na frente de um corpo é desenhada de novo por cima dele (o chão assado fica embaixo).
    const rel = mapa.relevo;
    if (rel) {
      for (const c of rel.cobreNoBloco(bx, by)) {
        const r = rel.casa(c.x, c.y);
        if (!r) continue;
        const x0 = w2sx(wx(c.x, c.y)) - r.ox * zoom;
        const y0 = w2sy(wy(c.x, c.y) - rel.zCanto(c.x, c.y) * Z_PX) - r.oy * zoom;
        const x1 = x0 + r.c.width * zoom, y1 = y0 + r.c.height * zoom;
        if (!corpos.some(p => p.sx + 40 > x0 && p.sx - 40 < x1 && p.sy + 10 > y0 && p.sy - 110 < y1)) continue;
        drawList.push({ k: regua(c.x, c.y, 0, rel.zMedio(c.x, c.y) - 1), kind: 't', x: c.x, y: c.y });
      }
    }
  }
  // Outros jogadores também abrem a porta quando passam (techspec-casas §03.14).
  const passantes: { x: number; y: number; z: number }[] = [];
  for (const remote of remotes.values()) {
    if (remote.kind && remote.kind !== 'player') continue;
    const rp = remotePos(remote, nowDraw);
    passantes.push({ x: Math.round(rp.x), y: Math.round(rp.y), z: zDoRemoto(remote.id, rp) });
  }
  pecasDasCasas(mapa, { x: Math.round(player.x), y: Math.round(player.y), z: pzDraw }, cam, VW, VH, zoom,
    (k, p) => drawList.push({ k, kind: 'c', p }), passantes);
  drawList.push({ k: kJogador, kind: 'player' });
  for (const remote of remotes.values()){
    const rp = remotePos(remote, nowDraw);
    const rz = mapa.zEm(Math.round(rp.x), Math.round(rp.y));
    const sentou = sentadoDoRemoto(remote, rp, nowDraw);
    const andando = nowDraw < remote.toAt;
    const [rox, roy] = tileDeOrdem(remote.fromX, remote.fromY, andando ? remote.toX : remote.fromX, andando ? remote.toY : remote.fromY, rp.x, rp.y);
    drawList.push({ k: regua(rox, roy, rz, Math.max(zDoRemoto(remote.id, rp) + 2, sentou?.prio ?? 0)) - 0.001 + (remote.kind === 'corpse' ? -0.04 : 0), kind: 'remote', id: remote.id });
  }
  const agoraFx = performance.now();
  forEachProjetil(agoraFx, (s, _p, tx, ty, tz) => {
    const seg = s.seguir ? posSeguida(s.seguir, nowDraw) : null;
    const x = seg?.x ?? tx;
    const y = seg?.y ?? ty;
    const z = seg ? mapa.zEm(Math.round(x), Math.round(y)) : tz;
    const terrainZ = mapa.zEm(Math.round(x), Math.round(y));
    drawList.push({ k: mapa.relevo ? regua(x, y, 0, z + 3) : x + y + terrainZ, kind: 'fx', s, tx: x, ty: y, tz: z });
  }, id => posSeguida(id, nowDraw));
  for (const p of portaisGuardados) {
    const z = mapa.zEm(p.x, p.y);
    drawList.push({ k: (mapa.relevo ? regua(p.x, p.y, 0, z) : p.x + p.y + z) - 0.02, kind: 'portal', x: p.x, y: p.y, z, efeito: p.efeito });
  }
  if (mapa.id === MAPA_SANTUARIO) {
    pontasDoMapa(mapa.regioes).forEach((p, i) => {
      if (!p) return;
      const z = mapa.zEm(p.x, p.y);
      const modo = pentaCor === i ? 'forte' : (pentaMascara & (1 << i)) ? 'orbe' : 'chama';
      drawList.push({ k: p.x + p.y + z + 0.05, kind: 'penta', x: p.x, y: p.y, z, cor: i, modo });
    });
    const c = centroDoMapa(mapa.regioes);
    if (c && (pentaMascara || pentaRitual)) drawList.push({ k: c.x + c.y + 0.9, kind: 'pentaRitual' });
  }
  drawList.sort((a, b) => a.k - b.k);

  let playerHeadY = CABECA_PADRAO;
  /* Nome, barra de vida e balão dos outros saem depois da luz (techspec-dia-e-noite): à noite o mundo escurece,
     o texto não. */
  const depoisDaLuz: Array<() => void> = [];
  /* As magias brilham por conta própria (techspec-magias-animacoes): o efeito sai depois da noite, como no cliente
     da UO, senão a faísca e o projétil somem no escuro. Fica abaixo do nome e das barras. */
  const efeitosDepoisDaLuz: Array<() => void> = [];

  for (const it of drawList){
    if (it.kind === 'e'){
      const e = it.e;
      const occl = it.k > (mapa.relevo ? kJogador : player.x + player.y + mapa.zEm(Math.round(player.x), Math.round(player.y))) && dentroDaPlanta(e, player.x, player.y);
      desenharEstatico(ctx, mapa, e, cam, VW, VH, zoom, occl ? 0.34 : 1);
    } else if (it.kind === 'c'){
      desenharPecaDeCasa(ctx, mapa, it.p, cam, VW, VH, zoom);
    } else if (it.kind === 't'){
      const rel = mapa.relevo, r = rel?.casa(it.x, it.y);
      if (rel && r) ctx.drawImage(r.c, w2sx(wx(it.x, it.y)) - r.ox * zoom,
        w2sy(wy(it.x, it.y) - rel.zCanto(it.x, it.y) * Z_PX) - r.oy * zoom, r.c.width * zoom, r.c.height * zoom);
    } else if (it.kind === 'remote'){
      const remote = remotes.get(it.id);
      if (!remote) continue;
      const rp = remotePos(remote, nowDraw);
      const rz = zDoRemoto(remote.id, rp);
      const rx = w2sx(wx(rp.x, rp.y));
      const ry = w2sy(wy(rp.x, rp.y) + TH / 2 - rz * Z_PX);
      ctx.save();
      ctx.globalAlpha = 0.32;
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.ellipse(rx, ry - 2 * zoom, (ehMontaria(remote.mount) ? 22 : 16) * zoom, (ehMontaria(remote.mount) ? 10 : 7) * zoom, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      const remoteRiding = nowDraw - (remote.moveStamp || 0) < 240;
      const clock = tickIdleClock(remoteRiding, nowDraw, remote.idleAt, remote.wasMoving);
      remote.idleAt = clock.idleAt;
      remote.wasMoving = clock.wasMoving;
      const cadaver = remote.kind === 'corpse';
      const dummy = remote.kind === 'dummy';
      const guarda = remote.kind === 'guard';
      const monster = !cadaver && !dummy && !guarda && (remote.kind === 'monster' || !!remote.defId);
      const npc = remote.kind === 'npc';
      const headY = cadaver
        ? blitCadaver(ctx, remote.dir, rx, ry, remote.deathAt, nowDraw)
        : dummy
        ? blitDummy(ctx, remote.defId, nowDraw, rx, ry, remote.attackAt)
        : monster
        ? blitMob(ctx, remote.aparencia || remote.defId, remote.dir, remoteRiding, remote.gait, nowDraw, rx, ry, remote.attackAt, remote.deathAt, remote.hits)
        : comOculto(ctx, remote.oculto, () => blitAvatar(ctx, remote.skin || SKIN_PADRAO, remote.skinHue, remote.mount, remote.cloth, remote.dir, remoteRiding, remote.gait, nowDraw, rx, ry, remote.idleAt, remote.attackAt, remote.weapon, remote.shield, remote.hairStyle, remote.hairHue, remote.beardStyle, remote.beardHue, remote.feminino, remoteRiding && remote.pace >= PACE_CORRIDA, conjuracaoDe(remote.id, nowDraw),
            remoteRiding ? null : sentadoDoRemoto(remote, remotePos(remote, nowDraw), nowDraw)));
      const isTarget = !npc && !cadaver && targetId === remote.id;
      const warHostil = isTarget && warTargetId === remote.id && remote.kind === 'player';
      depoisDaLuz.push(() => {
      if (isTarget && dummy) {
        ctx.save();
        ctx.strokeStyle = NICK.target;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(rx, ry - 2 * zoom, 18 * zoom, 8 * zoom, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
      const temTitulo = npc && !!remote.titulo;
      const nameY = ry - (headY - NICK_DA_CABECA) * zoom - (temTitulo ? 13 : 0);
      ctx.font = '600 13px Spectral, Georgia, serif';
      ctx.textAlign = 'center';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(0,0,0,.85)';
      const plate = dummy && remote.name.startsWith('manequim.')
        ? t(remote.name)
        : monster || npc || cadaver || dummy || guarda ? remote.name : nickPlate(remote.guildTag, remote.name, remote.cargo);
      const staff = !monster && !npc && !cadaver && !dummy && !guarda && (remote.cargo === 1 || remote.cargo === 2);
      const fill = npc ? NICK.npc : dummy ? NICK.dummy : guarda ? NICK.guard : monster ? NICK.monster : staff ? NICK.staff : nickFill({
        notoriety: remote.notoriety,
        dead: remote.dead || cadaver,
        inParty: remote.rel === 3,
        guildAlly: remote.rel === 1,
        enemyGuild: remote.rel === 2
      });
      ctx.save();
      if (ignored.has(remote.id)) ctx.globalAlpha = 0.55;
      ctx.fillStyle = isTarget && dummy ? NICK.target : fill;
      const vipNome = !monster && !npc && !cadaver && !dummy && !guarda && !!remote.vip;
      escreverPlaca(ctx, plate, rx, nameY, vipNome, !!(remote.dead || cadaver), !monster && !npc && !cadaver && !dummy && !guarda ? remote.selo : 0);
      if (isTarget && dummy) {
        const w = ctx.measureText(plate).width;
        ctx.strokeStyle = NICK.target;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(rx - w / 2, nameY + 4);
        ctx.lineTo(rx + w / 2, nameY + 4);
        ctx.stroke();
      }
      if (npc && remote.titulo) {
        const sub = t(remote.titulo);
        ctx.font = 'italic 11px Spectral, Georgia, serif';
        ctx.strokeText(sub, rx, nameY + 13);
        ctx.fillText(sub, rx, nameY + 13);
      }
      ctx.restore();
      marcarCabeca(remote.id, rx, nameY);
      if (remote.id !== youId && !npc && !cadaver && !dummy && !guarda && !remote.dead && remote.hits > 0 && remote.hitsMax > 0)
        drawHitsBar(ctx, rx, ry, remote.hits, remote.hitsMax, remote.envenenado, isTarget, warHostil);
      drawBalloon(ctx, rx, nameY - 16 * zoom, remote.id, nowDraw);
      ancorarBalao(remote.id, rx, baseDaFala(remote.id, nameY - 16 * zoom, nowDraw));
      });
    } else if (it.kind === 'fx'){
      const def = defEfeito(it.s.id);
      const img = imgEfeito(it.s.id);
      if (!def || !img) continue;
      const bruto = Math.floor((agoraFx - it.s.inicio) / 1000 * def.fps);
      const quadro = def.modo === 'alvo' ? Math.min(def.quadros - 1, bruto) : bruto;
      const fromX = wx(it.s.deX, it.s.deY);
      const fromY = wy(it.s.deX, it.s.deY) + TH / 2 - (it.s.deZ + peitoZ()) * Z_PX;
      const toX = wx(it.s.paraX, it.s.paraY);
      const toY = wy(it.s.paraX, it.s.paraY) + TH / 2 - (it.s.paraZ + peitoZ()) * Z_PX;
      const angulo = Math.atan2(w2sy(toY) - w2sy(fromY), w2sx(toX) - w2sx(fromX)) + def.rotacao;
      const mundoX = wx(it.tx, it.ty);
      const mundoY = wy(it.tx, it.ty) + TH / 2 - it.tz * Z_PX;
      efeitosDepoisDaLuz.push(() => desenharProjetil(ctx, img, def, quadro, mundoX, mundoY, angulo, cam, VW, VH, zoom));
    } else if (it.kind === 'penta'){
      const sx = w2sx(wx(it.x, it.y));
      const sy = w2sy(wy(it.x, it.y) + TH / 2 - it.z * Z_PX);
      desenharPonta(ctx, sx, sy, zoom, CORES_PENTA[it.cor], it.modo, agoraFx / 1000);
    } else if (it.kind === 'pentaRitual'){
      const c = centroDoMapa(mapa.regioes);
      if (c) {
        const tela = (x: number, y: number, alto = 0) =>
          ({ x: w2sx(wx(x, y)), y: w2sy(wy(x, y) + TH / 2 - mapa.zEm(x, y) * Z_PX) - alto * zoom });
        const pontas = pontasDoMapa(mapa.regioes).map(p => (p ? tela(p.x, p.y, 60) : null));
        desenharRitual(ctx, pontas, tela(c.x, c.y, 30), zoom, pentaMascara, pentaRitual || pentaMascara === TODAS_PENTA, agoraFx / 1000);
      }
    } else if (it.kind === 'portal'){
      const agoraS = agoraFx / 1000;
      const sprite = spriteDoPortal(it.efeito, agoraS);
      const def = defEfeito(sprite);
      const img = imgEfeito(sprite);
      const mundoX = wx(it.x, it.y);
      const mundoY = wy(it.x, it.y) + TH / 2 - it.z * Z_PX;
      const tema = it.efeito === EFEITO_PORTAL_PENTA ? corDoPortalPenta(pentaCor, agoraS) : null;
      const telaX = (mundoX - cam.x) * zoom + VW / 2;
      const telaY = (mundoY - cam.y) * zoom + VH / 2;
      if (tema) desenharSeloPortal(ctx, telaX, telaY, zoom, tema.cor, tema.alfa, tema.desperto, agoraS);
      if (def && img) {
        const quadro = Math.floor(agoraS * def.fps);
        if (tema && tema.alfa < 1) { ctx.save(); ctx.globalAlpha = 0.35 + 0.65 * tema.alfa; }
        desenharProjetil(ctx, img, def, quadro, mundoX, mundoY, 0, cam, VW, VH, zoom);
        if (tema && tema.alfa < 1) ctx.restore();
      }
      if (tema) desenharParticulasPortal(ctx, telaX, telaY, zoom, tema.cor, tema.alfa, tema.desperto, agoraS);
    } else {
      // sombra + personagem (a pé ou montado — uma folha só)
      if (youOculto) ctx.filter = FILTRO_OCULTO;
      ctx.save();
      ctx.globalAlpha = 0.32;
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.ellipse(pScreenX, pScreenY - 2 * zoom, (ehMontaria(youMount) ? 22 : 16) * zoom, (ehMontaria(youMount) ? 10 : 7) * zoom, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      const youClock = tickIdleClock(riding, now, youIdleAt, youWasMoving);
      youIdleAt = youClock.idleAt;
      youWasMoving = youClock.wasMoving;
      playerHeadY = blitAvatar(ctx, youSkin, youSkinHue, youMount, youCloth, player.dir, riding, player.gait, now, pScreenX, pScreenY, youIdleAt, youAttackAt, youWeapon, youShield, youHairStyle, youHairHue, youBeardStyle, youBeardHue, femininoDo(youSkin, false),
        // A caçada automática sempre corre; no teclado, corre quem galopa.
        riding && !youDead && (segueServidor || wantsGallop), conjuracaoDe(youId, now), riding ? null : youSentado);
      if (youOculto) ctx.filter = 'none';
    }
  }

  /* tom ambiente — o Felucca original é bem mais fechado que o padrão do canvas */
  ctx.fillStyle = 'rgba(10,13,8,.14)';
  ctx.fillRect(0, 0, VW, VH);

  /* dia e noite (techspec-dia-e-noite): masmorra é sempre noite; o resto segue a hora de Britannia */
  {
    const escuro = MAPAS_ESCUROS.has(mapa.id);
    const minuto = escuro ? 0 : minutoAgora();
    const nivelLuz = escuro ? NOITE : nivelDeLuz(minuto);
    // Visão noturna (techspec-pocoes): a noite e a masmorra não escurecem enquanto o efeito dura.
    if (nivelLuz > 0.2 && !visaoNoturna()) aplicarLuz(ctx, mapa, cam, VW, VH, zoom, nivelLuz, corAmbiente(minuto, nivelLuz), now);
  }
  for (const f of efeitosDepoisDaLuz) f();
  for (const f of depoisDaLuz) f();

  /* nome encostado na cabeça; vida, mana e vigor acima dele */
  ctx.font = '600 13px Spectral, Georgia, serif';
  ctx.textAlign = 'center';
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(0,0,0,.85)';
  const youPlate = nickPlate(youGuildTag, playerName, youCargo);
  const youNameY = pScreenY - (playerHeadY - NICK_DA_CABECA) * zoom;
  ctx.fillStyle = youCargo ? NICK.staff : nickFill({ notoriety: youNotoriety, dead: youDead, oculto: youOculto });
  escreverPlaca(ctx, youPlate, pScreenX, youNameY, youVip, youDead, youSelo);
  const balloonY = youDead
    ? youNameY - 16 * zoom
    : drawYouVitals(ctx, pScreenX, youNameY) - 4 * zoom;
  drawBalloon(ctx, pScreenX, balloonY, youId, now);
  ancorarBalao(youId, pScreenX, baseDaFala(youId, balloonY, now));
  marcarCabeca(youId, pScreenX, youDead ? youNameY : balloonY);
  desenharDano(ctx, zoom, now);
  desenharBaloes(ctx, VW, now);

  /* A marca do clique no destino (techspec-marca-do-clique). O losango do mouse saiu; fica só o da colocação na casa. */
  if (colocacaoAtiva())
    desenharColocacao(ctx, mapa, hoverTile, player.tz, { x: player.tx, y: player.ty }, cam, VW, VH, zoom);
  desenharDestaque(ctx, mapa, cam, VW, VH, zoom);
  if (marcaClique){
    const dur = DURACAO_MARCA[marcaClique.tipo];
    const segura = PARADA_MARCA[marcaClique.tipo] * dur;
    const el = now - marcaClique.t0;
    const fim = path.length ? path[path.length - 1] : null;
    const indo = !!fim && fim.x === marcaClique.x && fim.y === marcaClique.y;
    if (!indo && marcaClique.parada < 0) marcaClique.parada = Math.max(0, el - segura);
    const p = marcaClique.parada >= 0 ? (el - marcaClique.parada) / dur : Math.min(el, segura) / dur;
    if (p >= 1) marcaClique = null;
    else {
      const { gx, gy } = centroTile(marcaClique.x, marcaClique.y);
      desenharMarcaClique(ctx, marcaClique.tipo, gx, gy, p, zoom * TW / 44);
    }
  }

  /* --- HUD --- */
  // Tile assentado, não o float do passo: no meio do SQM o arredondamento
  // pulava duelo-b ↔ arredores a cada quadro e o diário derrubava o fps.
  const reg = regionAt(player.tx, player.ty);
  if (reg !== lastRegion){
    lastRegion = reg;
    if (anunciaChegada(reg))
      logKey('mundo.vocechegaA', { lugar: reg }, 'system');
  }
  const lugar = anunciaChegada(reg) ? reg : mapa.nome;
  publish(now, lugar, riding ? (wantsGallop ? 'galope' : 'trote') : 'parado');
  marcarPortal();

  drawMinimap(time);
  marcarQuadro();
  raf = requestAnimationFrame(frame);
}

/* ══════════════════ 9. INÍCIO ══════════════════ */

resize();
if (!net){
  logKey('mundo.bemVindoA', { lugar: mapa.nome }, 'system');
  logKey('mundo.comoAndar', { botao: t(lerBotaoAndar() === 'esquerdo' ? 'hud.cfg.botaoEsquerdo' : 'hud.cfg.botaoDireito') }, 'note');
} else {
  logKey('mundo.entrandoNaCidade', undefined, 'system');
  avisarCarga('sala');
}
lastRegion = net ? '' : regionAt(player.tx, player.ty);
publish(performance.now(), net ? mapa.nome : lastRegion, 'parado', true);

raf = requestAnimationFrame(now => { last = now; frame(now); });

function guardarNaFila(item: PendingJoin){
  if (item.k === 'n') {
    pendingNet.length = 0;
    pendingGolpes.length = 0;
    sawJoinSnap = true;
    pendingNet.push(item);
    return;
  }
  if (!sawJoinSnap) return;
  if (pendingNet.length >= MAX_PENDING_NET) {
    const idx = pendingNet.findIndex((x, i) => i > 0 && x.k === 'f');
    if (idx >= 0) {
      const dropped = pendingNet.splice(idx, 1)[0];
      if (dropped && dropped.k === 'f') {
        if (dropped.frame.strikes.length === STRIKE_CAP) opts.onStrikeCap?.();
        if (dropped.frame.strikes.length)
          pendingGolpes.push(...dropped.frame.strikes);
      }
    } else {
      // O look (vivo/morto) não pode cair: é ele que desfaz o snapshot antigo.
      const outro = pendingNet.findIndex((x, i) => i > 0 && x.k !== 'l');
      pendingNet.splice(outro >= 0 ? outro : 1, 1);
    }
  }
  pendingNet.push(item);
}

function soltarFilaDeEntrada(){
  const fila = pendingNet.splice(0);
  sawJoinSnap = false;
  const vitals: Extract<PendingJoin, { k: 'v' }>[] = [];
  for (const item of fila){
    if (item.k === 'n') absorbSnapshot(item.entities);
    else if (item.k === 'f') ingerirFrame(item.frame);
    else if (item.k === 'l') applyLook(item.id, item.look);
    else vitals.push(item);
  }
  for (const item of vitals)
    aplicarVitals(item.id, item.hits, item.hitsMax, item.mana, item.manaMax, item.poisoned);
}

function aplicarVitals(id: number, hits: number, hitsMax: number, mana: number, manaMax: number, poisoned?: boolean){
  if (id === youId){
    const prev = player.hp;
    player.hp = hits; player.hpMax = hitsMax;
    player.mana = mana; player.manaMax = manaMax;
    if (poisoned !== undefined) player.envenenado = poisoned;
    lastKey = '';
    noteYouHits(prev, hits);
    return;
  }
  const r = remotes.get(id);
  if (!r){
    pendingVitals.set(id, { hits, hitsMax, mana, manaMax, poisoned });
    return;
  }
  noteRemoteHits(r, hits);
  r.hits = hits; r.hitsMax = hitsMax; r.mana = mana; r.manaMax = manaMax;
  if (poisoned !== undefined) r.envenenado = poisoned;
}

/** O tique de veneno liga o flag; Self/Moved/Entered é quem desliga quando acaba. */
function aplicarVenenoDoQuadro(netFrame: NetFrame){
  for (const e of netFrame.entered){
    if (e.id === youId) player.envenenado = !!e.poisoned;
    else {
      const r = remotes.get(e.id);
      if (r) r.envenenado = !!e.poisoned;
    }
  }
  for (const m of netFrame.moved){
    if (m.poisoned === undefined) continue;
    const r = remotes.get(m.id);
    if (r) r.envenenado = m.poisoned;
  }
  if (netFrame.self.poisoned !== undefined) {
    if (player.envenenado !== netFrame.self.poisoned) lastKey = '';
    player.envenenado = netFrame.self.poisoned;
  }
}

function aplicarGolpe(s: NetStrike){
  const r = remotes.get(s.targetId);
  const emVoce = s.targetId === youId;
  const bruto = emVoce ? playerName : (r?.name || t('mundo.aventureiro'));
  const nome = r && ehDummyRemoto(r) ? nomeDoManequim(bruto) : bruto;
  const meu = !emVoce && meuGolpe(s);
  opts.onStrike?.(s, meu, emVoce, nome);
  const somCheio = meu || emVoce || s.atacante === youId;
  const volGolpe = somCheio ? 1 : Math.max(volDistancia(s.atacante), volDistancia(s.targetId));
  if (emVoce && s.atacante) somDeRaiva(s.atacante);
  somDoGolpe(s, volGolpe, somCheio);
  if (s.miss) {
    if (s.dodge && emVoce)        logKey('mundo.voceEsquiva', {}, 'note');
    else if (s.dodge && meu && r) logKey('mundo.alvoEsquiva', { nome }, 'warn');
    else if (meu)                 logKey('mundo.voceErra', { nome }, 'warn');
    return;
  }
  if (r) {
    const matou = !!(s.kill || (r.kind === 'monster' && s.hits <= 0));
    if (matou && segurarMorte(r, performance.now())) {
      /* sprite espera o personagem chegar */
    } else {
      r.hits = s.hits;
      r.hitsMax = s.hitsMax || r.hitsMax;
    }
    if (r.kind === 'dummy' && !s.miss) r.attackAt = performance.now();
  }
  if (s.kind === 'poison') {
    if (emVoce) player.envenenado = true;
    else if (r) r.envenenado = true;
  }
  if (s.dealt > 0) {
    spawnDano(s.targetId, s.dealt, tipoDoStrike(s.kind, s.crit), emVoce);
    marcarGolpe(s.targetId);
    if (s.kind === 'cura') {
      /* cura não é dano: o diário não diz "você recebe dano". */
    } else if (emVoce) {
      golpeEmVoceAt = performance.now();
      logKey('mundo.voceRecebeDano', { n: s.dealt }, 'warn');
    } else if (meu) {
      logKey('mundo.voceCausaDano', { n: s.dealt, nome }, 'note');
    }
    // A dor de quem apanhou; no golpe que mata, toca a morte (cueRemoteDie / applyLife).
    if (s.kind !== 'cura' && !s.kill && !(emVoce && s.hits <= 0)) somDeDor(s.targetId, volGolpe, somCheio);
  }
  if (s.kill && !emVoce) {
    if (meu) logKey('mundo.voceMata', { nome }, 'note');
    if (!(r && r.morteEspera > 0)) {
      cueRemoteDie(s.targetId);
      if (r && r.deathAt <= 0) r.deathAt = performance.now();
    }
  }
  lastKey = '';
}

/**
 * Fala local: o servidor já escolheu quem ouve (quem está na tela). Jogador vira balão e linha
 * da aba Local; guarda e NPC só balão. Quem está ignorado não aparece em nenhum dos dois.
 */
function ouvirFalas(netFrame: NetFrame){
  for (const s of netFrame.said ?? []) {
    if (!s.text) continue;
    const voce = s.id === youId;
    const r = voce ? undefined : remotes.get(s.id);
    const nome = voce ? playerName : (r?.name ?? '');
    const jogador = voce || r?.kind === 'player';
    if (jogador && !voce && opts.ignorado?.(nome)) continue;
    falarBalao(s.id, s.text);
    if (jogador && nome)
      opts.onFalaLocal?.({ name: nome, text: s.text, at: Date.now(), vip: voce ? youVip : !!r?.vip, selo: voce ? youSelo : r?.selo ?? 0 });
  }
}

function ingerirFrame(netFrame: NetFrame){
  quadrosDesdePintura++;
  const self = netFrame.self;
  player.stam = self.stam;
  const tetoVigor = self.stamMax ?? 0;
  if (tetoVigor > 0) player.stamMax = tetoVigor;
  if ((self.fadigaEm ?? 0) > 0) {
    const nextSnap = self.fadiga ?? fadigaSnap;
    const nextEm = self.fadigaEm ?? 0;
    const nextFaixa = self.fadigaFaixa ?? 0;
    if (nextSnap !== fadigaSnap || nextEm !== fadigaEm || nextFaixa !== fadigaFaixa) lastKey = '';
    fadigaSnap = nextSnap;
    fadigaEm = nextEm;
    fadigaFaixa = nextFaixa;
  }
  const nextCriminal = self.criminalLeft ?? 0;
  if (nextCriminal !== criminalLeft) lastKey = '';
  criminalLeft = nextCriminal;
  const nextParalizado = !!self.paralizado;
  if (nextParalizado !== paralizado) lastKey = '';
  paralizado = nextParalizado;
  const huntTarget = self.targetId | 0;
  if (huntTarget !== huntTargetAtual) lastKey = '';
  huntTargetAtual = huntTarget;
  const nextWar = typeof self.warTargetId === 'number' ? self.warTargetId : 0;
  if (nextWar !== warTargetId) {
    warTargetId = nextWar;
    opts.onWar?.(warTargetId !== 0);
  }
  // Manopla vermelha: War ligado ou a caçada brigando com alguém.
  cursorCombate(warTargetId !== 0 || (huntTarget !== 0 && huntTarget !== youId));
  if (self.hitsMax) {
    lastKey = '';
    noteYouHits(player.hp, self.hits ?? player.hp);
    player.hp = self.hits ?? player.hp;
    player.hpMax = self.hitsMax;
    if (self.manaMax) {
      player.mana = self.mana ?? player.mana;
      player.manaMax = self.manaMax;
    }
  }
  serverSelfX = netFrame.self.x;
  serverSelfY = netFrame.self.y;
  serverSelfZ = netFrame.self.z ?? 0;
  const ack = self.ackSeq;
  const predicted = pending.find(p => p.seq === ack);
  if (predicted && (predicted.x !== netFrame.self.x || predicted.y !== netFrame.self.y))
    pending.length = 0;
  else {
    while (pending.length && pending[0].seq <= ack) pending.shift();
  }
  const zServidor = netFrame.self.z ?? 0;
  if (zServidor && !player.moving && pending.length === 0 && player.tx === netFrame.self.x && player.ty === netFrame.self.y
    && zServidor !== player.tz && peValido(mapa.id, player.tx, player.ty, zServidor))
    player.tz = player.nz = zServidor;
  const jump = chebyshev(player.tx, player.ty, netFrame.self.x, netFrame.self.y);
  if (pending.length === 0) {
    followServerSelf(netFrame.self.x, netFrame.self.y);
  } else if (jump > pending.length + 1) {
    pending.length = 0;
    followServerSelf(netFrame.self.x, netFrame.self.y);
  }
  if ((self.act ?? 0) === 1) {
    lastStrikeId = targetId ?? huntTarget ?? 0;
    const recoverMs = self.recoverMs ?? 0;
    if (chebyshev(player.x, player.y, netFrame.self.x, netFrame.self.y) <= 4)
      dispararSwingSelf(self.dir, recoverMs);
    else {
      swingPendenteEm = performance.now();
      swingPendenteDir = self.dir ?? null;
      swingPendenteMs = recoverMs;
    }
  }
  for (const e of netFrame.entered){
    if (e.id === youId) continue;
    dropRemotesNamed(e.name, e.id, e.kind);
    dyingRemotes.delete(e.id);
    upsertRemote(e.id, e.name, e.x, e.y, e.dir, true, e.mount, e.skin, e.skinHue, e.dead, e.cloth, e.hits, e.hitsMax, e.mana, e.manaMax, e.notoriety, e.guildTag, e.rel, e.kind, e.defId, e.poisoned, e.weapon, e.shield, e.hairStyle, e.hairHue, e.beardStyle, e.beardHue, e.feminino, e.aparencia, e.cargo, e.vip, e.oculto, e.selo);
    zRemoto.set(e.id, e.z ?? 0);
    anotarStam(e);
    if ((e.hits ?? 1) <= 0) {
      const recem = remotes.get(e.id);
      if (recem && recem.kind === 'monster' && recem.deathAt <= 0)
        recem.deathAt = performance.now();
    }
  }
  for (const m of netFrame.moved){
    if (m.id === youId) continue;
    const prev = remotes.get(m.id);
    if (!prev) continue;
    zRemoto.set(m.id, m.z ?? 0);
    const far = chebyshev(prev.toX, prev.toY, m.x, m.y) > 32;
    if (far || !seguirPassos(m)) upsertRemote(m.id, prev.name, m.x, m.y, m.dir, far);
    if (!far && prev.kind === 'monster' && (m.passoMs ?? 0) > 0) ritmoDoMob(m.id, m.passoMs!);
    anotarStam(m);
    if (m.act === 1) {
      const hit = remotes.get(m.id);
      if (hit) {
        hit.attackAt = performance.now();
        if (hit.kind === 'player') somDaFerramenta(hit.weapon, ehMontaria(hit.mount) ? 417 : 583, volDistancia(hit.id), false);
      }
    }
    if ((m.hitsMax ?? 0) > 0) {
      const live = remotes.get(m.id);
      if (live && m.hits != null && m.hits !== live.hits) {
        if (live.kind === 'monster' && m.hits <= 0) {
          if (!segurarMorte(live, performance.now())) {
            if (live.deathAt <= 0) live.deathAt = performance.now();
            live.hits = m.hits;
            live.hitsMax = m.hitsMax ?? live.hitsMax;
          }
        } else {
          live.hits = m.hits;
          live.hitsMax = m.hitsMax ?? live.hitsMax;
        }
      }
    }
  }
  const golpes = pendingGolpes.length
    ? pendingGolpes.splice(0).concat(netFrame.strikes ?? [])
    : (netFrame.strikes ?? []);
  if ((netFrame.strikes ?? []).length === STRIKE_CAP) opts.onStrikeCap?.();
  for (const s of golpes) aplicarGolpe(s);
  // A rodada fecha com o golpe fatal e, no mesmo quadro, o duelista
  // renasce no ponto. O golpe chega depois do Entered e deixa a vida em
  // zero: a barra some e o clique não marca alvo até o próximo passo.
  for (const e of netFrame.entered) {
    if (e.id === youId || e.dead || (e.hits ?? 0) <= 0) continue;
    const vivo = remotes.get(e.id);
    if (!vivo || vivo.kind === 'corpse') continue;
    vivo.hits = e.hits ?? vivo.hits;
    if ((e.hitsMax ?? 0) > 0) vivo.hitsMax = e.hitsMax ?? vivo.hitsMax;
    vivo.dead = false;
    vivo.deathAt = 0;
    vivo.morteEspera = 0;
  }
  aplicarVenenoDoQuadro(netFrame);
  ouvirFalas(netFrame);
  for (const id of netFrame.left) sairDoQuadro(id);
  if (huntTarget && huntTarget !== youId) {
    const atual = targetId != null ? remotes.get(targetId) : undefined;
    if (atual && (ehDummyRemoto(atual) || ehDoGrupo(atual)) && atual.id !== huntTarget) {
      /* Tab/clique no manequim de combate ou num membro do grupo não perdem o alvo para o lock do War. */
    } else {
      const alvo = remotes.get(huntTarget);
      if (!alvo || alvo.hits > 0 || alvo.morteEspera > 0) targetId = huntTarget;
      else if (targetId != null && !alvoAindaVive(targetId))
        retargetMorto();
    }
  } else if (targetId != null && !alvoAindaVive(targetId)) {
    retargetMorto();
  }
}

return {
  destroy(){
    pararAmbiente();
    trocaAc?.abort();
    trocaAc = null;
    cancelTilePick();
    if (raf !== null) cancelAnimationFrame(raf);
    raf = null;
    for (const off of teardown) off();
    teardown.length = 0;
    calarTodos();
  },
  say: log,
  sayKey: logKey,
  falarNpc(id, text){
    if (!text) return;
    falarBalao(id, text);
  },
  falarVoce(text){
    if (!text.trim()) return;
    falarBalao(youId, text);
  },
  applyWelcome(w){
    const gen = ++welcomeGen;
    aplicarVelocidades(w);
    cenaPronta = false;
    salaCanal = w.canal;
    salaProtegida = !!w.protegida;
    aplicarMundoLabel(w);
    const troca = mapa.id !== w.cityId;
    recomecarCarga(troca ? 'mapa' : 'desenhar', w.cityLabel, troca && jaEntrou, mapa.nome);
    iniciarAmbiente(w.cityId);
    if (troca) {
      somDeEquipDesde = performance.now() + 4000;
      raivaTocada.clear();
    }
    passoTravado = false;
    portalSobre = null;
    if (troca) {
      joined = false;
      pendingNet.length = 0;
      sawJoinSnap = false;
    }
    void (async () => {
      try {
        if (troca) {
          trocaAc?.abort();
          const ac = new AbortController();
          trocaAc = ac;
          const novo = await carregarMapa(w.cityId, ac.signal);
          if (gen !== welcomeGen) return;
          await renovarViagens();
          if (gen !== welcomeGen) return;
          instalarMapa(novo);
        }
        if (gen !== welcomeGen) return;
        pending.length = 0;
        path = [];
        serverPath = [];
        seq = 0;
        youId = w.youId;
        opts.onYouId?.(youId);
        aquecerEm(mapa, w.spawnX, w.spawnY);
        snapSelf(w.spawnX, w.spawnY);
        logKey('mundo.bemVindoA', { lugar: w.cityLabel }, 'system');
        joined = true;
        cenaPronta = true;
        cargaDesde = performance.now();
        avisarCarga('desenhar');
        soltarFilaDeEntrada();
        if (treinoPendente) {
          const p = treinoPendente;
          treinoPendente = null;
          aproximarManequim(p.dummyId, p.alcance, p.onArrive, p.alvo);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (gen !== welcomeGen) return;
        pendingNet.length = 0;
        sawJoinSnap = false;
        joined = true;
        cenaPronta = true;
        cargaDesde = performance.now();
        avisarCarga('desenhar');
        logKey('mundo.mapaNaoCarregou', undefined, 'note');
      }
    })();
  },
  applyMundoLabel(meta){ aplicarMundoLabel(meta); },
  provador(canvas, cloth, dir, feminino = false){
    const prova = { cloth: cloth.trim(), dir: ((Math.trunc(dir) % 8) + 8) % 8, feminino };
    provadores.set(canvas, prova);
    if (prova.cloth) ensureCloth(prova.cloth);
    const corpo = jsonPersonagem(corpoDoProvador(feminino), '', 'idle');
    if (!prontas.has(corpo)) {
      void carregarFolha(corpo).then(f => {
        if (atlasDead) return;
        prontas.set(corpo, f);
        paintDolls();
      }).catch(() => {});
    }
    pintarProvador(canvas, prova);
    return () => { provadores.delete(canvas); };
  },
  setAdversarioDuelo(nome){
    adversarioDuelo = nome && nome.trim() ? nome.trim() : null;
    lastKey = '';
  },
  setGrupo(nomes){
    grupoNomes = new Set(nomes.map(n => n.trim().toLowerCase()));
  },
  marcarJogador(nome){
    const alvo = nome.trim().toLowerCase();
    for (const r of remotes.values()) {
      if (r.kind !== 'player' || r.name.toLowerCase() !== alvo) continue;
      targetId = r.id;
      lastKey = '';
      return true;
    }
    return false;
  },
  idDoJogador(nome){
    const alvo = nome.trim().toLowerCase();
    for (const r of remotes.values()) if (r.kind === 'player' && r.name.toLowerCase() === alvo) return r.id;
    return null;
  },
  retratoDoAlvo(canvas, id){
    retratosDeAlvo.set(canvas, id);
    pintarRetratoDoAlvo(canvas, id);
    return () => { retratosDeAlvo.delete(canvas); };
  },
  retratoDe(canvas, nome, look){
    const item = { nome, look };
    retratos.set(canvas, item);
    pintarRetrato(canvas, item);
    return () => { retratos.delete(canvas); };
  },
  bonecoDe(nome, canvas){
    bonecosDeOutros.set(canvas, nome);
    pintarBonecoDeOutro(canvas, nome);
    return () => { bonecosDeOutros.delete(canvas); };
  },
  applySnapshot(entities){
    if (!joined){
      guardarNaFila({ k: 'n', entities });
      return;
    }
    absorbSnapshot(entities);
  },
  applyFrame(frame){
    if (!joined){
      guardarNaFila({ k: 'f', frame });
      return;
    }
    ingerirFrame(frame);
  },
  applyReject(reject){
    const idx = pending.findIndex(p => p.seq === reject.seq);
    if (idx >= 0) pending.splice(idx, 1);
    snapSelf(reject.x, reject.y);
    replayPending();
  },
  setMount(defId){
    applyYouMount(defId);
  },
  setCloth(defId){
    applyYouCloth(defId);
  },
  setMainHand(defId){
    applyYouWeapon(defId);
  },
  setOffHand(defId){
    applyYouShield(defId);
  },
  setRemoteMount(id, mount){
    applyRemoteMount(id, mount);
  },
  setLook(id, look){
    // Na troca de mapa o snapshot da sala nova espera o mapa carregar. Um look que chega
    // depois dele (o Reviver na chegada: "Tu tornas a ser mortal") tem de esperar junto e
    // entrar na ordem; aplicado na hora, o snapshot atrasado (ainda fantasma) o desfazia.
    if (!joined && sawJoinSnap){
      guardarNaFila({ k: 'l', id, look });
      return;
    }
    applyLook(id, look);
  },
  setVitals(id: number, hits: number, hitsMax: number, mana: number, manaMax: number, poisoned?: boolean){
    if (!joined){
      guardarNaFila({ k: 'v', id, hits, hitsMax, mana, manaMax, poisoned });
      return;
    }
    aplicarVitals(id, hits, hitsMax, mana, manaMax, poisoned);
  },
  getTargetId(){ return targetId; },
  getTargetInfo(){
    if (targetId === null) return null;
    const r = remotes.get(targetId);
    return r ? { id: targetId, nome: r.name, kind: r.kind, notoriety: r.notoriety ?? 0 } : null;
  },
  getSelfNotoriety(){ return youNotoriety; },
  getTargetKind(){
    if (targetId === null) return null;
    return remotes.get(targetId)?.kind ?? null;
  },
  spawnLocalMob(mob){
    const id = mob.id || ++localMobSeq;
    upsertRemote(id, mob.name, mob.x, mob.y, 2, true, '', '', undefined, false, '', mob.hits, mob.hitsMax, 0, 0, 0, '', 0, 'monster', mob.defId);
    localBrains.set(id, {
      homeX: mob.x, homeY: mob.y, radius: mob.range,
      wait: 8, walking: false, destX: mob.x, destY: mob.y, stepWait: PASSO_MOB_TICKS,
      avoidX: -1e9, avoidY: -1e9,
      hunting: false, swingWait: 0
    });
  },
  removeLocalMob(id){
    remotes.delete(id);
    localBrains.delete(id);
    if (targetId === id) targetId = null;
  },
  applySpeech(id, text, kind){
    // kind 1 = falha (o texto vem vazio e a frase é do resource);
    // kind 2 = a magia acertou (ou parou sem falha): o balão das palavras some;
    // kind 0 ou ausente = PALAVRAS DE PODER, desenhadas literalmente. O balão fica até a magia acertar ou falhar.
    // `text === FIZZLE_LEGADO` é a ponte para o servidor que ainda manda a
    // palavra "falhou..." pelo mesmo canal das palavras.
    if (kind === 1 || text === FIZZLE_LEGADO) {
      showFizzle(id);
      return;
    }
    if (kind === 2) {
      if (balloons.get(id)?.magia) balloons.delete(id);
      conjurando.delete(id);
      return;
    }
    if (!text) return;
    balloons.set(id, { text, until: performance.now() + PALAVRAS_MAX_MS, magia: true });
    conjurando.set(id, { desde: performance.now(), area: AREA_POR_PALAVRAS.get(text.trim().toLowerCase()) ?? false });
    if (id === youId) {
      awaitingHit = true;
      // As palavras saem no começo e ficam até a magia acertar ou falhar. A recarga da barra vem do "cs".
      logKey('mundo.voceConjura', { palavras: text }, 'spell');
    }
    else {
      const name = remotes.get(id)?.name;
      if (name) logKey('mundo.alguemConjura', { nome: name, palavras: text }, 'spell');
    }
  },
  applyFizzle(){
    if (!awaitingHit) return;
    showFizzle(youId);
  },
  falharMagia(){
    if (youId) showFizzle(youId);
  },
  playSpell(spellId, casterId){
    if (casterId === youId) awaitingHit = false;
    if (casterId != null) conjurando.delete(casterId);
    playSkill(spellId);
  },
  spawnEffect(magia, de, para, inicio, duracaoMs, _casterId, seguir, mirar){
    if (!joined) return;
    spawnProjetil(magia, {
      x: de.x,
      y: de.y,
      z: zPe(Math.round(de.x), Math.round(de.y))
    }, {
      x: para.x,
      y: para.y,
      z: zPe(Math.round(para.x), Math.round(para.y))
    }, inicio, duracaoMs, seguir ?? 0, mirar ?? 0);
  },
  async trocarMapa(id){
    if (mapa.id === id) return;
    trocaAc?.abort();
    const ac = new AbortController();
    trocaAc = ac;
    const novo = await carregarMapa(id, ac.signal);
    if (ac.signal.aborted) return;
    await renovarViagens();
    if (ac.signal.aborted) return;
    instalarMapa(novo);
    joined = true;
    snapSelf(mapa.spawn.x, mapa.spawn.y);
    aquecerEm(mapa, player.tx, player.ty);
    logKey('mundo.vocechegaA', { lugar: mapa.nome }, 'system');
  },
  beginTilePick,
  cancelTilePick,
  teleportSelf,
  travarPasso,
  travarColeta,
  setPortaEvento,
  setPenta(cor, mascara, ritual) {
    pentaCor = cor;
    pentaMascara = mascara & TODAS_PENTA;
    pentaRitual = ritual;
  },
  setOculto(ligado) {
    youOculto = !!ligado;
    paintDolls();
  },
  manequimDeTreinoPerto() {
    const from = player.moving ? { x: player.nx, y: player.ny } : { x: player.tx, y: player.ty };
    let melhor: number | null = null, dist = Infinity;
    for (const r of remotes.values()) {
      if (!ehDummyRemoto(r) || dummyEhCombate(r)) continue;
      const d = chebyshev(from.x, from.y, r.toX, r.toY);
      if (d < dist) { dist = d; melhor = r.id; }
    }
    return melhor;
  },
  dummyEm(x, y) {
    for (const r of remotes.values())
      if (ehDummyRemoto(r) && Math.round(r.toX) === x && Math.round(r.toY) === y) return r.id;
    return null;
  },
  approachDummy(dummyId, alcance, onArrive, alvo) {
    return aproximarManequim(dummyId, alcance, onArrive, alvo);
  },
  irPara(x, y) {
    const antes = path;
    andarAte({ x, y });
    return path !== antes && path.length > 0;
  },
  /** A casa à frente do jogador, no andar dele (techspec-casas §04: onde prende item ou põe baú). */
  alvoAFrente(){
    const d = DIRS[player.dir] ?? DIRS[0];
    const x = player.tx + d.dx, y = player.ty + d.dy;
    return { x, y, z: peMaisPerto(mapa.id, x, y, player.tz) ?? zPe(x, y) };
  },
  playerScreen(){
    const pz = zJogador();
    return {
      x: w2sx(wx(player.x, player.y)),
      y: w2sy(wy(player.x, player.y) + TH / 2 - pz * Z_PX)
    };
  },
  npcsDaSala(){
    return npcsGuardados.map((n, i) => ({ ...n, remoteId: NPC_ID_BASE + i }));
  },
  portaisDaSala(){ return portaisGuardados; },
  viagensDoMenu(){ return viagensGuardadas; },
  mapaNome(){ return mapa.nome; },
  mapaId(){ return mapa.id; },
  recarregarNpcs(_etag: string){
    void (async () => {
      try {
        esquecerMapas();
        const arquivo = mapa.id + '.json';
        const [npcs, manequins, portais] = await Promise.all([
          carregarNpcs(arquivo),
          carregarManequins(arquivo),
          carregarPortais(arquivo),
          renovarViagens()
        ]);
        for (const [id, r] of remotes) {
          if (r.kind === 'npc') remotes.delete(id);
        }
        npcsGuardados = npcs;
        manequinsGuardados = manequins;
        aplicarPortais(portais);
        reporNpcs();
        opts.onSala?.();
      } catch { /* a lista anterior segura a tela */ }
    })();
  }
};
}
